import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { requestNotificationPermission, sendPushNotification } from './pushNotifications';

// --- CONFIGURACIÓN DE SERVICIOS ---
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

// ⚠️ API KEY GEMINI
const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "";

const COLORS = {
  red: '#FF5630',
  gold: '#FFAB00',
  teal: '#00A76F',
  navy: '#212B36'
};

// --- UTILIDADES ---
const safeStorage = {
  getItem: (key) => { try { return localStorage.getItem(key); } catch (e) { return null; } },
  setItem: (key, val) => { try { localStorage.setItem(key, val); } catch (e) { } }
};

const getFormData = (form) => {
  const data = {};
  const formData = new FormData(form);
  formData.forEach((value, key) => { data[key] = value; });
  return data;
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64Data = reader.result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
  });
};

const validateWithGemini = async (base64Data, mimeType, type) => {
  if (!apiKey) return { valid: true, reason: "Sin API Key - Validación simulada (Aprobado)" };

  let promptText = "";
  if (type === 'medical') {
    promptText = "Analiza este documento (imagen o PDF) buscando un certificado médico. REGLAS ESTRICTAS: 1. Debe verse un logo de clínica/hospital o un sello médico. 2. Si es solo texto plano sin formato oficial, recházalo. Responde JSON: { 'valid': boolean, 'reason': 'string' }.";
  } else if (type === 'human_check') {
    promptText = "Analiza esta imagen. ¿Contiene clara y visiblemente el rostro de una persona real? No aceptes dibujos ni objetos. Responde JSON: { 'valid': boolean, 'reason': 'string' }";
  } else {
    promptText = "Analiza este documento (imagen o PDF) de una excusa escolar. TU MISIÓN ES RECHAZAR DOCUMENTOS FALSOS O IRRELEVANTES. REGLAS ESTRICTAS: 1. RECHAZA INMEDIATAMENTE: fotos de animales, fondos en negro/oscuros, fotos completamente borrosas o contenido que no sea un documento/identidad válido. 2. PARA APROBAR, el documento DEBE CONTENER AL MENOS UNO de los siguientes elementos: a) Una firma manuscrita legible (trazos de bolígrafo), b) Un texto o nota escrita a mano, c) Un sello o logo médico/institucional claro, d) Una foto clara del estudiante o acudiente sosteniendo un documento. 3. Si es solo un texto impreso por computadora SIN ninguno de los elementos anteriores, RECHÁZALO. Responde estrictamente con un objeto JSON válido (sin markdown): { \"valid\": boolean, \"reason\": \"Breve explicación en español del motivo de aprobación o rechazo\" }.";
  }

  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: promptText }, { inlineData: { mimeType: mimeType, data: base64Data } }] }],
          generationConfig: { responseMimeType: "application/json" }
        })
      }
    );
    if (!response.ok) throw new Error("Error API I.A.");
    const data = await response.json();
    let textResult = (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts && data.candidates[0].content.parts[0]) ? data.candidates[0].content.parts[0].text : "";

    // Limpieza de Markdown si la IA lo incluye a pesar de la instrucción
    textResult = textResult.replace(/```json/g, '').replace(/```/g, '').trim();

    return JSON.parse(textResult);
  } catch (error) {
    console.error("Error Gemini:", error);
    return { valid: true, reason: "Validación técnica omitida (Error Conexión)." };
  }
};

const normalName = (s) => (s || '').trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const getStudentAlerts = (excusesList, studentName, tardiesList, infirmaryList) => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const fifteenDaysAgo = new Date(now);
  fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
  const isCurrentMonth = (dateStr) => {
    const d = new Date(dateStr);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  };
  const isLast15Days = (dateStr) => new Date(dateStr) >= fifteenDaysAgo;
  const norm = normalName(studentName);
  const result = { uniformAlert: false, absenceAlert: false, tardinessAlert: false, infirmaryAlert: false, tardiness15Count: 0, infirmary15Count: 0, uniformCount: 0, absenceCount: 0 };
  if (excusesList && excusesList.length > 0) {
    const studentExcuses = excusesList.filter(e => normalName(e.student_name) === norm && !e.is_teacher_report);
    // Excusas: ventana mensual
    result.uniformCount = studentExcuses.filter(e => e.type === 'uniform' && isCurrentMonth(e.created_at)).length;
    result.absenceCount = studentExcuses.filter(e => (e.type === 'absence' || e.type === 'tardiness') && isCurrentMonth(e.created_at)).length;
    result.uniformAlert = result.uniformCount >= 3;
    result.absenceAlert = result.absenceCount >= 3;
  }
  if (tardiesList && tardiesList.length > 0) {
    // Tardanzas: últimos 15 días
    result.tardiness15Count = tardiesList.filter(t => normalName(t.student_name) === norm && isLast15Days(t.created_at)).length;
    result.tardinessAlert = result.tardiness15Count >= 3;
  }
  if (infirmaryList && infirmaryList.length > 0) {
    // Enfermería: últimos 15 días
    result.infirmary15Count = infirmaryList.filter(i => normalName(i.student_name) === norm && isLast15Days(i.created_at)).length;
    result.infirmaryAlert = result.infirmary15Count >= 3;
  }
  return result;
};


const getTardinessCount = (tardiesList, studentName) => {
  if (!tardiesList) return 0;
  return tardiesList.filter(t => t.student_name === studentName).length;
};

// --- COMPONENTES UI ---
// Festivos oficiales de Colombia 2025-2026 (fechas fijas + lunes trasladados Ley Emiliani)
const COLOMBIAN_HOLIDAYS = [
  // 2025
  '2025-01-01','2025-01-06','2025-03-24','2025-04-17','2025-04-18',
  '2025-05-01','2025-06-02','2025-06-23','2025-06-30','2025-07-20',
  '2025-08-07','2025-08-18','2025-10-13','2025-11-03','2025-11-17',
  '2025-12-08','2025-12-25',
  // 2026
  '2026-01-01','2026-01-12','2026-03-23','2026-04-02','2026-04-03',
  '2026-05-01','2026-05-18','2026-06-08','2026-06-15','2026-06-29',
  '2026-07-20','2026-08-07','2026-08-17','2026-10-12','2026-11-02',
  '2026-11-16','2026-12-08','2026-12-25',
];
const holidaySet = new Set(COLOMBIAN_HOLIDAYS);

const isBusinessDay = (date) => {
  const day = date.getDay();
  if (day === 0 || day === 6) return false; // Sáb/Dom
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return !holidaySet.has(`${yyyy}-${mm}-${dd}`);
};

const addBusinessDays = (startDate, numDays) => {
  const result = new Date(startDate);
  let added = 0;
  while (added < numDays) {
    result.setDate(result.getDate() + 1);
    if (isBusinessDay(result)) added++;
  }
  return result;
};

const CountdownTimer = ({ startDate, days = 1, darkMode }) => {
  const [timeLeft, setTimeLeft] = useState("");
  const [expired, setExpired] = useState(false);
  const [endDateTime, setEndDateTime] = useState(null);
  const [businessDaysLeft, setBusinessDaysLeft] = useState(0);

  useEffect(() => {
    const calculateTime = () => {
      const start = new Date(startDate);
      // Avanzar los días de la excusa (días calendario de la excusa misma)
      const excuseEnd = new Date(start.getTime() + ((days - 1) * 24 * 60 * 60 * 1000));
      // Sumar 3 días hábiles a partir del fin de la excusa
      const deadline = addBusinessDays(excuseEnd, 3);
      deadline.setHours(23, 59, 0, 0);

      setEndDateTime(deadline);
      const now = new Date();
      const diff = deadline.getTime() - now.getTime();

      if (diff < 0) {
        setExpired(true);
        setTimeLeft("PLAZO VENCIDO");
        setBusinessDaysLeft(0);
      } else {
        // Contar días hábiles restantes desde hoy hasta el deadline
        let bDays = 0;
        const cursor = new Date(now);
        cursor.setHours(0,0,0,0);
        const deadlineMidnight = new Date(deadline);
        deadlineMidnight.setHours(0,0,0,0);
        while (cursor < deadlineMidnight) {
          cursor.setDate(cursor.getDate() + 1);
          if (isBusinessDay(cursor)) bDays++;
        }
        setBusinessDaysLeft(bDays);

        const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeLeft(`${bDays} día${bDays !== 1 ? 's' : ''} hábil${bDays !== 1 ? 'es' : ''} · ${h}h ${m}m`);
      }
    };

    calculateTime();
    const timer = setInterval(calculateTime, 60000);
    return () => clearInterval(timer);
  }, [startDate, days]);

  const urgencyLevel = expired ? 'expired' : businessDaysLeft <= 1 ? 'critical' : businessDaysLeft <= 2 ? 'warning' : 'normal';

  return (
    <div className={`mt-3 p-3 rounded-2xl border-2 flex flex-col gap-1.5 transition-colors ${
      urgencyLevel === 'expired' ? (darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-100 border-slate-300') :
      urgencyLevel === 'critical' ? (darkMode ? 'bg-red-950/40 border-red-500/50 shadow-[0_0_20px_rgba(239,68,68,0.15)]' : 'bg-red-50 border-red-400 shadow-sm') :
      urgencyLevel === 'warning' ? (darkMode ? 'bg-amber-950/40 border-amber-500/50 shadow-[0_0_15px_rgba(245,158,11,0.15)]' : 'bg-amber-50 border-amber-400 shadow-sm') :
      (darkMode ? 'bg-indigo-950/40 border-indigo-500/50 shadow-[0_0_15px_rgba(99,102,241,0.15)]' : 'bg-indigo-50 border-indigo-400 shadow-sm')
    }`}>
      <div className="flex items-center justify-between">
        <span className={`text-[9px] font-black uppercase tracking-[0.2em] flex items-center gap-1.5 ${
          urgencyLevel === 'expired' ? (darkMode ? 'text-slate-500' : 'text-slate-400') :
          urgencyLevel === 'critical' ? (darkMode ? 'text-red-400' : 'text-red-600') :
          urgencyLevel === 'warning' ? (darkMode ? 'text-amber-400' : 'text-amber-600') :
          (darkMode ? 'text-indigo-400' : 'text-indigo-600')
        }`}>
          📚 Plazo para presentar talleres / exámenes
        </span>
        {!expired && urgencyLevel === 'critical' && <span className="animate-pulse flex h-2.5 w-2.5 rounded-full bg-red-500" />}
        {!expired && urgencyLevel !== 'critical' && <span className="animate-pulse flex h-2 w-2 rounded-full bg-indigo-500" />}
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col">
          <span className={`text-[10px] font-bold ${darkMode ? 'text-white/70' : 'text-navy/70'}`}>
            {endDateTime ? `Fecha límite: ${endDateTime.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' })}` : '--'}
          </span>
        </div>
        <span className={`text-xs font-black font-mono px-3 py-1 rounded-xl ${
          urgencyLevel === 'expired' ? (darkMode ? 'text-slate-500 bg-slate-900' : 'text-slate-400 bg-white line-through') :
          urgencyLevel === 'critical' ? (darkMode ? 'text-red-300 bg-red-950 animate-pulse' : 'text-red-700 bg-white animate-pulse') :
          urgencyLevel === 'warning' ? (darkMode ? 'text-amber-300 bg-amber-950' : 'text-amber-700 bg-white') :
          (darkMode ? 'text-indigo-300 bg-indigo-950' : 'text-indigo-700 bg-white')
        }`}>
          {timeLeft}
        </span>
      </div>
    </div>
  );
};

const Icon = ({ name, size = 20, className = "" }) => {
  const icons = {
    logo: (<g><path d="M12 2L2 6v10l10 6 10-6V6L12 2z" fill={COLORS.red} /><path d="M12 4L4 7.2v7.6l8 4.8 8-4.8V7.2L12 4z" fill={COLORS.gold} /><path d="M12 5.5L5.5 8.1v6.3l6.5 3.9 6.5-3.9V8.1L12 5.5z" fill={COLORS.teal} /><path d="M11 12.5c0 .5.5 1 1 1s1-.5 1-1-.5-1-1-1-1 .5-1 1zM10 14h4v.5h-4z" fill={COLORS.navy} /></g>),
    check: <path d="M20 6 9 17l-5-5" />,
    x: <path d="M18 6 6 18M6 6l12 12" />,
    info: <g><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></g>,
    send: <path d="m22 2-7 20-4-9-9-4Z" />,
    clock: <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />,
    logout: <g><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" x2="9" y1="12" y2="12" /></g>,
    users: <g><circle cx="12" cy="7" r="4" /><path d="M20 21v-2a4 4 0 0 0-3-3.87" /></g>,
    user: <g><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></g>,
    plus: <path d="M12 5v14M5 12h14" />,
    eye: <path d="M2 12s3-7 10-7 10 7 10 7-10 7-10 7-10-7Z" />,
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    ai: <path d="M12 2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM2 12a10 10 0 1 1 20 0 10 10 0 0 1-20 0zm10 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />,
    download: <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />,
    camera: <g><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></g>,
    folder: <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />,
    heart: <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />,
    phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.12 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />,
    medical: <path d="M12 2a3 3 0 0 0-3 3v2H7a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-8a3 3 0 0 0-3-3h-2V5a3 3 0 0 0-3-3zM8 14h8M12 10v8" />,
    cross: <path d="M12 2v20M2 12h20" />,
    refresh: <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />,
    lock: <path d="M19 11H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2Zm-7-7a4 4 0 1 1 0 8 4 4 0 0 1 0-8Z" />,
    bulb: <path d="M12 2a6 6 0 0 1 6 6c0 2.2-1.5 4.3-3.5 5.2l-1 4.8h-3l-1-4.8A5.96 5.96 0 0 1 6 8a6 6 0 0 1 6-6Z" />,
    arrowRight: <path d="M5 12h14M12 5l7 7-7 7" />,
    arrowLeft: <path d="M19 12H5M12 19l-7-7 7-7" />,
    edit: <g><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></g>,
    trash: <g><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></g>,
    filter: <g><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></g>,
    book: <g><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></g>,
    file: <g><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><polyline points="13 2 13 9 20 9" /></g>,
    bell: <g><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></g>,
    calendar: <g><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></g>,
    alert: <g><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></g>,
    phoneCall: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.12 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />,
    sun: <g><circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" /></g>,
    moon: <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
  };
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{icons[name] || icons.info}</svg>;
};

const GRADES = ["Jardín", "Transición", "1°", "2°", "3°", "4°", "5°A", "5°B", "6°A", "6°B", "7°A", "7°B", "8°A", "8°B", "9°A", "9°B", "10°A", "10°B", "11°A", "11°B"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const WelcomeAnimation = ({ name, logoUrl, darkMode }) => (
  <div className={`fixed inset-0 z-[60] ${darkMode ? 'bg-[#161C24]' : 'bg-white'} flex flex-col items-center justify-center animate-in fade-in duration-500`}>
    <div className="mb-8 w-20 h-20 flex items-center justify-center">
      {logoUrl ? <img src={logoUrl} alt="Logo" className="h-16 w-auto object-contain" /> : <Icon name="logo" size={64} />}
    </div>
    <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-[#212B36]'} text-center mb-2`}>¡Bienvenido!</h1>
    <p className={`text-base ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'} font-medium`}>{name}</p>
    <div className={`mt-8 w-40 h-1 ${darkMode ? 'bg-[#212B36]' : 'bg-[#F4F6F8]'} rounded-full overflow-hidden relative`}>
      <div className="absolute inset-0 bg-[#00A76F] animate-[loading_1.5s_infinite_ease-in-out]" style={{ width: '40%' }}></div>
    </div>
  </div>
);

const LogoutAnimation = ({ logoUrl, darkMode }) => (
  <div className={`fixed inset-0 z-[100] ${darkMode ? 'bg-[#161C24]' : 'bg-white'} flex flex-col items-center justify-center animate-in fade-in duration-500`}>
    <div className="mb-8 w-20 h-20 flex items-center justify-center">
      {logoUrl ? <img src={logoUrl} alt="Logo" className="h-16 w-auto object-contain" /> : <Icon name="logo" size={64} className={darkMode ? 'text-white' : 'text-[#212B36]'} />}
    </div>
    <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-[#212B36]'} text-center mb-2`}>
      Cerrando sesión
    </h1>
    <p className={`text-base ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'} font-medium mb-8`}>
      Hasta pronto
    </p>
    <div className={`w-40 h-1 ${darkMode ? 'bg-[#212B36]' : 'bg-[#F4F6F8]'} rounded-full overflow-hidden relative`}>
      <div className="absolute inset-0 bg-[#FF5630] animate-[loading_1.5s_infinite_ease-in-out]" style={{ width: '40%' }}></div>
    </div>
  </div>
);

const ZoomLoadingScreen = ({ logoUrl, onDone, darkMode }) => {
  const [phase, setPhase] = React.useState('loading'); // loading | zooming | done
  React.useEffect(() => {
    const t1 = setTimeout(() => setPhase('zooming'), 600);
    const t2 = setTimeout(() => { setPhase('done'); onDone(); }, 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);
  if (phase === 'done') return null;
  return (
    <div
      className={`fixed inset-0 z-[90] flex items-center justify-center ${darkMode ? 'bg-[#161C24]' : 'bg-white'}`}
      style={{ transition: 'opacity 0.4s', opacity: phase === 'zooming' ? 0 : 1 }}
    >
      <div className="flex flex-col items-center justify-center">
        {logoUrl
          ? <img src={logoUrl} alt="Logo" className="h-20 w-auto object-contain mb-6 animate-pulse" />
          : <Icon name="logo" size={80} className={`mb-6 animate-pulse ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`} />}
        <div className={`w-32 h-1 ${darkMode ? 'bg-[#212B36]' : 'bg-[#F4F6F8]'} rounded-full overflow-hidden relative`}>
          <div className="absolute inset-0 bg-[#00A76F] animate-[loading_1s_infinite_linear]" style={{ width: '30%' }}></div>
        </div>
      </div>
    </div>
  );
};

const TOUR_CONTENT = {
  parent: [
    { title: "¡Bienvenido/a!", text: "Te damos la bienvenida a la plataforma PORTAL DE EXCUSAS CAN. Aquí podrás gestionar los reportes escolares de tu hijo/a de forma fácil y rápida.", icon: "logo" },
    { title: "Reportar Novedades", text: "Usa el botón 'NUEVA EXCUSA' para reportar inasistencias, citas médicas o llegadas tarde. ¡Es el primer paso!", icon: "plus" },
    { title: "Organización por Opciones", text: "Ahora tienes pestañas para ver todo ordenado: 'Excusas', 'Tardanzas' y 'Enfermería'. Selecciona una para filtrar la información.", icon: "folder" },
    { title: "Códigos de Seguridad", text: "Para mayor seguridad, te pediremos un código que aparecerá en pantalla antes de enviar ciertos reportes.", icon: "lock" }
  ],
  teacher: [
    { title: "¡Bienvenido Profe!", text: "Esta es tu herramienta para gestionar las novedades de tus estudiantes y del colegio.", icon: "logo" },
    { title: "Panel de Docente", text: "Navega entre las pestañas superiores para ver Excusas, Tardanzas y Reportes de Enfermería.", icon: "folder" },
    { title: "Filtros de Curso", text: "Puedes ver solo los reportes de tu dirección de grupo o filtrar para ver todo el colegio si lo necesitas.", icon: "eye" }
  ],
  admin: [
    { title: "Panel Administrativo", text: "Tienes control total sobre la plataforma. Gestiona usuarios, reportes y configuraciones desde las pestañas superiores.", icon: "shield" }
  ]
};

const TourModal = ({ step, onNext, onPrev, onClose, role, darkMode }) => {
  const currentRoleContent = TOUR_CONTENT[role] || TOUR_CONTENT['parent'];

  useEffect(() => {
    const timer = setTimeout(() => {
      if (step === currentRoleContent.length - 1) {
        onClose();
      } else {
        onNext();
      }
    }, 60000);
    return () => clearTimeout(timer);
  }, [step, onNext, onClose, currentRoleContent.length]);

  const currentStepData = currentRoleContent[step] || currentRoleContent[0];
  const isLastStep = step === currentRoleContent.length - 1;

  return (
    <div className={`fixed inset-0 z-[80] ${darkMode ? 'bg-slate-950/90' : 'bg-navy/90'}  flex items-center justify-center p-6 animate-in fade-in duration-300`}>
      <div className={`${darkMode ? 'glass-morphism border-teal-500/50' : 'bg-white border-teal-500'} w-full max-w-md p-8 rounded-[2rem] shadow-2xl relative text-center border-4`}>

        {step > 0 && (
          <button
            onClick={onPrev}
            className={`absolute top-1/2 left-4 -translate-y-1/2 p-2 ${darkMode ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-slate-100 text-navy hover:bg-slate-200'} rounded-full shadow-lg hover:scale-110 transition-colors z-20`}
            title="Anterior"
          >
            <Icon name="arrowLeft" size={24} />
          </button>
        )}

        {!isLastStep && (
          <button
            onClick={onNext}
            className="absolute top-1/2 right-4 -translate-y-1/2 p-2 bg-teal-500 rounded-full text-white shadow-lg hover:scale-110 hover:bg-teal-600 transition-colors z-20 animate-pulse"
            title="Siguiente"
          >
            <Icon name="arrowRight" size={24} />
          </button>
        )}

        <div className={`absolute -top-10 left-1/2 transform -translate-x-1/2 w-20 h-20 ${darkMode ? 'bg-slate-900 border-teal-500/50' : 'bg-white border-teal-500'} rounded-full flex items-center justify-center border-4 shadow-xl z-10 text-teal-500`}>
          <Icon name={currentStepData.icon} size={40} />
        </div>

        <div className="mt-8 mb-6 px-6">
          <h2 className={`text-2xl font-black ${darkMode ? 'text-white' : 'text-navy'} mb-2`}>{currentStepData.title}</h2>
          <p className={`${darkMode ? 'text-slate-400' : 'text-slate-500'} text-sm leading-relaxed font-medium`}>{currentStepData.text}</p>
        </div>

        <div className="flex justify-between items-center mt-8">
          <div className="flex gap-1">
            {currentRoleContent.map((_, i) => (
              <div key={i} className={`h-2 rounded-full transition-colors duration-300 ${i === step ? 'w-8 bg-teal-500' : (darkMode ? 'bg-slate-800' : 'bg-slate-200')}`} />
            ))}
          </div>

          <button onClick={isLastStep ? onClose : onNext} className={`${darkMode ? 'bg-teal-600 hover:bg-teal-500' : 'bg-navy hover:bg-opacity-90'} text-white px-6 py-3 rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg transition-transform active:scale-95`}>
            {isLastStep ? '¡COMENCEMOS!' : 'SIGUIENTE'} <Icon name="arrowRight" size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};


const AdminAiAssistant = React.memo(({ darkMode, adminChatMessages, setAdminChatMessages, isChatLoading, setIsChatLoading, apiKey, userProfile }) => {
  const [input, setInput] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isChatLoading) return;

    const userMsg = { role: 'user', content: input.trim() };
    const currentInput = input.trim();
    setAdminChatMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsChatLoading(true);

    try {
      const manualContext = `Faltas Leves (Tipo I): Incumplimiento de uniforme, retardos, irrespeto verbal leve. Acciones: Diálogo, compromiso, citación.
      Faltas Graves (Tipo II): Agresión recurrente, fraude, daño a propiedad. Acciones: Suspensión 1-3 días, acompañamiento.
      Faltas Muy Graves (Tipo III): Armas, drogas, bullying grave, abuso. Acciones: Suspensión hasta 5 días, comité de convivencia, cancelación matrícula.
      Principios: Amor, Servicio, Cristocentrismo.`;

      const history = adminChatMessages.map(m => `${m.role === 'user' ? 'Coordinador' : 'IA'}: ${m.content}`).join('\n');

      const promptText = `Actúa como un experto en convivencia escolar colombiana (Ley 1620 y Decreto 1965).
Contexto: Colegio Adventista del Norte (CAN).
Manual de Convivencia: ${manualContext}

Instrucciones:
1. Responde de forma muy concisa, profesional y estructurada.
2. Usa emojis para enfatizar puntos clave (🚨, 🔴, ⚖️, 🌱, 📞).
3. Clasifica las faltas según el manual (Tipo I, II o III).
4. Sugiere acciones restaurativas y pedagógicas específicas.
5. En situaciones de posible abuso, activa protocolos de protección.

${history ? `Historial de conversación:\n${history}\n\n` : ''}Nueva consulta del Coordinador: ${currentInput}`;

      const requestBody = {
        contents: [{ role: 'user', parts: [{ text: promptText }] }],
        safetySettings: [
          { category: "HARM_CATEGORY_HARASSMENT", threshold: "BLOCK_NONE" },
          { category: "HARM_CATEGORY_HATE_SPEECH", threshold: "BLOCK_NONE" },
          { category: "HARM_CATEGORY_SEXUALLY_EXPLICIT", threshold: "BLOCK_NONE" },
          { category: "HARM_CATEGORY_DANGEROUS_CONTENT", threshold: "BLOCK_NONE" }
        ],
        generationConfig: { temperature: 0.7, maxOutputTokens: 2048 }
      };

      const modelsToTry = [
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`,
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-pro:generateContent?key=${apiKey}`,
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`,
      ];

      let aiResponse = "";
      let lastApiError = "";

      for (const url of modelsToTry) {
        try {
          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
          });
          const result = await res.json();

          if (res.ok) {
            const candidate = result.candidates?.[0];
            const text = candidate?.content?.parts?.[0]?.text;
            const finishReason = candidate?.finishReason;

            if (text) {
              aiResponse = text;
            } else if (finishReason === 'SAFETY' || !result.candidates?.length) {
              aiResponse = "🚨 Este caso activó filtros de seguridad automáticos de la IA. Por favor reformula el caso usando lenguaje técnico y profesional.";
            } else {
              aiResponse = "No pude generar una respuesta completa. Por favor, reformula tu consulta.";
            }
            break;
          } else {
            lastApiError = `[${res.status}] ${result.error?.message || "Desconocido"}`;
            console.warn(`Modelo no disponible:`, lastApiError);
          }
        } catch (fetchErr) {
          lastApiError = `Error de red: ${fetchErr.message}`;
          console.warn(lastApiError);
        }
      }

      if (!aiResponse) {
        aiResponse = `⚠️ Error de conexión con la IA. (Detalle: ${lastApiError}). Verifica tu clave API.`;
      }

      setAdminChatMessages(prev => [...prev, { role: 'model', content: aiResponse }]);
    } catch (err) {
      console.error(err);
      setAdminChatMessages(prev => [...prev, { role: 'model', content: `⚠️ Error de procesamiento: ${err.message}` }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[70vh] max-h-[800px] border-t-8 border-indigo-500 rounded-3xl overflow-hidden shadow-2xl transition-colors">
      <div className={`${darkMode ? 'bg-slate-900  border-slate-800' : 'bg-white border-slate-100'} p-6 border-b flex items-center justify-between`}>
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${darkMode ? 'bg-indigo-500/20 text-indigo-400 shadow-[0_0_20px_rgba(99,102,241,0.2)]' : 'bg-indigo-50 text-indigo-600'}`}>
            <span className="text-2xl">🤖</span>
          </div>
          <div>
            <h3 className={`font-black uppercase tracking-tight text-lg ${darkMode ? 'text-white' : 'text-navy'} leading-tight`}>IA Coordinación</h3>
            <p className={`text-[10px] font-bold uppercase tracking-widest ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Predictor Comportamental & Legal</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setAdminChatMessages([])} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-colors ${darkMode ? 'bg-slate-800 text-slate-400 hover:bg-red-900/40 hover:text-red-400' : 'bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-600'}`}>Limpiar Chat</button>
          <div className="hidden sm:block">
            <span className={`text-[10px] font-black uppercase px-3 py-1.5 rounded-full ${darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>Desarrollado con Gemini</span>
          </div>
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto p-6 space-y-6 ${darkMode ? 'bg-slate-950/50' : 'bg-slate-50'} custom-scrollbar`}>
        {adminChatMessages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center opacity-50">
            <span className="text-6xl mb-4 grayscale">🏛️</span>
            <h4 className={`text-sm font-black uppercase tracking-widest ${darkMode ? 'text-white' : 'text-navy'} mb-2`}>Hola, {userProfile?.name?.split(' ')[0] || 'Admin'}</h4>
            <p className={`text-xs font-bold ${darkMode ? 'text-slate-400' : 'text-slate-500'} max-w-sm`}>Describe un caso estudiantil. Analizaré la situación bajo la Ley 1620 y te daré recomendaciones de sanción y restauración.</p>
          </div>
        ) : (
          adminChatMessages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-[2rem] p-5 shadow-sm border ${msg.role === 'user' ? (darkMode ? 'bg-indigo-900/40 border-indigo-800 text-indigo-50' : 'bg-indigo-600 border-indigo-700 text-white rounded-tr-sm') : (darkMode ? 'bg-slate-900 border-slate-700 text-slate-300 rounded-tl-sm' : 'bg-white border-slate-200 text-slate-700 rounded-tl-sm')}`}>
                <span className={`text-[9px] font-black uppercase tracking-widest block mb-2 opacity-50`}>{msg.role === 'user' ? 'COORDINADOR' : '🤖 IA LEY 1620'}</span>
                <div className="text-sm font-medium leading-relaxed space-y-1">
                  {msg.content.split('\n').map((line, i) => {
                    const parts = line.split(/\*\*(.*?)\*\*/g);
                    const rendered = parts.map((p, j) => j % 2 === 1 ? <strong key={j} className={darkMode ? 'text-indigo-300' : 'text-indigo-700'}>{p}</strong> : p);
                    const lineClass = line.startsWith('🚨') ? (darkMode ? 'text-red-400' : 'text-red-600') :
                      line.startsWith('🔴') ? (darkMode ? 'text-orange-400' : 'text-orange-600') :
                        line.startsWith('⚖️') ? (darkMode ? 'text-amber-400' : 'text-amber-700') :
                          line.startsWith('🌱') ? (darkMode ? 'text-teal-400' : 'text-teal-700') :
                            line.startsWith('📞') ? (darkMode ? 'text-blue-400' : 'text-blue-700') :
                              line.startsWith('🏷️') ? (darkMode ? 'text-purple-400' : 'text-purple-700') :
                                line.startsWith('📌') ? (darkMode ? 'text-pink-400' : 'text-pink-700') : '';
                    return line.trim() ? <p key={i} className={lineClass || undefined}>{rendered}</p> : <br key={i} />;
                  })}
                </div>
              </div>
            </div>
          ))
        )}
        {isChatLoading && (
          <div className="flex justify-start">
            <div className={`max-w-[85%] rounded-[2rem] rounded-tl-sm p-5 border ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'} flex items-center gap-3`}>
              <span className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-indigo-500 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 rounded-full bg-indigo-500 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 rounded-full bg-indigo-500 animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </span>
              <span className={`text-[10px] font-black uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Analizando jurisprudencia...</span>
            </div>
          </div>
        )}
      </div>

      <div className={`${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'} p-4 border-t`}>
        <form onSubmit={handleSubmit} className="relative flex items-end gap-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (input.trim()) handleSubmit(e);
              }
            }}
            placeholder="Escribe aquí el caso o consulta legal..."
            className={`w-full max-h-32 min-h-[56px] text-sm p-4 rounded-2xl border outline-none resize-none transition-colors custom-scrollbar flex-1 ${darkMode ? 'bg-slate-950 border-slate-700 text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500' : 'bg-slate-50 border-slate-200 text-navy focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'} font-medium`}
            style={{ overflow: 'hidden' }}
            onInput={(e) => {
              e.target.style.height = '56px';
              e.target.style.height = (e.target.scrollHeight) + 'px';
            }}
          />
          <button
            type="submit"
            disabled={!input.trim() || isChatLoading}
            className={`h-14 px-6 rounded-2xl font-black text-xs uppercase transition-colors shadow-lg flex items-center gap-2 ${darkMode ? 'bg-indigo-600 text-white hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-600 disabled:shadow-none' : 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none'} active:scale-95`}
          >
            <span className="hidden sm:inline">Analizar</span> <Icon name="arrowRight" size={16} />
          </button>
        </form>
        <p className={`text-[9px] text-center mt-3 font-semibold ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
          La IA ofrece sugerencias normativas basadas en lineamientos generales. Verifique siempre con el Manual de Convivencia oficial de la institución.
        </p>
      </div>
    </div>
  );
});

const ExcuseCard = React.memo(({
  ex,
  isAdmin,
  darkMode,
  userProfile,
  setSelectedExcuse,
  setRejectId,
  handleUpdateStatus,
  handleDeleteExcuse,
  isAlert
}) => {
  return (
    <div key={ex.id} onClick={() => setSelectedExcuse(ex)} className={`${darkMode ? 'glass-card hover:bg-white/10' : 'glass-card-light hover:bg-white/80'} p-4 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] relative overflow-hidden cursor-pointer transition-colors duration-200 mb-4 sm:mb-6 group ${isAlert ? 'ring-2 ring-red-500/50 shadow-[0_0_40px_rgba(239,68,68,0.2)]' : ''}`} style={{ willChange: 'transform', contain: 'layout style' }}>
      {isAlert && <div className="absolute top-0 left-0 w-full bg-gradient-to-r from-transparent via-red-600 to-transparent text-white text-[10px] font-black text-center py-1.5 uppercase tracking-[0.3em] animate-pulse z-20">⚠️ ALERTA CRÍTICA</div>}

      <div className="absolute inset-0 bg-gradient-to-r from-gold/0 via-gold/5 to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />

      <div className={`flex flex-col gap-4 relative z-10 ${isAlert ? 'mt-4 sm:mt-6' : ''}`}>
        {/* Badge prominente del tipo de excusa */}
        <div className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl border-2 ${
          ex.type === 'uniform' ? (darkMode ? 'bg-purple-950/50 border-purple-500/40 text-purple-300' : 'bg-purple-50 border-purple-300 text-purple-700') :
          ex.type === 'absence' ? (darkMode ? 'bg-amber-950/50 border-amber-500/40 text-amber-300' : 'bg-amber-50 border-amber-300 text-amber-700') :
          ex.type === 'medical' ? (darkMode ? 'bg-rose-950/50 border-rose-500/40 text-rose-300' : 'bg-rose-50 border-rose-300 text-rose-700') :
          ex.type === 'medical_appointment' ? (darkMode ? 'bg-pink-950/50 border-pink-500/40 text-pink-300' : 'bg-pink-50 border-pink-300 text-pink-700') :
          ex.type === 'early_exit' ? (darkMode ? 'bg-teal-950/50 border-teal-500/40 text-teal-300' : 'bg-teal-50 border-teal-300 text-teal-700') :
          ex.type === 'tardiness' ? (darkMode ? 'bg-orange-950/50 border-orange-500/40 text-orange-300' : 'bg-orange-50 border-orange-300 text-orange-700') :
          (darkMode ? 'bg-slate-800/50 border-slate-600 text-slate-300' : 'bg-slate-50 border-slate-300 text-slate-600')
        }`}>
          <span className={`text-lg sm:text-xl`}>
            {ex.type === 'uniform' ? '👔' : ex.type === 'absence' ? '📋' : ex.type === 'medical' ? '🏥' : ex.type === 'medical_appointment' ? '🩺' : ex.type === 'early_exit' ? '🚪' : ex.type === 'tardiness' ? '⏰' : '📄'}
          </span>
          <span className="text-sm sm:text-base font-black uppercase tracking-wider">
            {ex.type === 'uniform' ? 'UNIFORME' : ex.type === 'absence' ? 'INASISTENCIA' : ex.type === 'medical' ? 'INCAPACIDAD MÉDICA' : ex.type === 'medical_appointment' ? 'CITA MÉDICA' : ex.type === 'early_exit' ? 'SALIDA ANTICIPADA' : ex.type === 'tardiness' ? 'TARDANZA' : 'EXCUSA'}
          </span>
        </div>

        <div className="flex items-start justify-between gap-2 min-w-0">
          <div className="flex-1 min-w-0">
            <h4 className={`font-black uppercase text-sm sm:text-xl tracking-tight break-words ${isAlert ? (darkMode ? 'text-red-400' : 'text-red-600') : (darkMode ? 'text-white' : 'text-navy')}`}>{ex.student_name}</h4>
            <p className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} text-xs sm:text-sm font-medium mt-1 leading-relaxed line-clamp-2`}>
              "{ex.reason}"
            </p>
          </div>
          <span className={`${darkMode ? 'bg-white/5 text-slate-400 border-white/10' : 'bg-navy/5 text-navy border-navy/10'} text-[10px] font-black px-2 py-1 rounded-xl border-2 uppercase tracking-widest shrink-0`}>
            {ex.is_teacher_report || (ex.contact_info && ex.contact_info.includes(ex.student_name)) ? 'DOC' : ex.grade}
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className={`text-[10px] ${darkMode ? 'bg-white/5 text-slate-500 border-white/10' : 'bg-slate-50 text-slate-400 border-slate-100'} font-black px-2.5 py-1 rounded-lg border flex items-center gap-1.5 uppercase tracking-tighter`}>
            <Icon name="calendar" size={11} /> {new Date(ex.created_at).toLocaleDateString()}
          </span>
          {ex.valid_from && (
            <span className={`text-[10px] ${darkMode ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' : 'bg-teal-50 text-teal-700 border-teal-200'} font-black px-2.5 py-1 rounded-lg border flex items-center gap-1.5 uppercase tracking-tighter`}>
              <Icon name="check" size={11} /> {new Date(ex.valid_from).toLocaleDateString()}
            </span>
          )}
          <span className={`text-[10px] ${darkMode ? 'bg-gold/20 text-gold border-gold/30' : 'bg-gold/10 text-amber-700 border-gold/20'} font-bold px-2.5 py-1 rounded-lg border flex items-center gap-1.5 uppercase tracking-tighter`}>
            <Icon name="clock" size={11} /> {ex.days || 1} DÍA(S)
          </span>
        </div>

        {ex.status === 'rejected' && ex.rejection_reason && (
          <div className={`${darkMode ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-red-50 text-red-600 border-red-100'} text-xs font-black p-3 rounded-xl border-2 flex items-start gap-2`}>
            <Icon name="alert" size={14} className="shrink-0 mt-0.5" />
            <span>MOTIVO RECHAZO: {ex.rejection_reason}</span>
          </div>
        )}

        <div className="flex flex-col 2xl:flex-row flex-wrap items-stretch 2xl:items-center justify-between gap-3 mt-4">
          {ex.file_url && userProfile.role === 'admin' && (
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedExcuse(ex); }}
              className={`w-full 2xl:w-auto shrink-0 p-3 flex justify-center items-center gap-2 ${darkMode ? 'bg-white/5 text-slate-400 hover:text-white border-white/10' : 'bg-slate-100 text-slate-500 hover:text-navy border-slate-200'} rounded-xl border transition-colors hover:scale-105 shadow-sm font-bold text-[10px] sm:text-xs uppercase`}
            >
              <Icon name="eye" size={16} /> VER ADJUNTO
            </button>
          )}

          {isAdmin && ex.status === 'pending_review' ? (
            <div className="flex gap-2 w-full 2xl:w-auto 2xl:ml-auto">
              <button onClick={(e) => { e.stopPropagation(); setRejectId(ex.id) }} className={`flex-1 2xl:flex-none px-2 sm:px-4 py-3 sm:py-2.5 ${darkMode ? 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'} rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest border transition-colors min-w-[100px]`}>RECHAZAR ❌</button>
              <button onClick={(e) => { e.stopPropagation(); handleUpdateStatus(ex.id, 'approved'); }} className="flex-1 2xl:flex-none px-2 sm:px-4 py-3 sm:py-2.5 bg-emerald-600 text-white rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest hover:bg-emerald-700 transition-colors border border-white/20 shadow-sm shadow-emerald-600/20 min-w-[100px]">APROBAR ✅</button>
              <button onClick={(e) => { e.stopPropagation(); handleDeleteExcuse(ex.id) }} className={`flex-none px-3 py-3 sm:py-2.5 ${darkMode ? 'bg-red-900/20 text-red-500 hover:bg-red-900/40' : 'bg-red-100 text-red-700 hover:bg-red-200'} rounded-xl transition-colors border border-transparent`} title="Eliminar Excusa"><Icon name="trash" size={16} /> 🗑️</button>
            </div>
          ) : (
            <div className="flex gap-2 w-full 2xl:w-auto 2xl:ml-auto items-center">
              <div className={`w-full text-center px-4 py-3 sm:py-2.5 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest border-2 ${ex.status === 'approved' ? (darkMode ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border-emerald-200') : ex.status === 'rejected' ? (darkMode ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-700 border-red-200') : (darkMode ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-amber-50 text-amber-700 border-amber-200 shadow-inner')}`}>
                {ex.status === 'approved' ? 'AUTORIZADO ✅' : ex.status === 'rejected' ? 'RECHAZADO ❌' : 'PENDIENTE ⏳'}
              </div>
              {isAdmin && (
                <button onClick={(e) => { e.stopPropagation(); handleDeleteExcuse(ex.id) }} className={`flex-none px-3 py-3 sm:py-2.5 ${darkMode ? 'bg-red-900/20 text-red-500 hover:bg-red-900/40 border-red-900/50' : 'bg-red-100 text-red-700 hover:bg-red-200 border-red-200'} rounded-xl transition-colors border shadow-sm`} title="Eliminar Excusa"><Icon name="trash" size={16} /></button>
              )}
            </div>
          )}
        </div>
        {ex.status === 'approved' && ex.type !== 'early_exit' && ex.type !== 'tardiness' && (
          <div className="w-full mt-2">
            <CountdownTimer
              startDate={ex.valid_from || ex.created_at}
              days={ex.days}
              darkMode={darkMode}
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default function App() {
  const [supabase, setSupabase] = useState(null);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [excuses, setExcuses] = useState([]);
  const [tardies, setTardies] = useState([]);
  const [infirmaryRecords, setInfirmaryRecords] = useState([]);
  const [capellaniaRecords, setCapellaniaRecords] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [schoolLogo, setSchoolLogo] = useState(null);
  const [view, setView] = useState('login');

  const [adminTab, setAdminTab] = useState('excuses');
  const [adminExcuseTypeFilter, setAdminExcuseTypeFilter] = useState('all');
  const [adminExcuseGradeFilter, setAdminExcuseGradeFilter] = useState('all');
  const [teacherFilter, setTeacherFilter] = useState('my_grade');
  const [teacherGradeFilter, setTeacherGradeFilter] = useState('all');
  const [tardyGradeFilter, setTardyGradeFilter] = useState('all');
  const [parentTab, setParentTab] = useState('all');

  const [loading, setLoading] = useState(true);
  const [showZoomAnim, setShowZoomAnim] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiStatus, setAiStatus] = useState("");
  const [notification, setNotification] = useState(null);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [welcomeUser, setWelcomeUser] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [requestingCode, setRequestingCode] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);

  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  const [showParentForm, setShowParentForm] = useState(false);
  const [selectedExcuse, setSelectedExcuse] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [excuseType, setExcuseType] = useState('absence');
  const [rejectId, setRejectId] = useState(null);
  const [reasonText, setReasonText] = useState("");

  // Edición de tardanzas y estudiantes (admin)
  const [editingTardy, setEditingTardy] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [extendingExcuse, setExtendingExcuse] = useState(null);
  const [parentProfiles, setParentProfiles] = useState([]);
  const [studentsSearchFilter, setStudentsSearchFilter] = useState('');
  const [regStudents, setRegStudents] = useState([{ name: '', grade: '' }]);
  const [selectedStudentIndex, setSelectedStudentIndex] = useState(0);
  const [unreadExcuseCount, setUnreadExcuseCount] = useState(0);
  const [addingStudents, setAddingStudents] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [alertReviews, setAlertReviews] = useState([]);

  // IA Coordinación
  const [adminChatMessages, setAdminChatMessages] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [investigatingName, setInvestigatingName] = useState(null);

  // Enfermería
  const [nursingPermissions, setNursingPermissions] = useState([]);
  const [showNursingForm, setShowNursingForm] = useState(false);
  const [denialId, setDenialId] = useState(null);
  const [denialReason, setDenialReason] = useState("");
  const [editingInfirmary, setEditingInfirmary] = useState(null);
  const [editingCapellania, setEditingCapellania] = useState(null);

  // Llamados de Atención Docentes
  const [teacherAttentionCalls, setTeacherAttentionCalls] = useState([]);
  const [selectedTeacherForAttention, setSelectedTeacherForAttention] = useState(null);
  const [attentionType, setAttentionType] = useState('attention');
  const [attentionObservation, setAttentionObservation] = useState('');
  const [teacherNovelties, setTeacherNovelties] = useState([]);
  const [editingAttentionCallId, setEditingAttentionCallId] = useState(null);
  const [submissionStep, setSubmissionStep] = useState(null);
  const [localHiddenAlerts, setLocalHiddenAlerts] = useState(new Set());
  const [pendingReview, setPendingReview] = useState(null); // { name, type, month, year }
  const [selectedFaltaLeve, setSelectedFaltaLeve] = useState(null);
  const [currentViewPeriod, setCurrentViewPeriod] = useState(() => {
    const saved = safeStorage.getItem('current_view_period');
    return saved ? parseInt(saved) : 1;
  });
  const [periodsConfig, setPeriodsConfig] = useState([{ id: 1, start_date: '2024-01-01T00:00:00Z' }]);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = safeStorage.getItem('theme_preference');
    if (saved) return saved === 'dark';
    const hour = new Date().getHours();
    return hour >= 18 || hour < 6;
  });
  const [historyPage, setHistoryPage] = useState(20);
  const [imageError, setImageError] = useState(false);

  // Bloquear scroll del cuerpo cuando hay modales abiertos
  useEffect(() => {
    const hasModalOpen = selectedExcuse || selectedFaltaLeve || rejectId || editingTardy || editingStudent || extendingExcuse || selectedTeacherForAttention || pendingReview || editingInfirmary || editingTeacher;
    if (hasModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [selectedExcuse, selectedFaltaLeve, rejectId, editingTardy, editingStudent, extendingExcuse, selectedTeacherForAttention, pendingReview, editingInfirmary, editingTeacher]);

  // Reset del error de imagen al cambiar de excusa seleccionada
  useEffect(() => {
    setImageError(false);
  }, [selectedExcuse]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    safeStorage.setItem('theme_preference', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    safeStorage.setItem('current_view_period', currentViewPeriod.toString());
  }, [currentViewPeriod]);

  useEffect(() => {
    if (schoolLogo) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = schoolLogo;
    }
  }, [schoolLogo]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  // --- DATA OPTIMIZATIONS ---
  const memoizedData = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const currentPeriodData = periodsConfig.find(p => p.id === currentViewPeriod) || periodsConfig[0];
    const nextPeriodData = periodsConfig.find(p => p.id === currentViewPeriod + 1);
    const periodStart = currentViewPeriod === 1 ? new Date(0) : new Date(currentPeriodData.start_date);
    const periodEnd = nextPeriodData ? new Date(nextPeriodData.start_date) : new Date(now.getTime() + 1000 * 60 * 60 * 24 * 365); // Far future if no next period

    const filterByPeriod = (list) => list.filter(item => {
      const dt = new Date(item.created_at);
      return dt >= periodStart && dt < periodEnd;
    });

    const filteredExcuses = filterByPeriod(excuses);
    const filteredTardies = filterByPeriod(tardies);
    const filteredInfirmary = filterByPeriod(infirmaryRecords);
    const filteredCapellania = filterByPeriod(capellaniaRecords);
    const filteredNursingPermissions = filterByPeriod(nursingPermissions);

    const excusesByStudent = {};
    filteredExcuses.forEach(e => {
      const norm = normalName(e.student_name);
      if (!excusesByStudent[norm]) excusesByStudent[norm] = [];
      excusesByStudent[norm].push(e);
    });

    const tardiesByStudent = {};
    filteredTardies.forEach(t => {
      const norm = normalName(t.student_name);
      if (!tardiesByStudent[norm]) tardiesByStudent[norm] = [];
      tardiesByStudent[norm].push(t);
    });

    const infirmaryByStudent = {};
    filteredInfirmary.forEach(i => {
      const norm = normalName(i.student_name);
      if (!infirmaryByStudent[norm]) infirmaryByStudent[norm] = [];
      infirmaryByStudent[norm].push(i);
    });

    const capellaniaByStudent = {};
    filteredCapellania.forEach(c => {
      const norm = normalName(c.student_name);
      if (!capellaniaByStudent[norm]) capellaniaByStudent[norm] = [];
      capellaniaByStudent[norm].push(c);
    });

    const reviewsByStudent = {};
    alertReviews.forEach(r => {
      const norm = normalName(r.student_name);
      if (r.review_month === (currentMonth + 1) && r.review_year === currentYear) {
        if (!reviewsByStudent[norm] || new Date(r.reviewed_at) > new Date(reviewsByStudent[norm].reviewed_at)) {
          reviewsByStudent[norm] = r;
        }
      }
    });

    const seenNorms = new Set();
    const uniqueStudentNames = [];
    [...studentOptions, ...filteredTardies.map(t => t.student_name || ""), ...filteredExcuses.map(e => e.student_name || ""), ...filteredInfirmary.map(i => i.student_name || "")].forEach(name => {
      if (!name) return;
      const norm = normalName(name);
      if (!seenNorms.has(norm)) {
        seenNorms.add(norm);
        uniqueStudentNames.push(name.trim());
      }
    });

    const studentAlerts = {};
    const fifteenDaysAgo = new Date(now);
    fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);

    // Alerts use current month/year and last 15 days, BUT must be within the period too.
    const isM2 = (d) => {
      const dt = new Date(d);
      return dt >= periodStart && dt < periodEnd && dt.getMonth() === currentMonth && dt.getFullYear() === currentYear;
    };
    const isLast15 = (d) => {
      const dt = new Date(d);
      return dt >= periodStart && dt < periodEnd && dt >= fifteenDaysAgo;
    };

    uniqueStudentNames.forEach(name => {
      const norm = normalName(name);
      const studentExcuses = excusesByStudent[norm] || [];
      const studentTardis = tardiesByStudent[norm] || [];
      const studentInfirm = infirmaryByStudent[norm] || [];

      const counts = {
        absence: studentExcuses.filter(e => (e.type === 'absence' || e.type === 'tardiness') && isM2(e.created_at)).length,
        uniform: studentExcuses.filter(e => e.type === 'uniform' && isM2(e.created_at)).length,
        tardy: studentTardis.filter(t => isLast15(t.created_at)).length,
        infirmary: studentInfirm.filter(i => isLast15(i.created_at)).length
      };

      const reviewThisMonth = reviewsByStudent[norm];
      let hasNewActivity = false;
      if (reviewThisMonth) {
        const reviewedAt = new Date(reviewThisMonth.reviewed_at);
        const newTardy = studentTardis.some(t => new Date(t.created_at) > reviewedAt && isLast15(t.created_at));
        const newExcuse = studentExcuses.some(e => new Date(e.created_at) > reviewedAt && (e.type === 'absence' || e.type === 'tardiness' || e.type === 'uniform') && isM2(e.created_at));
        const newInfirmary = studentInfirm.some(i => new Date(i.created_at) > reviewedAt && isLast15(i.created_at));
        hasNewActivity = newTardy || newExcuse || newInfirmary;
      }

      studentAlerts[norm] = {
        counts,
        hasCritical: counts.absence >= 3 || counts.uniform >= 3 || counts.tardy >= 3 || counts.infirmary >= 3,
        hasNewActivity
      };
    });

    return {
      excusesByStudent,
      tardiesByStudent,
      infirmaryByStudent,
      reviewsByStudent,
      uniqueStudentNames,
      studentAlerts,
      currentMonth: currentMonth + 1,
      currentYear,
      filteredExcuses,
      filteredTardies,
      filteredInfirmary,
      filteredCapellania,
      capellaniaByStudent,
      filteredNursingPermissions
    };
  }, [excuses, tardies, infirmaryRecords, capellaniaRecords, alertReviews, studentOptions, currentViewPeriod, periodsConfig, nursingPermissions]);


  const notify = useCallback((text, type = "info", image = null) => {
    setNotification({ text, type, image });
    setTimeout(() => setNotification(null), 6000);
  }, []);

  const checkTourStatus = useCallback((userId) => {
    const hasSeenTour = safeStorage.getItem(`tour_seen_${userId}`);
    if (!hasSeenTour) {
      setShowTour(true);
      setTourStep(0);
    }
  }, []);

  const handleTourComplete = () => {
    setShowTour(false);
    if (user) {
      safeStorage.setItem(`tour_seen_${user.id}`, 'true');
    }
  };

  const fetchData = useCallback(async () => {
    if (!supabase || !user || !userProfile) return;
    try {
      // Parallelize base settings and metadata
      const [pCfg, nursingD] = await Promise.all([
        supabase.from('settings').select('value').eq('key', 'periods_config').single(),
        supabase.from('nursing_permissions').select('*').order('created_at', { ascending: false })
      ]);

      const pData = pCfg.data ? JSON.parse(pCfg.data.value) : [{ id: 1, start_date: '2024-01-01T00:00:00Z' }];
      setPeriodsConfig(pData);
      if (nursingD.data) setNursingPermissions(nursingD.data);

      if (userProfile.role === 'admin') {
        const [adminExD, teachersD, profilesD, exNames, trDNames, infDNames, reviewsD, trD, infD, attD] = await Promise.all([
          supabase.from('excuses').select('*').order('created_at', { ascending: false }),
          supabase.from('profiles').select('*').eq('role', 'teacher'),
          supabase.from('profiles').select('student_represented').not('student_represented', 'is', null),
          supabase.from('excuses').select('student_name'),
          supabase.from('tardies').select('student_name'),
          supabase.from('infirmary_records').select('student_name'),
          supabase.from('alert_reviews')
            .select('*')
            .eq('review_month', new Date().getMonth() + 1)
            .eq('review_year', new Date().getFullYear())
            .order('reviewed_at', { ascending: false }),
          supabase.from('tardies').select('*').order('created_at', { ascending: false }),
          supabase.from('infirmary_records').select('*').order('created_at', { ascending: false }),
          supabase.from('teacher_attention_calls').select('*').order('created_at', { ascending: false })
        ]);

        if (adminExD.data) setExcuses(adminExD.data);
        if (teachersD.data) setTeachers(teachersD.data);
        if (trD.data) setTardies(trD.data);
        if (infD.data) setInfirmaryRecords(infD.data);
        const capD = await supabase.from('capellania_records').select('*').order('created_at', { ascending: false });
        if (capD.data) setCapellaniaRecords(capD.data);
        if (reviewsD.data) setAlertReviews(reviewsD.data);
        if (attD.data) setTeacherAttentionCalls(attD.data);

        const allNames = [];
        if (profilesD.data) {
          profilesD.data.forEach(s => {
            if (s.student_represented) {
              s.student_represented.split('|').forEach(n => {
                if (n.trim()) allNames.push(n.trim());
              });
            }
          });
        }
        [...(exNames.data || []), ...(trDNames.data || []), ...(infDNames.data || [])].forEach(record => {
          if (record.student_name && record.student_name.trim()) {
            allNames.push(record.student_name.trim());
          }
        });
        setStudentOptions([...new Set(allNames)]);

      } else if (userProfile.role === 'teacher') {
        let exQuery = supabase.from('excuses').select('*').eq('is_teacher_report', false);
        if (teacherFilter === 'my_grade') {
          exQuery = exQuery.eq('grade', userProfile.assigned_grade);
        } else if (teacherGradeFilter !== 'all') {
          exQuery = exQuery.eq('grade', teacherGradeFilter);
        }

        const [exD, trD, infD, attD, myNovels] = await Promise.all([
          exQuery.order('created_at', { ascending: false }),
          supabase.from('tardies').select('*').order('created_at', { ascending: false }),
          supabase.from('infirmary_records').select('*').order('created_at', { ascending: false }),
          supabase.from('teacher_attention_calls').select('*').order('created_at', { ascending: false }),
          supabase.from('excuses').select('*').eq('is_teacher_report', true).eq('student_name', userProfile.name).order('created_at', { ascending: false })
        ]);

        if (exD.error) throw exD.error;
        const filteredExD = (exD.data || []).filter(e => !(e.contact_info && e.contact_info.indexOf(e.student_name) !== -1));
        setExcuses(filteredExD);
        if (trD.data) setTardies(trD.data);
        if (infD.data) setInfirmaryRecords(infD.data);
        const capD = await supabase.from('capellania_records').select('*').order('created_at', { ascending: false });
        if (capD.data) setCapellaniaRecords(capD.data);
        if (attD.data) setTeacherAttentionCalls(attD.data);
        if (myNovels.data) setTeacherNovelties(myNovels.data);

      } else if (userProfile.role === 'parent') {
        const [exD, trD, infD] = await Promise.all([
          supabase.from('excuses').select('*').order('created_at', { ascending: false }),
          supabase.from('tardies').select('*').order('created_at', { ascending: false }),
          supabase.from('infirmary_records').select('*').order('created_at', { ascending: false })
        ]);

        if (exD.error) throw exD.error;
        if (exD.data) setExcuses(exD.data);
        if (trD.data) setTardies(trD.data);
        if (infD.data) setInfirmaryRecords(infD.data);
        const capD = await supabase.from('capellania_records').select('*').order('created_at', { ascending: false });
        if (capD.data) setCapellaniaRecords(capD.data);
      }
    } catch (err) {
      console.error(err);
      notify("Error cargando datos", "error");
    }
  }, [supabase, user, userProfile, teacherFilter, teacherGradeFilter, notify]);

  const handleConfirmReview = async () => {
    if (!pendingReview) return;
    const { name, type, month, year } = pendingReview;

    // Optimistic Update: Hide instantly
    setLocalHiddenAlerts(prev => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    setPendingReview(null);

    try {
      const { data: inserted, error } = await supabase.from('alert_reviews').insert([{
        student_name: name,
        reviewed_by: user.id,
        review_month: month,
        review_year: year,
        alert_type: type,
        reviewed_at: new Date().toISOString()
      }]).select().single();
      if (error) throw error;
      // Use inserted record from DB, or build a reliable fallback with current timestamp
      const reviewedAt = new Date();
      reviewedAt.setSeconds(reviewedAt.getSeconds() + 1); // +1s to ensure it's after all existing records
      const newReview = inserted || {
        student_name: name,
        reviewed_by: user.id,
        review_month: month,
        review_year: year,
        alert_type: type,
        reviewed_at: reviewedAt.toISOString()
      };
      setAlertReviews(prev => {
        // Remove any previous review for this student this month, then add the new one
        const filtered = prev.filter(r =>
          !(normalName(r.student_name) === normalName(name) &&
            r.review_month === month &&
            r.review_year === year)
        );
        return [...filtered, newReview];
      });
      // Keep localHiddenAlerts until next render cycle to avoid flicker
      setLocalHiddenAlerts(prev => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      notify(`✅ Alerta de ${name} procesada correctamente`, 'success');
    } catch (err) {
      // Rollback on error
      setLocalHiddenAlerts(prev => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      notify('Error al procesar: ' + err.message, 'error');
    }
  };

  useEffect(() => {
    const initSupabase = async () => {
      if (!window.supabase) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2';
        script.async = true;
        script.onload = () => setupClient();
        script.onerror = () => {
          setLoading(false);
          notify("Error crítico: No se pudo cargar el servicio de base de datos. Verifica tu conexión.", "error");
        };
        document.head.appendChild(script);
      } else { setupClient(); }
    };
    const setupClient = () => {
      const client = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
      setSupabase(client);
      client.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          client.from('profiles').select('*').eq('id', session.user.id).single()
            .then(({ data: profile }) => {
              if (profile) {
                setUser(session.user);
                setUserProfile(profile);
                if (profile.password_changed === false) {
                  setShowChangePassword(true);
                } else {
                  checkTourStatus(session.user.id);
                  setView('dashboard');
                }
              }
            });
        }
        client.from('settings').select('value').eq('key', 'school_logo').single()
          .then(({ data }) => { if (data) setSchoolLogo(data.value); });
        setLoading(false);
        setShowZoomAnim(true);
      });
    };
    initSupabase();

    const handleAppInstalled = () => {
      console.log('App was installed, requesting push permission...');
      requestNotificationPermission('anonymous', 'parent', window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY));
    };
    window.addEventListener('appinstalled', handleAppInstalled);
    
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, [notify, checkTourStatus]);


  useEffect(() => {
    if (!supabase || !user || !userProfile) return;
    fetchData();

    // Debounce realtime refetches to avoid cascading re-renders on bursts of DB events
    let realtimeTimer = null;
    const debouncedFetch = () => {
      clearTimeout(realtimeTimer);
      realtimeTimer = setTimeout(() => fetchData(), 800);
    };

    const channel = supabase.channel('custom-all-channel')
      .on('postgres_changes', { event: '*', schema: 'public' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const newData = payload.new;
          if (userProfile.role !== 'parent' && payload.table === 'excuses') {
            notify("🔔 Nueva excusa recibida", "success");
            if (userProfile.role === 'admin') {
              setUnreadExcuseCount(prev => prev + 1);
              setRecentNotifications(prev => [payload.new, ...prev].slice(0, 10));
            }
          }
          if (userProfile.role === 'parent') {
            const representedString = userProfile.student_represented || "";
            const representedNames = representedString.split(/\s*\|\s*/).map(s => normalName(s));
            const incomingName = normalName(newData.student_name);
            if (payload.table === 'tardies' && representedNames.includes(incomingName)) {
              notify(`⚠️ TARDANZA REGISTRADA: ${newData.student_name}`, "warning", userProfile.avatar_url);
            }
            if (payload.table === 'infirmary_records' && representedNames.includes(incomingName)) {
              notify(`🩺 REPORTE ENFERMERÍA: ${newData.student_name}`, "info", userProfile.avatar_url);
            }
          }
        }
        if (payload.eventType === 'UPDATE') {
          const newData = payload.new;
          if (userProfile.role === 'parent' && payload.table === 'excuses') {
            if (newData.parent_id === user.id) {
              if (newData.status === 'approved') notify("✅ Tu excusa ha sido APROBADA", "success", userProfile.avatar_url);
              if (newData.status === 'rejected') notify("❌ Tu excusa ha sido RECHAZADA", "error", userProfile.avatar_url);
            }
          }
          if (payload.table === 'profiles' && newData.id === user.id) {
            setUserProfile(newData);
            notify("Tu información ha sido actualizada por administración", "info");
          }
        }
        debouncedFetch();
      })
      .subscribe();
    return () => {
      clearTimeout(realtimeTimer);
      supabase.removeChannel(channel);
    };
  }, [supabase, user, userProfile, fetchData, notify]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { email, password } = e.target;
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.value, password: password.value });
    if (error) { notify(error.message, "error"); setLoading(false); return; }

    if (data.user) {
      let { data: profile } = await supabase.from('profiles').select('*').eq('id', data.user.id).single();
      if (!profile && data.user.user_metadata && data.user.user_metadata.full_name) {
        const meta = data.user.user_metadata;
        const newProfile = { id: data.user.id, name: meta.full_name, email: data.user.email, role: meta.role || 'parent', assigned_grade: meta.assigned_grade || '', phone: meta.phone || '', student_represented: meta.student_represented || '', password_changed: true };
        await supabase.from('profiles').insert([newProfile]);
        profile = newProfile;
      }
      if (profile) {
        setUser(data.user);
        setUserProfile(profile);
        
        requestNotificationPermission(data.user.id, profile.role, window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY));

        if (profile.role === 'admin') {
          const lastSeen = safeStorage.getItem(`last_seen_excuses_${data.user.id}`) || 0;
          const { data: newEx } = await supabase.from('excuses')
            .select('*')
            .gt('created_at', new Date(parseInt(lastSeen)).toISOString())
            .order('created_at', { ascending: false })
            .limit(10);

          setUnreadExcuseCount(newEx ? newEx.length : 0);
          setRecentNotifications(newEx || []);
        }

        if (profile.password_changed === false) {
          setWelcomeUser(profile.name);
          setTimeout(() => { setWelcomeUser(null); setShowChangePassword(true); }, 2000);
        } else {
          setWelcomeUser(profile.name);
          setTimeout(() => {
            setWelcomeUser(null);
            checkTourStatus(data.user.id);
            setView('dashboard');
          }, 2500);
        }
      } else {
        notify("Perfil no encontrado. Contacta al administrador.", "error");
      }
    }
    setLoading(false);
  };


  const handleChangePassword = async (e) => {
    e.preventDefault();
    setUploading(true);
    const { newPassword } = getFormData(e.target);
    if (newPassword.length < 6) { notify("Mínimo 6 caracteres", "error"); setUploading(false); return; }

    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) { notify(error.message, "error"); setUploading(false); return; }

    await supabase.from('profiles').update({ password_changed: true }).eq('id', user.id);
    notify("Contraseña actualizada correctamente", "success");
    setUploading(false);
    setShowChangePassword(false);
    checkTourStatus(user.id);
    setView('dashboard');
  };

  const handleRegister = async (e, role) => {
    e.preventDefault();
    setUploading(true);
    const formData = getFormData(e.target);
    const { name, email, password, phone } = formData;

    let student_name = '';
    let grade = '';

    if (role === 'parent') {
      if (regStudents.some(s => !s.name.trim() || !s.grade)) {
        notify("Completa todos los campos de los estudiantes", "error");
        setUploading(false);
        return;
      }
      student_name = regStudents.map(s => s.name.trim()).join(' | ');
      grade = regStudents.map(s => s.grade).join(' | ');
    } else {
      student_name = formData.get('student_name');
      grade = formData.get('grade');
    }

    try {
      let { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: name, role, assigned_grade: grade, phone, student_represented: student_name } } });
      if (error) throw error;
      if (data.user) {
        await supabase.from('profiles').upsert([{ id: data.user.id, name, role, email, assigned_grade: grade, phone, student_represented: student_name, avatar_url: null, password_changed: true }]);
        notify("Registro exitoso", "success"); setView('login');
      }
    } catch (err) { notify(err.message, "error"); } finally { setUploading(false); setAiAnalyzing(false); }
  };

  const handleAddPeriod = async () => {
    if (periodsConfig.length >= 4) return notify("⚠️ Máximo de 4 periodos alcanzado", "warning");
    if (!window.confirm(`¿Estás seguro de que deseas iniciar el Periodo ${periodsConfig.length + 1}? Esto 'limpiará' la vista actual para nuevos registros.`)) return;

    const newPeriod = {
      id: periodsConfig.length + 1,
      start_date: new Date().toISOString()
    };
    const newConfig = [...periodsConfig, newPeriod];

    try {
      setUploading(true);
      await supabase.from('settings').upsert({ key: 'periods_config', value: JSON.stringify(newConfig) });
      setPeriodsConfig(newConfig);
      setCurrentViewPeriod(newPeriod.id);
      notify(`🎉 ¡Periodo ${newPeriod.id} iniciado con éxito!`, "success");
      fetchData();
    } catch (err) {
      notify("❌ Error al crear periodo", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleAdminRegister = async (e) => {
    e.preventDefault();
    setUploading(true);
    const { name, email, password, grade, phone } = getFormData(e.target);
    const role = 'teacher';
    try {
      let { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: name, role, assigned_grade: grade, phone: phone || '' } } });
      if (error) throw error;
      if (data.user) {
        await supabase.from('profiles').upsert([{ id: data.user.id, name, role, email, assigned_grade: grade, phone: phone || '', password_changed: false }]);
        notify("Docente registrado con éxito.", "success");
        e.target.reset();
      }
    } catch (err) { notify(err.message, "error"); } finally { setUploading(false); }
  };

  const handleSubmitExcuse = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const type = formData.get('type');
    const inputCode = (formData.get('verificationCode') || '').toString().trim();
    const validCode = (generatedCode || '').toString().trim();

    if (inputCode !== validCode) return notify("Código incorrecto", "error");
    if (userProfile.role !== 'teacher' && type !== 'early_exit' && type !== 'tardiness' && !selectedFile) return notify("Evidencia requerida", "error");

    setUploading(true);
    setSubmissionStep('ai');
    let fileUrl = null;

    try {
      if (type !== 'early_exit' && type !== 'tardiness' && selectedFile) {
        setAiAnalyzing(true);
        setAiStatus(type === 'medical' || type === 'medical_appointment' ? "Analizando certificado médico..." : "Verificando firma en documento...");
        const base64 = await fileToBase64(selectedFile);
        const analysis = await validateWithGemini(base64, selectedFile.type, type === 'medical' ? 'medical' : 'signature');
        if (!analysis.valid) throw new Error(`DOCUMENTO RECHAZADO POR I.A.: ${analysis.reason}`);

        setSubmissionStep('upload');
        setAiStatus("Documento Validado. Subiendo...");
        const path = `excusas/${Date.now()}_${selectedFile.name.replace(/\s+/g, '_')}`;
        const { error: uploadError } = await supabase.storage.from('evidencias').upload(path, selectedFile);
        if (uploadError) throw new Error(`ERROR AL SUBIR IMAGEN: ${uploadError.message}`);

        fileUrl = supabase.storage.from('evidencias').getPublicUrl(path).data.publicUrl;
      }

      setSubmissionStep('save');
      const daysInput = formData.get('days');
      const days = (type === 'medical_appointment' || type === 'tardiness') ? 1 : (parseInt(daysInput) || 1);
      const valid_from = formData.get('valid_from') || new Date().toISOString().split('T')[0];
      let status = (type === 'tardiness' || (userProfile.role === 'parent' && type === 'absence' && days <= 2)) ? 'approved' : 'pending_review';
      if (userProfile.role === 'teacher') status = 'pending_review';

      let reason = formData.get('reason');
      if (type === 'early_exit') {
        const pickup = userProfile.role === 'teacher' ? 'DOCENTE' : formData.get('pickup');
        reason = `[Salida: ${pickup}] ${reason}`;
      }

      const { error: insertError } = await supabase.from('excuses').insert([{
        student_name: formData.get('studentName'),
        grade: formData.get('grade'),
        days,
        reason,
        type,
        status,
        file_url: fileUrl,
        parent_id: user.id,
        contact_info: `${userProfile.name} | ${userProfile.phone}`,
        valid_from,
        is_teacher_report: userProfile.role === 'teacher'
      }]);

      if (insertError) throw insertError;

      setSubmissionStep('done');
      notify("Reporte enviado correctamente", "success", userProfile.avatar_url);
      setTimeout(() => {
        setSubmissionStep(null);
        e.target.reset(); setSelectedFile(null); setGeneratedCode(null); setShowParentForm(false);
        fetchData();
      }, 1500);
    } catch (err) {
      setSubmissionStep(null);
      if (err.message.includes('DOCUMENTO RECHAZADO POR I.A.')) {
        alert("⚠️ EL DOCUMENTO HA SIDO RECHAZADO:\n\n" + err.message.replace('DOCUMENTO RECHAZADO POR I.A.: ', ''));
      } else {
        notify(err.message, "error");
      }
    } finally {
      setUploading(false);
      setAiAnalyzing(false);
    }
  };

  const handleRegisterTardy = async (e) => {
    e.preventDefault(); setUploading(true);
    const formData = new FormData(e.target);
    const studentName = formData.get('studentName');
    const grade = formData.get('grade');
    const tardyDateInput = formData.get('tardyDate');
    const created_at = tardyDateInput ? new Date(tardyDateInput).toISOString() : new Date().toISOString();
    try {
      await supabase.from('tardies').insert([{ student_name: studentName, grade, created_at }]);
      notify(`Tardanza registrada: ${studentName}`, "success"); e.target.reset(); fetchData();
      sendPushNotification(studentName, '🚨 Nueva Tardanza', `Se ha registrado una llegada tarde para ${studentName}.`, supabase);
    } catch (err) { notify("Error: " + err.message, "error"); }
    setUploading(false);
  };

  const handleEditTardy = async (e) => {
    e.preventDefault(); setUploading(true);
    const formData = new FormData(e.target);
    const studentName = formData.get('studentName');
    const grade = formData.get('grade');
    const dateValue = formData.get('tardyDate');
    try {
      const { data, error } = await supabase.from('tardies')
        .update({ student_name: studentName, grade, created_at: new Date(dateValue).toISOString() })
        .eq('id', editingTardy.id)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Operación bloqueada (Sin permisos).');
      notify(`Tardanza actualizada: ${studentName}`, "success");
      setEditingTardy(null);
      fetchData();
    } catch (err) { notify("Error: " + err.message, "error"); }
    setUploading(false);
  };

  const handleDeleteTardy = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta tardanza?')) return;
    try {
      const { data, error } = await supabase.from('tardies').delete().eq('id', id).select();
      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Operación bloqueada (Sin permisos).');
      notify('Tardanza eliminada', 'success');
      setEditingTardy(null);
      fetchData();
    } catch (err) { notify('Error al eliminar: ' + err.message, 'error'); }
  };

  const handleDeleteExcuse = useCallback(async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta excusa permanentemente?')) return;
    try {
      await supabase.from('excuses').delete().eq('id', id);
      notify('Excusa eliminada correctamente', 'success');
      fetchData();
    } catch (err) { notify('Error al eliminar: ' + err.message, 'error'); }
  }, [supabase, notify, fetchData]);


  const fetchParentProfiles = async () => {
    if (!supabase) return;
    const { data } = await supabase.from('profiles').select('*').eq('role', 'parent').order('name', { ascending: true });
    if (data) setParentProfiles(data);
  };

  const handleEditStudent = async (e) => {
    e.preventDefault(); setUploading(true);
    const student_represented = addingStudents.map(s => s.name.trim()).filter(n => n).join(' | ');
    const assigned_grade = addingStudents.map(s => s.grade).filter(g => g).join(' | ');
    try {
      await supabase.from('profiles').update({ student_represented, assigned_grade }).eq('id', editingStudent.id);
      notify(`Información de estudiantes actualizada`, 'success');
      setEditingStudent(null);
      setAddingStudents([]);
      fetchParentProfiles();
    } catch (err) { notify('Error: ' + err.message, 'error'); }
    setUploading(false);
  };

  const handleExtendExcuse = async (e) => {
    e.preventDefault(); setUploading(true);
    const formData = new FormData(e.target);
    const extraDays = parseInt(formData.get('extraDays')) || 0;
    if (extraDays <= 0) { notify('Ingresa un número válido de días', 'error'); setUploading(false); return; }
    const newDays = (extendingExcuse.days || 1) + extraDays;
    try {
      await supabase.from('excuses').update({ days: newDays }).eq('id', extendingExcuse.id);
      notify(`Excusa actualizada a ${newDays} día(s)`, 'success');
      setExtendingExcuse(null);
      fetchData();
    } catch (err) { notify('Error: ' + err.message, 'error'); }
    setUploading(false);
  };

  const handleCloseAttentionModal = useCallback(() => {
    setSelectedTeacherForAttention(null);
    setEditingAttentionCallId(null);
    setAttentionObservation('');
    setAttentionType('attention');
  }, []);

  const handleCreateAttentionCall = async (e) => {
    e.preventDefault(); setUploading(true);
    try {
      if (editingAttentionCallId) {
        await supabase.from('teacher_attention_calls').update({
          type: attentionType,
          observation: attentionObservation
        }).eq('id', editingAttentionCallId);
        notify(`${attentionType === 'attention' ? 'Llamado de atención' : 'Felicitación'} actualizado(a)`, "success");
      } else {
        await supabase.from('teacher_attention_calls').insert([{
          teacher_name: selectedTeacherForAttention.name,
          type: attentionType,
          observation: attentionObservation,
          created_by: userProfile.name,
          created_at: new Date().toISOString()
        }]);
        notify(`${attentionType === 'attention' ? 'Llamado de atención' : 'Felicitación'} registrado(a)`, "success");
        sendPushNotification(selectedTeacherForAttention.name, attentionType === 'attention' ? '⚠️ Llamado de Atención' : '🌟 Felicitación', `Nuevo registro para ${selectedTeacherForAttention.name}: ${attentionObservation}`, supabase);
      }
      handleCloseAttentionModal();
      fetchData();
    } catch (err) { notify("Error: " + err.message, "error"); }
    setUploading(false);
  };

  const handleDeleteAttentionCall = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este registro?')) return;
    try {
      await supabase.from('teacher_attention_calls').delete().eq('id', id);
      notify('Registro eliminado correctamente', 'success');
      fetchData();
    } catch (err) { notify('Error al eliminar: ' + err.message, 'error'); }
  };

  const handleDownloadAttentionReport = () => {
    if (teacherAttentionCalls.length === 0) return notify("No hay datos", "warning");
    let tableContent = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr style="background-color: #1A2A44; color: white;"><th>Fecha</th><th>Docente</th><th>Tipo</th><th>Observación</th><th>Registrado Por</th></tr></thead><tbody>`;
    teacherAttentionCalls.forEach(call => {
      const typeText = call.type === 'attention' ? 'Atención' : 'Felicitación';
      tableContent += `<tr><td>${new Date(call.created_at).toLocaleString()}</td><td>${call.teacher_name}</td><td>${typeText}</td><td>${call.observation}</td><td>${call.created_by || ''}</td></tr>`;
    });
    tableContent += `</tbody></table></body></html>`;
    const url = window.URL.createObjectURL(new Blob([tableContent], { type: 'application/vnd.ms-excel' }));
    const a = document.createElement('a'); a.href = url; a.download = `Reporte_Docentes_${new Date().toLocaleDateString()}.xls`; a.click();
    notify("Excel descargado", "success");
  };

    const handleRegisterCapellania = async (e) => {
    e.preventDefault();
    if (!userProfile) return notify("Error de sesión", "error");
    const formData = new FormData(e.target);
    try {
      const { error } = await supabase.from('capellania_records').insert([{ student_name: formData.get('studentName'), diagnosis: formData.get('diagnosis'), reporter_name: userProfile.name }]);
      if (error) throw error;
      notify("Visita de Capellanía registrada", "success");
      e.target.reset();
      fetchData();
    } catch (error) { notify("Error al registrar", "error"); }
  };

  const handleEditCapellaniaRecord = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const { error } = await supabase.from('capellania_records')
        .update({ student_name: formData.get('studentName'), diagnosis: formData.get('diagnosis') })
        .eq('id', editingCapellania.id);
      if (error) throw error;
      notify("Registro actualizado", "success");
      setEditingCapellania(null);
      fetchData();
    } catch (error) { notify("Error al actualizar", "error"); }
  };

  const handleDeleteCapellaniaRecord = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este registro?")) return;
    try {
      const { error } = await supabase.from('capellania_records').delete().eq('id', id);
      if (error) throw error;
      notify("Registro eliminado", "success");
      setEditingCapellania(null);
      fetchData();
    } catch (error) { notify("Error al eliminar", "error"); }
  };

  const handleRegisterInfirmary = async (e) => {
    e.preventDefault(); setUploading(true);
    const formData = new FormData(e.target);
    const photoFile = formData.get('photo');
    try {
      let photoUrl = null;
      if (photoFile && photoFile.size > 0) {
        if (photoFile.size > MAX_FILE_SIZE) throw new Error("La foto excede 5MB.");
        const path = `enfermeria/${Date.now()}_${photoFile.name}`;
        await supabase.storage.from('evidencias').upload(path, photoFile);
        photoUrl = supabase.storage.from('evidencias').getPublicUrl(path).data.publicUrl;
      }
      await supabase.from('infirmary_records').insert([{ student_name: formData.get('studentName'), diagnosis: formData.get('diagnosis'), created_at: new Date().toISOString(), photo_url: photoUrl }]);
      notify("Registro de enfermería exitoso", "success"); e.target.reset(); fetchData();
    } catch (err) { notify("Error: " + err.message, "error"); }
    setUploading(false);
  };

  const handleEditInfirmaryRecord = async (e) => {
    e.preventDefault();
    setUploading(true);
    const formData = new FormData(e.target);
    const studentName = formData.get('studentName');
    const diagnosis = formData.get('diagnosis');
    try {
      const { data, error } = await supabase.from('infirmary_records')
        .update({ student_name: studentName, diagnosis })
        .eq('id', editingInfirmary.id)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Operación bloqueada (Sin permisos RLS).');
      notify(`Registro actualizado: ${studentName}`, "success");
      setEditingInfirmary(null);
      fetchData();
    } catch (err) { notify("Error: " + err.message, "error"); }
    setUploading(false);
  };

  const handleDeleteInfirmaryRecord = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este registro de enfermería?')) return;
    try {
      const { data, error } = await supabase.from('infirmary_records').delete().eq('id', id).select();
      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Operación bloqueada (Sin permisos RLS).');
      notify('Registro eliminado', 'success');
      setEditingInfirmary(null);
      fetchData();
    } catch (err) { notify('Error al eliminar: ' + err.message, 'error'); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fileName = `brand/logo_${Date.now()}.png`;
      await supabase.storage.from('evidencias').upload(fileName, file);
      const { data: { publicUrl } } = supabase.storage.from('evidencias').getPublicUrl(fileName);
      await supabase.from('settings').upsert({ key: 'school_logo', value: publicUrl });
      setSchoolLogo(publicUrl);
      notify("Logo actualizado.", "success");
    } catch (err) { notify("Error al subir logo.", "error"); } finally { setUploading(false); }
  };

  const handleDownloadTardiesExcel = () => {
    if (memoizedData.filteredTardies.length === 0) return notify("No hay datos", "warning");
    let tableContent = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr style="background-color: #1A2A44; color: white;"><th>Fecha</th><th>Estudiante</th><th>Grado</th><th>Estado</th></tr></thead><tbody>`;
    memoizedData.filteredTardies.forEach(t => {
      const count = getTardinessCount(memoizedData.filteredTardies, t.student_name);
      const style = count > 3 ? 'background-color: #FEE2E2; color: #DC2626; font-weight: bold;' : '';
      tableContent += `<tr style="${style}"><td>${new Date(t.created_at).toLocaleString()}</td><td>${t.student_name} ${count > 3 ? '(⚠️ +3)' : ''}</td><td>${t.grade}</td><td>LLEGADA TARDE</td></tr>`;
    });
    tableContent += `</tbody></table></body></html>`;
    const url = window.URL.createObjectURL(new Blob([tableContent], { type: 'application/vnd.ms-excel' }));
    const a = document.createElement('a'); a.href = url; a.download = `Reporte_Tardanzas_${new Date().toLocaleDateString()}.xls`; a.click();
    notify("Excel descargado", "success");
  };

  const handleDownloadTeacherExcusesExcel = () => {
    const teacherEx = memoizedData.filteredExcuses.filter(e => e.is_teacher_report || (e.contact_info?.includes(e.student_name)));
    if (teacherEx.length === 0) return notify("No hay datos docentes", "warning");

    let tableContent = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr style="background-color: #56A3B1; color: white;"><th>Fecha</th><th>Docente</th><th>Tipo</th><th>Motivo</th><th>Estado</th><th>Días</th></tr></thead><tbody>`;
    teacherEx.forEach(ex => {
      tableContent += `<tr><td>${new Date(ex.created_at).toLocaleString()}</td><td>${ex.student_name}</td><td>${ex.type}</td><td>${ex.reason}</td><td>${ex.status}</td><td>${ex.days || 1}</td></tr>`;
    });
    tableContent += `</tbody></table></body></html>`;
    const url = window.URL.createObjectURL(new Blob([tableContent], { type: 'application/vnd.ms-excel' }));
    const a = document.createElement('a'); a.href = url; a.download = `Reporte_Excusas_Docentes_${new Date().toLocaleDateString()}.xls`; a.click();
    notify("Excel de docentes descargado", "success");
  };

  const handleDownloadInfirmaryReport = () => {
    if (memoizedData.filteredInfirmary.length === 0) return notify("No hay datos de enfermería", "warning");

    // Stats
    const infByMonth = {};
    memoizedData.filteredInfirmary.forEach(r => {
      const date = new Date(r.created_at);
      const monthStr = date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, '0');
      infByMonth[monthStr] = (infByMonth[monthStr] || 0) + 1;
    });

    const studentCounts = {};
    memoizedData.filteredInfirmary.forEach(r => {
      studentCounts[r.student_name] = (studentCounts[r.student_name] || 0) + 1;
    });

    const sortedInfMonths = Object.keys(infByMonth).sort();
    const monthLabels = sortedInfMonths.map(m => {
      const [year, month] = m.split('-');
      const date = new Date(year, parseInt(month) - 1, 1);
      return date.toLocaleString('es-CO', { month: 'short', year: 'numeric' }).toUpperCase();
    });
    const monthData = sortedInfMonths.map(m => infByMonth[m]);

    const topStudents = Object.entries(studentCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    const studentLabels = topStudents.map(s => s[0]);
    const studentData = topStudents.map(s => s[1]);

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Reporte de Enfermería - CAN Digital</title>
        <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800;900&display=swap');
          :root { --rose: #E11D48; --navy: #1A2A44; --bg: #FFF1F2; }
          body { font-family: 'Outfit', sans-serif; background: var(--bg); color: #334155; padding: 40px; margin: 0; }
          .container { max-width: 1000px; margin: 0 auto; background: white; padding: 40px; border-radius: 2rem; box-shadow: 0 20px 50px rgba(0,0,0,0.05); }
          .header { border-bottom: 3px solid #fce7f3; padding-bottom: 20px; margin-bottom: 40px; display: flex; justify-content: space-between; align-items: center; }
          h1 { color: var(--rose); margin: 0; text-transform: uppercase; font-weight: 900; letter-spacing: -1px; }
          .charts { display: grid; grid-cols: 1; gap: 40px; margin-bottom: 50px; }
          .chart-card { background: #fff; padding: 25px; border-radius: 1.5rem; border: 1px solid #ffe4e6; box-shadow: 0 4px 12px rgba(225,29,72,0.05); }
          .chart-title { font-weight: 800; color: var(--navy); margin-bottom: 20px; text-transform: uppercase; font-size: 14px; letter-spacing: 1px; }
          table { width: 100%; border-collapse: collapse; border-radius: 1rem; overflow: hidden; margin-top: 20px; font-size: 13px; }
          th { background: var(--rose); color: white; padding: 15px; text-align: left; font-weight: 800; text-transform: uppercase; }
          td { padding: 12px 15px; border-bottom: 1px solid #ffe4e6; }
          tr:nth-child(even) { background: #fff1f2/30; }
          @media print { .btn-print { display: none; } }
          .btn-print { background: var(--rose); color: white; border: none; padding: 12px 24px; border-radius: 12px; font-weight: 800; cursor: pointer; float: right; }
        </style>
      </head>
      <body>
        <div class="container">
          <button class="btn-print" onclick="window.print()">IMPRIMIR REPORTE</button>
          <div class="header">
            <div>
              <h1>Reporte de Enfermería</h1>
              <p style="color: #94a3b8; font-weight: 600; margin: 5px 0 0;">Institución Educativa CAN - Gestión de Salud</p>
            </div>
            <div style="text-align: right; color: #64748b; font-size: 12px;">Generado: ${new Date().toLocaleDateString()}</div>
          </div>

          <div class="charts">
            <div class="chart-card">
              <div class="chart-title">Evolución de Visitas (Mensual)</div>
              <canvas id="monthChart" height="100"></canvas>
            </div>
            <div class="chart-card">
              <div class="chart-title">Top 5 Estudiantes con más ingresos</div>
              <canvas id="studentChart" height="100"></canvas>
            </div>
          </div>

          <div class="chart-title">Detalle de Registros</div>
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Estudiante</th>
                <th>Motivo / Diagnóstico</th>
              </tr>
            </thead>
            <tbody>
              ${memoizedData.filteredInfirmary.map(r => `
                <tr>
                  <td style="font-weight: 600;">${new Date(r.created_at).toLocaleString()}</td>
                  <td style="font-weight: 800; color: var(--navy);">${r.student_name}</td>
                  <td style="color: #64748b;">${r.diagnosis}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <script>
          Chart.defaults.font.family = "'Outfit', sans-serif";
          
          new Chart(document.getElementById('monthChart'), {
            type: 'line',
            data: {
              labels: ${JSON.stringify(monthLabels)},
              datasets: [{
                label: 'Visitas',
                data: ${JSON.stringify(monthData)},
                borderColor: '#E11D48',
                backgroundColor: 'rgba(225, 29, 72, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 6,
                pointBackgroundColor: '#E11D48'
              }]
            },
            options: { plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } }
          });

          new Chart(document.getElementById('studentChart'), {
            type: 'bar',
            data: {
              labels: ${JSON.stringify(studentLabels)},
              datasets: [{
                label: 'Ingresos',
                data: ${JSON.stringify(studentData)},
                backgroundColor: ['#E11D48', '#FB7185', '#FDA4AF', '#FECDD3', '#FFE4E6'],
                borderRadius: 8
              }]
            },
            options: { plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } }
          });
        </script>
      </body>
      </html>
    `;

    const win = window.open("", "_blank");
    win.document.write(htmlContent);
    win.document.close();
    notify("Reporte gráfico generado", "success");
  };

  const handleDownloadMedicalExcusesExcel = () => {
    const medicalEx = memoizedData.filteredExcuses.filter(e => e.type === 'medical' || e.type === 'medical_appointment');
    if (medicalEx.length === 0) return notify("No hay reportes de excusas", "warning");
    let tableContent = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr style="background-color: #E11D48; color: white;"><th>Fecha</th><th>Estudiante</th><th>Grado</th><th>Tipo de Excusa</th><th>Estado</th></tr></thead><tbody>`;
    medicalEx.forEach(ex => {
      tableContent += `<tr><td>${new Date(ex.created_at).toLocaleString()}</td><td>${ex.student_name}</td><td>${ex.grade}</td><td>${ex.type}</td><td>${ex.status}</td></tr>`;
    });
    tableContent += `</tbody></table></body></html>`;
    const url = window.URL.createObjectURL(new Blob([tableContent], { type: 'application/vnd.ms-excel' }));
    const a = document.createElement('a'); a.href = url; a.download = `Reporte_Excusas_${new Date().toLocaleDateString()}.xls`; a.click();
    notify("Reporte descargado", "success");
  };

  const handleDownloadGeneralReport = () => {
    // Collect data for students only (no teacher reports)
    const studentExcuses = memoizedData.filteredExcuses.filter(e => !e.is_teacher_report && !(e.contact_info?.includes(e.student_name)));
    const normalExcuses = studentExcuses.filter(e => e.type !== 'early_exit');
    const earlyExits = studentExcuses.filter(e => e.type === 'early_exit');

    if (memoizedData.filteredTardies.length === 0 && memoizedData.filteredInfirmary.length === 0 && studentExcuses.length === 0) {
      return notify("No hay datos para generar el reporte general", "warning");
    }

    // --- Statistics for Charts ---
    const tardyByGrade = {};
    memoizedData.filteredTardies.forEach(t => { tardyByGrade[t.grade] = (tardyByGrade[t.grade] || 0) + 1; });

    // Sort tardy grades properly (e.g. 1°, 2°, 3°...)
    const sortedTardyGrades = Object.keys(tardyByGrade).sort((a, b) => parseInt(a) - parseInt(b));
    const tardyLabels = sortedTardyGrades;
    const tardyData = sortedTardyGrades.map(g => tardyByGrade[g]);

    const infByMonth = {};
    memoizedData.filteredInfirmary.forEach(r => {
      const date = new Date(r.created_at);
      const monthStr = date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, '0');
      infByMonth[monthStr] = (infByMonth[monthStr] || 0) + 1;
    });

    const sortedInfMonths = Object.keys(infByMonth).sort();
    const infLabels = sortedInfMonths.map(m => {
      const [year, month] = m.split('-');
      const date = new Date(year, parseInt(month) - 1, 1);
      return date.toLocaleString('es-CO', { month: 'short', year: 'numeric' }).toUpperCase();
    });
    const infData = sortedInfMonths.map(m => infByMonth[m]);

    const excusesByType = { 'Ausencia': 0, 'Médica': 0, 'Cita Médica': 0, 'Uniforme': 0, 'Salida A.': 0 };
    studentExcuses.forEach(e => {
      let t = 'Ausencia';
      if (e.type === 'medical') t = 'Médica';
      else if (e.type === 'medical_appointment') t = 'Cita Médica';
      else if (e.type === 'uniform') t = 'Uniforme';
      else if (e.type === 'early_exit') t = 'Salida A.';
      excusesByType[t]++;
    });
    // Remove empty types
    Object.keys(excusesByType).forEach(k => { if (excusesByType[k] === 0) delete excusesByType[k]; });

    // --- HTML Generation ---
    let htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Reporte General - CAN Digital</title>
        <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800;900&display=swap');
          
          :root { --navy: #1A2A44; --gold: #D4AF37; --teal: #0D9488; --rose: #E11D48; --amber: #F59E0B; --amber-bg: linear-gradient(135deg, #F59E0B, #D97706); --rose-bg: linear-gradient(135deg, #E11D48, #BE123C); --navy-bg: linear-gradient(135deg, #1A2A44, #0f172a); --bg: #F1F5F9; }
          body { font-family: 'Outfit', system-ui, sans-serif; background-color: var(--bg); color: #334155; margin: 0; padding: 40px; line-height: 1.6; }
          
          @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes fade { from { opacity: 0; } to { opacity: 1; } }
          
          .container { max-width: 1200px; margin: 0 auto; background: white; padding: 50px; border-radius: 24px; box-shadow: 0 20px 40px -15px rgba(0,0,0,0.05); animation: slideUp 0.8s cubic-bezier(0.1, 0.9, 0.2, 1); }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #f1f5f9; padding-bottom: 20px; margin-bottom: 50px; }
          .header h1 { color: var(--navy); margin: 0; text-transform: uppercase; font-size: 32px; font-weight: 900; letter-spacing: -1px; }
          .header-meta { text-align: right; color: #64748B; font-size: 14px; font-weight: 600; }
          
          .section { margin-bottom: 60px; page-break-inside: avoid; animation: fade 1.5s ease-out forwards; }
          .section-title { color: white; padding: 12px 24px; border-radius: 12px; margin-bottom: 25px; text-transform: uppercase; font-size: 16px; font-weight: 800; letter-spacing: 2px; display: inline-block; box-shadow: 0 4px 15px rgba(0,0,0,0.1); }
          .title-navy { background: var(--navy-bg); } .title-amber { background: var(--amber-bg); } .title-rose { background: var(--rose-bg); } .title-teal { background: linear-gradient(135deg, var(--teal), #0f766e); }
          
          .chart-card { background: #ffffff; padding: 30px; border-radius: 20px; border: 1px solid #f8fafc; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.03); transition: transform 0.3s ease; margin-bottom: 30px; }
          .chart-card:hover { transform: translateY(-5px); box-shadow: 0 15px 35px -5px rgba(0,0,0,0.06); }
          .chart-title { text-align: center; font-size: 15px; font-weight: 900; color: var(--navy); margin-bottom: 20px; text-transform: uppercase; letter-spacing: 1px; }
          .chart-container-wrapper { position: relative; height: 250px; width: 100%; display: flex; justify-content: center; }
          
          /* Tablas */
          table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 14px; margin-top: 15px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border-radius: 12px; overflow: hidden; border: 1px solid #f1f5f9; }
          th, td { padding: 15px 20px; text-align: left; border-bottom: 1px solid #f1f5f9; }
          th { font-weight: 900; text-transform: uppercase; letter-spacing: 1px; font-size: 11px; color: white; }
          tr:nth-child(even) { background-color: #f8fafc; }
          tr { transition: background-color 0.2s; }
          tr:hover { background-color: #f1f5f9; }
          .th-tardy { background: var(--amber-bg); } .th-inf { background: var(--rose-bg); } .th-exc { background: var(--navy-bg); } .th-exit { background: linear-gradient(135deg, var(--teal), #0f766e); }
          
          .badge { padding: 6px 10px; border-radius: 6px; font-size: 10px; font-weight: 900; color: white; background: #94A3B8; text-transform: uppercase; letter-spacing: 0.5px; box-shadow: 0 2px 5px rgba(0,0,0,0.1); }
          .badge-approved { background: linear-gradient(135deg, #10B981, #059669); } .badge-rejected { background: linear-gradient(135deg, #EF4444, #DC2626); } .badge-pending { background: linear-gradient(135deg, #F59E0B, #D97706); }
          
          @media print { body { padding: 0; background: white; } .container { box-shadow: none; padding: 0; border-radius: 0; animation:none; } .chart-card { transition:none; transform:none; border: 1px solid #e2e8f0; page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div>
              <h1>Reporte General Consolidado</h1>
              <div style="color: var(--gold); font-weight: bold; margin-top: 5px;">Colegio AngloNorte Digital</div>
            </div>
            <div class="header-meta">
              Generado el: ${new Date().toLocaleDateString()}<br>
              Total Registros: ${memoizedData.filteredTardies.length + memoizedData.filteredInfirmary.length + studentExcuses.length}
            </div>
          </div>
    `;

    // 1. TARDANZAS
    htmlContent += `
          <div class="section">
            <div class="section-title title-amber">Llegadas Tarde (${memoizedData.filteredTardies.length})</div>
    `;

    if (Object.keys(tardyByGrade).length > 0) {
      htmlContent += `
            <div class="chart-card">
              <div class="chart-title">Tardanzas por Grado</div>
              <div class="chart-container-wrapper">
                <canvas id="tardyChart"></canvas>
              </div>
            </div>
      `;
    }

    htmlContent += `
            <table>
              <thead><tr><th class="th-tardy">Fecha</th><th class="th-tardy">Estudiante</th><th class="th-tardy">Grado</th></tr></thead>
              <tbody>
    `;
    memoizedData.filteredTardies.slice(0, 50).forEach(t => {
      htmlContent += `<tr><td>${new Date(t.created_at).toLocaleString()}</td><td><strong>${t.student_name}</strong></td><td>${t.grade}</td></tr>`;
    });
    if (memoizedData.filteredTardies.length > 50) {
      htmlContent += `<tr><td colspan="3" style="text-align:center; color:#64748b; font-style:italic;">(Mostrando los últimos 50 registros de ${memoizedData.filteredTardies.length})</td></tr>`;
    }
    htmlContent += `
              </tbody>
            </table>
          </div>
    `;

    // 2. ENFERMERÍA
    htmlContent += `
          <div class="section">
            <div class="section-title title-rose">Visitas a Enfermería (${memoizedData.filteredInfirmary.length})</div>
    `;

    if (Object.keys(infByMonth).length > 0) {
      htmlContent += `
            <div class="chart-card">
              <div class="chart-title">Visitas Médicas por Mes</div>
              <div class="chart-container-wrapper">
                <canvas id="infChart"></canvas>
              </div>
            </div>
      `;
    }

    htmlContent += `
            <table>
              <thead><tr><th class="th-inf">Fecha</th><th class="th-inf">Estudiante</th><th class="th-inf">Diagnóstico / Motivo</th></tr></thead>
              <tbody>
    `;
    memoizedData.filteredInfirmary.slice(0, 50).forEach(r => {
      htmlContent += `<tr><td>${new Date(r.created_at).toLocaleString()}</td><td><strong>${r.student_name}</strong></td><td>${r.diagnosis}</td></tr>`;
    });
    if (memoizedData.filteredInfirmary.length > 50) {
      htmlContent += `<tr><td colspan="3" style="text-align:center; color:#64748b; font-style:italic;">(Mostrando los últimos 50 registros de ${memoizedData.filteredInfirmary.length})</td></tr>`;
    }
    htmlContent += `
              </tbody>
            </table>
          </div>
    `;

    // 3. EXCUSAS
    htmlContent += `
          <div class="section">
            <div class="section-title title-navy">Inasistencias y Excusas (${normalExcuses.length})</div>
    `;

    if (Object.keys(excusesByType).length > 0) {
      htmlContent += `
            <div class="chart-card">
              <div class="chart-title">Distribución por Tipo de Excusa</div>
              <div class="chart-container-wrapper">
                <canvas id="excuseChart"></canvas>
              </div>
            </div>
      `;
    }

    htmlContent += `
            <table>
              <thead><tr><th class="th-exc">Fecha</th><th class="th-exc">Estudiante</th><th class="th-exc">Grado</th><th class="th-exc">Tipo</th><th class="th-exc">Estado</th></tr></thead>
              <tbody>
    `;
    normalExcuses.slice(0, 50).forEach(e => {
      const statusClass = e.status === 'approved' ? 'approved' : e.status === 'rejected' ? 'rejected' : 'pending';
      const typeLabel = e.type === 'absence' ? 'Ausencia' : e.type === 'medical' ? 'Médica' : e.type === 'medical_appointment' ? 'Cita Médica' : e.type === 'uniform' ? 'Uniforme' : 'Salida';
      htmlContent += `
                  <tr>
                    <td>${new Date(e.created_at).toLocaleDateString()}</td>
                    <td><strong>${e.student_name}</strong></td>
                    <td>${e.grade}</td>
                    <td>${typeLabel}</td>
                    <td><span class="badge badge-${statusClass}">${e.status}</span></td>
                  </tr>
      `;
    });
    htmlContent += `
              </tbody>
            </table>
          </div>
    `;

    // 4. SALIDAS ANTICIPADAS
    htmlContent += `
          <div class="section">
            <div class="section-title title-teal">Salidas Anticipadas (${earlyExits.length})</div>
            <table>
              <thead><tr><th class="th-exit">Fecha</th><th class="th-exit">Estudiante</th><th class="th-exit">Grado</th><th class="th-exit">Motivo</th><th class="th-exit">Estado</th></tr></thead>
              <tbody>
    `;
    earlyExits.slice(0, 50).forEach(e => {
      const statusClass = e.status === 'approved' ? 'approved' : e.status === 'rejected' ? 'rejected' : 'pending';
      htmlContent += `
                  <tr>
                    <td>${new Date(e.created_at).toLocaleDateString()}</td>
                    <td><strong>${e.student_name}</strong></td>
                    <td>${e.grade}</td>
                    <td>${e.reason}</td>
                    <td><span class="badge badge-${statusClass}">${e.status}</span></td>
                  </tr>
      `;
    });
    if (earlyExits.length === 0) {
      htmlContent += `<tr><td colspan="5" style="text-align:center; color:#64748b; font-style:italic;">No hay registros de salidas anticipadas</td></tr>`;
    }
    htmlContent += `
              </tbody>
            </table>
          </div>
        </div>
        
        <script>
          // Configuración general de Chart.js para alinearse al diseño
          Chart.defaults.font.family = "'Outfit', sans-serif";
          Chart.defaults.color = '#64748B';
          
          window.onload = function() {
            // Animación para que las gráficas aparezcan fluidamente
            const delayOptions = {
              animation: {
                y: { duration: 1000, easing: 'easeOutQuart', from: 500 }
              }
            };
            
            // Gráfico de Tardanzas (Línea con área)
            const ctxTardy = document.getElementById('tardyChart');
            if (ctxTardy) {
              new Chart(ctxTardy.getContext('2d'), {
                type: 'line',
                data: {
                  labels: ${JSON.stringify(tardyLabels)},
                  datasets: [{
                    label: 'Tardanzas',
                    data: ${JSON.stringify(tardyData)},
                    borderColor: '#F59E0B',
                    backgroundColor: 'rgba(245, 158, 11, 0.2)',
                    borderWidth: 3,
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#F59E0B',
                    pointRadius: 4,
                  }]
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { 
                    y: { beginAtZero: true, grid: { borderDash: [5, 5] } },
                    x: { grid: { display: false } }
                  },
                  ...delayOptions
                }
              });
            }

            // Gráfico de Enfermería (Barras)
            const ctxInf = document.getElementById('infChart');
            if (ctxInf) {
               new Chart(ctxInf.getContext('2d'), {
                type: 'bar',
                data: {
                  labels: ${JSON.stringify(infLabels)},
                  datasets: [{
                    label: 'Visitas',
                    data: ${JSON.stringify(infData)},
                    backgroundColor: '#E11D48',
                    borderRadius: 6,
                    barPercentage: 0.5
                  }]
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { 
                    y: { beginAtZero: true, grid: { borderDash: [5, 5] } },
                    x: { grid: { display: false } }
                  },
                  animation: { duration: 1500, easing: 'easeOutBounce' }
                }
              });
            }

            // Gráfico de Excusas (Doughnut)
            const ctxExc = document.getElementById('excuseChart');
            if (ctxExc) {
               new Chart(ctxExc.getContext('2d'), {
                type: 'doughnut',
                data: {
                  labels: ${JSON.stringify(Object.keys(excusesByType))},
                  datasets: [{
                    data: ${JSON.stringify(Object.values(excusesByType))},
                    backgroundColor: ['#1A2A44', '#0D9488', '#F59E0B', '#E11D48', '#64748B'],
                    borderWidth: 0,
                    hoverOffset: 4
                  }]
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '65%',
                  plugins: {
                    legend: { position: 'right', labels: { padding: 20, usePointStyle: true, pointStyle: 'circle' } }
                  },
                  animation: { animateScale: true, animateRotate: true, duration: 1500 }
                }
              });
            }
          };
        </script>
      </body>
      </html>
    `;

    // Create a Blob containing the HTML data
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    // Open in new tab or download
    const a = document.createElement('a');
    a.href = url;
    a.download = `CAN_Reporte_General_${new Date().toISOString().split('T')[0]}.html`;
    a.click();

    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    notify("Reporte General Dinámico generado exitosamente", "success");
  };

  const handleUpdateStatus = useCallback(async (id, status, extraData = {}) => {
    // Optimistic update: update local state immediately
    setExcuses(prev => prev.map(e => e.id === id ? { ...e, status, ...extraData } : e));
    try {
      const { error } = await supabase.from('excuses').update({ status, ...extraData }).eq('id', id);
      if (error) throw error;
      notify(status === 'approved' ? '✅ Excusa aprobada correctamente' : '❌ Excusa rechazada', status === 'approved' ? 'success' : 'info');
    } catch (err) {
      // Rollback on error
      fetchData();
      notify("Error al actualizar estado: " + err.message, "error");
    }
  }, [supabase, fetchData, notify]);

  const handleRejectSubmit = async () => {
    // Optimistic update
    setExcuses(prev => prev.map(e => e.id === rejectId ? { ...e, status: 'rejected', rejection_reason: reasonText } : e));
    setRejectId(null); setReasonText("");
    try {
      const { error } = await supabase.from('excuses').update({ status: 'rejected', rejection_reason: reasonText }).eq('id', rejectId);
      if (error) throw error;
      notify('❌ Excusa rechazada correctamente', 'info');
    } catch (err) {
      fetchData();
      notify("Error al rechazar: " + err.message, "error");
    }
  };

  const handleRequestCode = async () => {
    setRequestingCode(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    setGeneratedCode(code);
    setRequestingCode(false);
    notify(`💬 TU CÓDIGO ES: ${code}`, "success");
  };

  const handleRequestNursingPermission = async (e) => {
    e.preventDefault();
    setUploading(true);
    const formData = new FormData(e.target);
    const student_name = formData.get('studentName');
    const grade = formData.get('grade');
    const reason = formData.get('reason');

    try {
      const { error } = await supabase.from('nursing_permissions').insert([{
        student_name,
        grade,
        teacher_id: user.id,
        teacher_name: userProfile.name,
        reason,
        status: 'pending'
      }]);
      if (error) throw error;
      notify("Solicitud de enfermería enviada", "success");
      setShowNursingForm(false);
      e.target.reset();
      fetchData();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateNursingStatus = async (id, status, response = null) => {
    try {
      // Optener la solicitud original para el registro automático
      const { data: originalRequest } = await supabase.from('nursing_permissions').select('*').eq('id', id).single();

      const { error } = await supabase.from('nursing_permissions').update({
        status,
        nurse_response: response
      }).eq('id', id);

      if (error) throw error;

      // Si se aprueba, crear automáticamente el registro en infirmary_records
      if (status === 'accepted' && originalRequest) {
        await supabase.from('infirmary_records').insert([{
          student_name: originalRequest.student_name,
          diagnosis: `[Reporte Docente: ${originalRequest.teacher_name}] Motivo: ${originalRequest.reason}. Respuesta: ${response || 'Atención brindada.'}`,
          created_at: new Date().toISOString(),
        }]);
      }

      notify(status === 'accepted' ? "Permiso aceptado y registrado" : "Permiso denegado", "success");
      setDenialId(null);
      setDenialReason("");
      fetchData();
    } catch (err) {
      notify("Error: " + err.message, "error");
    }
  };

  const handleMergeStudents = async () => {
    if (!window.confirm('¿Deseas unificar los registros de estudiantes con nombres similares? Esta acción actualizará todas las tablas (excusas, tardanzas, etc.) al nombre más común o corregido.')) return;
    setUploading(true);
    try {
      // 1. Obtener todos los nombres posibles de todas las tablas
      const seenNorms = {}; // normalized -> { original, count }

      const addName = (name) => {
        if (!name) return;
        const norm = normalName(name);
        if (!seenNorms[norm]) {
          seenNorms[norm] = { original: name.trim(), count: 1, originals: [name.trim()] };
        } else {
          seenNorms[norm].count++;
          if (!seenNorms[norm].originals.includes(name.trim())) {
            seenNorms[norm].originals.push(name.trim());
          }
        }
      };

      studentOptions.forEach(addName);
      excuses.forEach(e => addName(e.student_name));
      tardies.forEach(t => addName(t.student_name));
      infirmaryRecords.forEach(i => addName(i.student_name));

      let mergedCount = 0;
      for (const norm in seenNorms) {
        const info = seenNorms[norm];
        if (info.originals.length > 1) {
          // Tenemos duplicados. El "maestro" será el que tenga más registros o el primero.
          const master = info.original;
          const duplicates = info.originals.filter(o => o !== master);

          for (const dupe of duplicates) {
            // Actualizar excuses
            await supabase.from('excuses').update({ student_name: master }).eq('student_name', dupe);
            // Actualizar tardies
            await supabase.from('tardies').update({ student_name: master }).eq('student_name', dupe);
            // Actualizar infirmary_records
            await supabase.from('infirmary_records').update({ student_name: master }).eq('student_name', dupe);
            // Actualizar nursing_permissions
            await supabase.from('nursing_permissions').update({ student_name: master }).eq('student_name', dupe);

            // Actualizar perfiles de acudientes
            const { data: parents } = await supabase.from('profiles').select('*').ilike('student_represented', `%${dupe}%`);
            if (parents) {
              for (const parent of parents) {
                const students = parent.student_represented.split(/\s*\|\s*/);
                const updatedStudents = students.map(s => s.trim() === dupe ? master : s.trim());
                // Eliminar duplicados en el array de estudiantes del padre
                const finalStudents = [...new Set(updatedStudents)].join(' | ');
                await supabase.from('profiles').update({ student_represented: finalStudents }).eq('id', parent.id);
              }
            }
            mergedCount++;
          }
        }
      }

      notify(`Se han unificado ${mergedCount} nombres de estudiantes duplicados.`, "success");
      fetchData();
    } catch (err) {
      notify("Error unificando: " + err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    const tempPassword = "CAN" + Math.floor(1000 + Math.random() * 9000);
    if (!window.confirm(`¿Restablecer contraseña para ${userEmail}? La nueva contraseña temporal será: ${tempPassword}`)) return;

    setUploading(true);
    try {
      // Invocamos la Edge Function segura para cambiar la contraseña usando el Service Role en el backend.
      const { data, error: functionError } = await supabase.functions.invoke('reset-password', {
        body: {
          userId: userId,
          id: userId, // Algunos cambios en el backend podrían esperar 'id'
          tempPassword: tempPassword,
          password: tempPassword // Algunos cambios podrían esperar 'password'
        },
      });

      if (functionError) throw functionError;
      if (data && data.error) throw new Error(data.error);

      // Importante: Actualizamos el perfil para marcar que DEBE cambiar la contraseña (password_changed: false)
      // Esto asegura que el sistema lo obligue a cambiarla en su próximo inicio de sesión.
      await supabase.from('profiles').update({ password_changed: false }).eq('id', userId);

      notify(`Contraseña de ${userEmail} restablecida a: ${tempPassword}`, "success");

      // Refrescamos datos locales si es necesario
      fetchData();

    } catch (err) {
      console.error("Reset Error:", err);
      notify("Error restableciendo contraseña: " + err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  const renderParentFeed = () => {
    const studentRepresented = userProfile.student_represented || "";
    // Separamos los hijos que tenga asignados el acudiente
    const studentNamesRaw = studentRepresented.split(/\s*\|\s*/).filter(s => s.trim().length > 0);

    // Normalizamos los nombres registrados por el acudiente para comparación exacta
    const studentNormsSet = new Set(studentNamesRaw.map(n => normalName(n)));
    const isMyStudent = (name) => studentNormsSet.has(normalName(name));

    const myTardies = memoizedData.filteredTardies
      .filter(t => isMyStudent(t.student_name))
      .map(t => ({ ...t, type: 'tardy', created_at: t.created_at }));

    const myCapellania = memoizedData.filteredCapellania
      .map(i => ({ ...i, type: 'capellania', created_at: i.created_at }));

    const myInfirmary = memoizedData.filteredInfirmary
      .filter(i => isMyStudent(i.student_name))
      .map(i => ({ ...i, type: 'infirmary', created_at: i.created_at }));

    const myNursing = nursingPermissions
      .filter(n => isMyStudent(n.student_name))
      .map(n => ({ ...n, type: 'nursing_permission', created_at: n.created_at }));

    const myExcuses = memoizedData.filteredExcuses
      .filter(e => e.parent_id === userProfile.id || isMyStudent(e.student_name))
      .map(e => ({ ...e, type: 'excuse' }));

    const combinedFeed = [
      ...myExcuses,
      ...myTardies,
      ...myInfirmary,
      ...myCapellania,
      ...myNursing
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const filteredFeed = combinedFeed.filter(item => {
      if (parentTab === 'all') return true;
      if (parentTab === 'excuses') return item.type === 'excuse';
      if (parentTab === 'tardies') return item.type === 'tardy';
      if (parentTab === 'infirmary') return item.type === 'infirmary';
      if (parentTab === 'capellania') return item.type === 'capellania';
      return true;
    });

    if (filteredFeed.length === 0) return <p className={`text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'} font-bold py-10`}>NO HAY REPORTES EN ESTA SECCIÓN</p>;

    return filteredFeed.map(item => {
      if (item.type === 'excuse') {
        const norm = normalName(item.student_name);
        const alertData = memoizedData.studentAlerts[norm];
        const isAlert = alertData ? (alertData.counts.absence >= 3 || alertData.counts.uniform >= 3 || alertData.counts.tardy >= 3 || alertData.counts.infirmary >= 3) : false;
        return (
          <ExcuseCard
            key={item.id}
            ex={item}
            isAdmin={false}
            darkMode={darkMode}
            userProfile={userProfile}
            isAlert={isAlert}
            setSelectedExcuse={setSelectedExcuse}
            setRejectId={setRejectId}
            handleUpdateStatus={handleUpdateStatus}
            handleDeleteExcuse={handleDeleteExcuse}
          />
        );
      }

      if (item.type === 'tardy') return (
        <div key={`tardy-${item.id}`} className={`${darkMode ? 'glass-card border-amber-600/50 shadow-lg' : 'bg-white border-amber-500 shadow'} p-4 rounded-2xl border-l-4 mb-4 flex justify-between items-center animate-in fade-in slide-in-from-bottom-2`}>
          <div>
            <h4 className={`font-black uppercase text-sm ${darkMode ? 'text-white' : 'text-navy'} flex items-center gap-2`}><Icon name="clock" size={14} className="text-amber-500" /> {studentNamesRaw.length > 1 ? `TARDANZA: ${item.student_name}` : 'LLEGADA TARDE'}</h4>
            <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'} mt-1`}>{new Date(item.created_at).toLocaleString()}</p>
          </div>
          <span className="text-xl font-bold text-amber-500">RETRASO</span>
        </div>
      );

      if (item.type === 'capellania') return (
  <div key={`capellania-${item.id}`} className={`${darkMode ? 'glass-card border-blue-600/50 shadow-[0_0_15px_rgba(59,130,246,0.15)] hover:bg-white/5' : 'glass-card-light border-blue-400 shadow-md hover:bg-white/80'} p-4 sm:p-6 rounded-[2rem] relative overflow-hidden transition-colors duration-200 mb-4`}>
    <div className="flex items-start justify-between gap-2 mb-3 relative z-10">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-xl border-2 ${darkMode ? 'bg-blue-950/50 border-blue-500/40 text-blue-400' : 'bg-blue-50 border-blue-300 text-blue-600'}`}>
          <Icon name="book" size={20} />
        </div>
        <div>
          <h4 className={`font-black uppercase text-sm ${darkMode ? 'text-white' : 'text-navy'}`}>CAPELLANÍA</h4>
          <span className={`text-[10px] font-bold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{new Date(item.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <span className={`text-[10px] font-black px-2 py-1 rounded-lg border uppercase tracking-widest ${darkMode ? 'bg-blue-950/50 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>REGISTRO</span>
    </div>
    <p className={`${darkMode ? 'text-slate-300' : 'text-slate-700'} text-xs font-medium relative z-10 p-3 rounded-xl ${darkMode ? 'bg-slate-900/50' : 'bg-slate-50'} border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
      {item.diagnosis}
    </p>
  </div>
);

if (item.type === 'infirmary') return (
        <div key={`infirmary-${item.id}`} className={`${darkMode ? 'glass-card border-rose-600/50 shadow-lg' : 'bg-white border-rose-500 shadow'} p-4 rounded-2xl border-l-4 mb-4 animate-in fade-in slide-in-from-bottom-2`}>
          <div className="flex justify-between items-start">
            <div>
              <h4 className={`font-black uppercase text-sm ${darkMode ? 'text-white' : 'text-navy'} flex items-center gap-2`}><Icon name="heart" size={14} className="text-rose-500" /> {studentNamesRaw.length > 1 ? `ENFERMERÍA: ${item.student_name}` : 'REPORTE ENFERMERÍA (REGISTRO)'}</h4>
              <p className={`text-sm font-bold ${darkMode ? 'text-rose-400' : 'text-rose-700'} mt-1 italic`}>"{item.diagnosis}"</p>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'} mt-2`}>{new Date(item.created_at).toLocaleString()}</p>
            </div>
            {item.photo_url && (
              <a href={item.photo_url} target="_blank" rel="noreferrer" className={`w-12 h-12 rounded-lg ${darkMode ? 'bg-slate-800 text-slate-400 hover:text-rose-400' : 'bg-slate-100 text-slate-400 hover:text-rose-500'} flex items-center justify-center`}>
                <Icon name="eye" />
              </a>
            )}
          </div>
        </div>
      );

      if (item.type === 'nursing_permission') return (
        <div key={`nursing-${item.id}`} className={`${darkMode ? 'glass-card hover:bg-white/10' : 'glass-card-light hover:bg-white/80'} p-8 rounded-[2.5rem] relative overflow-hidden transition-colors duration-150 mb-6 group`}>
          <div className="absolute inset-0 bg-gradient-to-r from-rose-500/0 via-rose-500/5 to-rose-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
          <div className="flex flex-col md:flex-row justify-between gap-6 relative z-10">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h4 className={`font-black uppercase text-xl tracking-tight ${darkMode ? 'text-white' : 'text-navy'}`}>{item.student_name}</h4>
                <span className={`${darkMode ? 'bg-white/5 text-slate-400 border-white/10' : 'bg-navy/5 text-navy border-navy/10'} text-[10px] font-black px-3 py-1 rounded-xl border-2 uppercase tracking-widest`}>
                  {item.teacher_name || 'DOCENTE'}
                </span>
              </div>
              <p className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} text-sm font-medium mt-3 leading-relaxed line-clamp-2`}>
                <span className={`font-black px-2 py-0.5 rounded-lg mr-2 ${darkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-500/10 text-rose-600'}`}>
                  ENFERMERÍA
                </span> "{item.reason}"
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                <span className={`text-[10px] ${darkMode ? 'bg-white/5 text-slate-500 border-white/10' : 'bg-slate-50 text-slate-400 border-slate-100'} font-black px-3 py-1 rounded-xl border flex items-center gap-2 uppercase tracking-tighter`}>
                  <Icon name="calendar" size={12} /> {new Date(item.created_at).toLocaleDateString()}
                </span>
              </div>
              {item.nurse_response && (
                <div className={`mt-4 ${darkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-slate-50 border-slate-200'} p-3 rounded-xl border text-xs`}>
                  <strong className={`${darkMode ? 'text-rose-400' : 'text-rose-600'} block mb-1 uppercase text-[10px] tracking-widest`}>Respuesta Enfermería:</strong>
                  {item.nurse_response}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end justify-between gap-4">
              <div className={`px-4 py-2 rounded-[1rem] text-[10px] font-black uppercase tracking-widest border-2 ${item.status === 'accepted' ? (darkMode ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border-emerald-200') : item.status === 'denied' ? (darkMode ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-700 border-red-200') : (darkMode ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-amber-50 text-amber-700 border-amber-200')} shadow-inner`}>
                {item.status === 'accepted' ? 'ACEPTADO ✅' : item.status === 'denied' ? 'RECHAZADO ❌' : 'PENDIENTE ⏳'}
              </div>
            </div>
          </div>
        </div>
      );
      return null;
    });
  };

  if (loading) return (
    <div className="flex h-screen items-center justify-center" style={{ backgroundColor: '#1A2A44' }}>
      <div className="animate-pulse flex items-center justify-center">
        {schoolLogo ? <img src={schoolLogo} alt="Logo" className="h-20 w-auto object-contain" /> : <Icon name="logo" size={80} />}
      </div>
    </div>
  );

  if (showZoomAnim) return (
    <ZoomLoadingScreen
      logoUrl={schoolLogo}
      onDone={() => setShowZoomAnim(false)}
    />
  );

  return (
    <div className={`h-screen overflow-y-auto ${darkMode ? 'liquid-crystal text-white' : 'bg-slate-50 text-navy'} font-sans transition-colors duration-150 relative overflow-x-hidden custom-scrollbar`}>
      {/* Elementos Decorativos de Fondo Global */}
      <div className="liquid-crystal-bg">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-navy/20 rounded-full blur-[120px] ambient-gradient" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-gold/10 rounded-full blur-[120px] ambient-gradient delay-1000" />
        <div className="absolute top-[30%] right-[-5%] w-[30%] h-[30%] bg-teal-500/10 rounded-full blur-[100px] ambient-gradient delay-700" />
      </div>

      {welcomeUser && <WelcomeAnimation name={welcomeUser} logoUrl={schoolLogo} darkMode={darkMode} />}
      {isLoggingOut && <LogoutAnimation logoUrl={schoolLogo} />}

      {showTour && (
        <TourModal
          step={tourStep}
          onNext={() => setTourStep(s => s + 1)}
          onPrev={() => setTourStep(s => Math.max(0, s - 1))}
          onClose={handleTourComplete}
          role={userProfile?.role}
          darkMode={darkMode}
        />
      )}

      {showChangePassword && (
        <div className="fixed inset-0 z-[70] bg-navy/90  flex items-center justify-center p-4">
          <div className={`${darkMode ? 'bg-slate-900 border-red-600/50' : 'bg-white border-red-500'} w-full max-w-sm p-8 rounded-[2rem] shadow-2xl border-t-8 transition-colors`}>
            <div className="text-center mb-6">
              <div className={`w-16 h-16 ${darkMode ? 'bg-red-900/20 text-red-500' : 'bg-red-50 text-red-600'} rounded-full flex items-center justify-center mx-auto mb-4`}><Icon name="lock" size={32} /></div>
              <h2 className={`text-xl font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase tracking-tight`}>CAMBIO DE CONTRASEÑA</h2>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'} mt-2 font-bold`}>Por seguridad, debes cambiar tu contraseña para continuar.</p>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <input
                name="newPassword"
                type="password"
                className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:border-red-600' : 'bg-slate-50 border-slate-200 text-navy focus:border-red-500'} rounded-xl border text-sm font-bold outline-none transition-colors placeholder:text-slate-500`}
                placeholder="Nueva Contraseña"
                required
                minLength={6}
              />
              <button disabled={uploading} className="w-full py-4 bg-red-600 text-white font-black rounded-xl shadow-lg shadow-red-600/20 hover:bg-red-700 transition-colors active:scale-95">
                {uploading ? 'ACTUALIZANDO...' : 'ACTUALIZAR Y ENTRAR'}
              </button>
            </form>
          </div>
        </div>
      )}

      {notification && (
        <div className={`fixed bottom-5 right-5 z-50 p-4 rounded-xl text-white font-bold shadow-lg flex items-center gap-4 animate-in slide-in-from-bottom-5 duration-300 ${notification.type === 'error' ? 'bg-red-600' : notification.type === 'warning' ? 'bg-amber-500' : 'bg-emerald-600'}`}>
          <div className="w-10 h-10 rounded-full border-2 border-white/50 bg-white flex items-center justify-center overflow-hidden shrink-0">
            {notification.image ? <img src={notification.image} className="w-full h-full object-cover" alt="Avatar" /> : <Icon name="user" className="text-slate-300" />}
          </div>
          <span>{notification.text}</span>
        </div>
      )}

      <datalist id="student-list">{studentOptions.map((name, index) => <option key={index} value={name} />)}</datalist>

      {view === 'login' && !showChangePassword && (
        <div className={`flex min-h-screen items-center justify-center p-4 relative overflow-hidden ${darkMode ? 'bg-[#161C24]' : 'bg-[#F4F6F8]'}`}>
          {/* Subtle Minimals Background Elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className={`absolute top-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full blur-[100px] ${darkMode ? 'bg-[#00A76F]/5' : 'bg-[#00A76F]/10'} mix-blend-multiply`} />
            <div className={`absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] rounded-full blur-[100px] ${darkMode ? 'bg-[#8E33FF]/5' : 'bg-[#8E33FF]/10'} mix-blend-multiply`} />
          </div>

          <div className={`w-full max-w-[420px] ${darkMode ? 'bg-[#212B36] shadow-[0_0_2px_0_rgba(0,0,0,0.2),_0_12px_24px_-4px_rgba(0,0,0,0.12)]' : 'bg-white shadow-[0_0_2px_0_rgba(145,158,171,0.2),_0_12px_24px_-4px_rgba(145,158,171,0.12)]'} p-8 md:p-10 rounded-2xl animate-in fade-in slide-in-from-bottom-4 duration-500 relative z-10`}>
            <div className="text-center mb-10">
              <div className={`mx-auto w-20 h-20 flex items-center justify-center mb-6`}>
                {schoolLogo ? <img src={schoolLogo} className="h-16 w-auto object-contain" alt="Logo" /> : <Icon name="logo" size={64} className={darkMode ? 'text-white' : 'text-[#212B36]'} />}
              </div>
              <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-[#212B36]'} leading-tight`}>
                Sign in to <span className="text-[#00A76F]">CAN</span>
              </h1>
              <p className={`text-sm ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'} mt-2`}>Ingresa tus credenciales</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-4">
                <input
                  name="email"
                  className={`w-full px-4 py-3.5 ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'} rounded-xl border text-sm transition-colors outline-none placeholder:text-[#919EAB]`}
                  placeholder="Correo electrónico"
                  required
                />
                <input
                  name="password"
                  type="password"
                  className={`w-full px-4 py-3.5 ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'} rounded-xl border text-sm transition-colors outline-none placeholder:text-[#919EAB]`}
                  placeholder="Contraseña"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full py-3.5 text-white rounded-xl font-bold text-sm bg-[#212B36] hover:bg-[#454F5B] hover:shadow-[0_8px_16px_0_rgba(33,43,54,0.24)] transition-colors duration-200"
              >
                Ingresar
              </button>
            </form>

            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setView('register-parent')}
                className={`text-sm font-medium ${darkMode ? 'text-[#00A76F] hover:text-[#5BE49B]' : 'text-[#00A76F] hover:text-[#007867]'} transition-colors`}
              >
                ¿No tienes cuenta? Regístrate
              </button>
            </div>
          </div>
        </div>
      )}

      {view === 'register-parent' && (
        <div className={`flex min-h-screen items-center justify-center p-4 relative overflow-hidden ${darkMode ? 'bg-[#161C24]' : 'bg-[#F4F6F8]'}`}>
          <div className={`w-full max-w-[480px] ${darkMode ? 'bg-[#212B36] shadow-[0_0_2px_0_rgba(0,0,0,0.2),_0_12px_24px_-4px_rgba(0,0,0,0.12)]' : 'bg-white shadow-[0_0_2px_0_rgba(145,158,171,0.2),_0_12px_24px_-4px_rgba(145,158,171,0.12)]'} p-8 md:p-10 rounded-2xl animate-in fade-in slide-in-from-bottom-4 duration-500 relative z-10`}>
            <h2 className={`text-xl font-bold text-center mb-6 ${darkMode ? 'text-white' : 'text-[#212B36]'}`}>Registro de Acudiente</h2>
            <form onSubmit={(e) => handleRegister(e, 'parent')} className="space-y-4">
              <input name="name" className={`w-full px-4 py-3.5 ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'} rounded-xl border text-sm transition-colors outline-none`} placeholder="Nombre completo" required />
              <div className="grid grid-cols-2 gap-4">
                <input name="phone" type="tel" className={`w-full px-4 py-3.5 ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'} rounded-xl border text-sm transition-colors outline-none`} placeholder="Teléfono" required />
                <input name="email" type="email" className={`w-full px-4 py-3.5 ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'} rounded-xl border text-sm transition-colors outline-none`} placeholder="Correo" required />
              </div>
              <input name="password" type="password" className={`w-full px-4 py-3.5 ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'} rounded-xl border text-sm transition-colors outline-none`} placeholder="Contraseña" required />

              <div className={`mt-6 p-5 rounded-xl border ${darkMode ? 'border-[#919EAB]/20 bg-[#161C24]/50' : 'border-[#919EAB]/30 bg-[#F4F6F8]'}`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-[#212B36]'}`}>Estudiantes</h3>
                  <button type="button" onClick={() => setRegStudents([...regStudents, { name: '', grade: '' }])} className="text-xs font-bold text-[#00A76F] flex items-center gap-1 hover:text-[#007867]">
                    <Icon name="plus" size={14} /> AGREGAR
                  </button>
                </div>
                <div className="space-y-3 max-h-60 overflow-y-auto custom-scrollbar pr-2">
                  {regStudents.map((student, index) => (
                    <div key={index} className={`p-4 rounded-xl border ${darkMode ? 'bg-[#212B36] border-[#919EAB]/20' : 'bg-white border-[#919EAB]/30'} relative`}>
                      {regStudents.length > 1 && (
                        <button type="button" onClick={() => setRegStudents(regStudents.filter((_, i) => i !== index))} className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${darkMode ? 'text-[#919EAB] hover:bg-[#919EAB]/20 hover:text-white' : 'text-[#637381] hover:bg-[#919EAB]/20 hover:text-[#212B36]'}`}>
                          <Icon name="x" size={14} />
                        </button>
                      )}
                      <div className="space-y-3 pt-2">
                        <input
                          value={student.name}
                          onChange={(e) => {
                            const newReg = [...regStudents];
                            newReg[index].name = e.target.value;
                            setRegStudents(newReg);
                          }}
                          className={`w-full px-3 py-2 ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'} rounded-lg border text-xs transition-colors outline-none`}
                          placeholder="Nombre del Estudiante"
                          required
                        />
                        <select
                          value={student.grade}
                          onChange={(e) => {
                            const newReg = [...regStudents];
                            newReg[index].grade = e.target.value;
                            setRegStudents(newReg);
                          }}
                          className={`w-full px-3 py-2 ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'} rounded-lg border text-xs transition-colors outline-none`}
                          required
                        >
                          <option value="">Curso</option>
                          {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button disabled={uploading} className="w-full mt-6 py-3.5 text-white rounded-xl font-bold text-sm bg-[#00A76F] hover:bg-[#007867] hover:shadow-[0_8px_16px_0_rgba(0,167,111,0.24)] transition-colors duration-200">
                {uploading ? 'VERIFICANDO...' : 'REGISTRARME'}
              </button>
            </form>
            <button onClick={() => setView('login')} className={`w-full mt-6 text-sm font-medium ${darkMode ? 'text-[#919EAB] hover:text-white' : 'text-[#637381] hover:text-[#212B36]'}`}>VOLVER</button>
          </div>
        </div>
      )}

      {view === 'dashboard' && (
        <div className={isLoggingOut ? 'animate-out fade-out zoom-out-95 duration-500 fill-mode-forwards' : 'animate-in fade-in zoom-in-95 duration-500'}>
          <header className={`px-6 py-4 sticky top-0 z-40 transition-colors duration-200 border-b  ${darkMode ? 'bg-[#161C24]/90 border-[#919EAB]/20 text-white' : 'bg-white/90 border-[#919EAB]/20 text-[#212B36]'}`}>
            <div className="max-w-[1200px] mx-auto flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${darkMode ? 'bg-[#212B36]' : 'bg-[#F4F6F8]'} rounded-xl flex items-center justify-center`}>
                  {schoolLogo ? <img src={schoolLogo} className="h-8 w-auto object-contain" alt="Logo" /> : <Icon name="logo" size={24} className={darkMode ? 'text-white' : 'text-[#212B36]'} />}
                </div>
                <span className="font-bold text-sm sm:text-base tracking-tight">CAN <span className="text-[#00A76F]">Portal</span></span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {userProfile?.role === 'admin' && (
                  <div className="relative">
                    <button
                      onClick={() => setShowNotifDropdown(!showNotifDropdown)}
                      className={`relative p-2.5 ${darkMode ? 'hover:bg-[#919EAB]/10 text-[#919EAB] hover:text-white' : 'hover:bg-[#919EAB]/10 text-[#637381] hover:text-[#212B36]'} rounded-full transition-colors`}
                    >
                      <Icon name="bell" size={20} />
                      {unreadExcuseCount > 0 && (
                        <span className="absolute top-2 right-2 w-2 h-2 bg-[#FF5630] rounded-full border-2 border-white dark:border-[#161C24]"></span>
                      )}
                    </button>

                    {showNotifDropdown && (
                      <div className={`absolute right-0 mt-2 w-[360px] ${darkMode ? 'bg-[#212B36] border-[#919EAB]/20 shadow-[0_0_2px_0_rgba(0,0,0,0.2),_0_20px_40px_-4px_rgba(0,0,0,0.24)]' : 'bg-white border-[#919EAB]/20 shadow-[0_0_2px_0_rgba(145,158,171,0.24),_0_20px_40px_-4px_rgba(145,158,171,0.24)]'} rounded-xl border overflow-hidden z-[100] animate-in fade-in slide-in-from-top-2 duration-200`}>
                        <div className={`px-5 py-4 ${darkMode ? 'border-[#919EAB]/20' : 'border-[#919EAB]/20'} border-b flex justify-between items-center`}>
                          <h4 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-[#212B36]'}`}>Notificaciones</h4>
                          <button onClick={() => {
                            setUnreadExcuseCount(0);
                            setRecentNotifications([]);
                            setShowNotifDropdown(false);
                            localStorage.setItem(`last_seen_excuses_${user.id}`, Date.now().toString());
                          }} className={`text-xs font-bold text-[#FF5630] hover:text-[#B71D18] transition-colors`}>LIMPIAR</button>
                        </div>
                        <div className="max-h-[360px] overflow-y-auto custom-scrollbar">
                          {recentNotifications.length === 0 ? (
                            <div className={`px-5 py-8 text-center text-sm ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`}>No hay notificaciones</div>
                          ) : (
                            recentNotifications.map(n => (
                              <div
                                key={n.id}
                                onClick={() => {
                                  setSelectedExcuse(n);
                                  setShowNotifDropdown(false);
                                }}
                                className={`px-5 py-4 border-b ${darkMode ? 'border-[#919EAB]/10 hover:bg-[#919EAB]/5' : 'border-[#919EAB]/10 hover:bg-[#919EAB]/5'} cursor-pointer transition-colors`}
                              >
                                <div className="flex justify-between items-start mb-1">
                                  <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-[#212B36]'} truncate pr-2`}>{n.student_name}</span>
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${n.status === 'approved' ? (darkMode ? 'bg-[#22C55E]/10 text-[#77ED8B]' : 'bg-[#22C55E]/20 text-[#118D57]') : (darkMode ? 'bg-[#FFAB00]/10 text-[#FFD666]' : 'bg-[#FFAB00]/20 text-[#B76E00]')}`}>
                                    {n.status === 'approved' ? 'APROBADO' : 'PENDIENTE'}
                                  </span>
                                </div>
                                <p className={`text-xs ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'} line-clamp-2 mt-1 mb-2`}>{n.reason}</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] text-[#919EAB] font-medium">{new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${darkMode ? 'bg-[#919EAB]/10 text-[#919EAB]' : 'bg-[#919EAB]/20 text-[#637381]'}`}>{n.grade}</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setAdminTab('excuses');
                            setShowNotifDropdown(false);
                          }}
                          className={`w-full p-4 text-sm font-bold ${darkMode ? 'text-[#00A76F] hover:bg-[#00A76F]/10' : 'text-[#007867] hover:bg-[#00A76F]/10'} transition-colors border-t ${darkMode ? 'border-[#919EAB]/20' : 'border-[#919EAB]/20'}`}
                        >
                          Ver todas
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Selector de Periodos */}
                <div className={`flex items-center p-1 rounded-xl border ${darkMode ? 'bg-[#212B36] border-[#919EAB]/20' : 'bg-[#F4F6F8] border-[#919EAB]/20'}`}>
                  <span className={`text-[10px] font-bold px-3 hidden md:inline-block ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`}>PERIODO</span>
                  <div className="flex items-center gap-1">
                    {periodsConfig.map(p => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setCurrentViewPeriod(p.id);
                          notify(`Cambiado al Periodo ${p.id}`, "info");
                        }}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors duration-200 ${currentViewPeriod === p.id
                          ? (darkMode ? 'bg-[#161C24] text-white shadow-sm' : 'bg-white text-[#212B36] shadow-sm')
                          : (darkMode ? 'text-[#919EAB] hover:text-white' : 'text-[#637381] hover:text-[#212B36]')}`}
                      >
                        P{p.id}
                      </button>
                    ))}
                  </div>
                  {userProfile?.role === 'admin' && periodsConfig.length < 4 && (
                    <button
                      onClick={handleAddPeriod}
                      className={`ml-1 p-1.5 rounded-lg ${darkMode ? 'text-[#00A76F] hover:bg-[#00A76F]/10' : 'text-[#007867] hover:bg-[#00A76F]/10'} transition-colors`}
                      title="Añadir Nuevo Periodo"
                    >
                      <Icon name="plus" size={16} />
                    </button>
                  )}
                </div>

                <div className={`w-px h-6 mx-1 ${darkMode ? 'bg-[#919EAB]/20' : 'bg-[#919EAB]/30'}`}></div>

                <button onClick={toggleDarkMode} className={`p-2.5 ${darkMode ? 'hover:bg-[#919EAB]/10 text-[#919EAB] hover:text-white' : 'hover:bg-[#919EAB]/10 text-[#637381] hover:text-[#212B36]'} rounded-full transition-colors`} title="Modo Color">
                  <Icon name={darkMode ? "sun" : "moon"} size={20} />
                </button>
                <button onClick={fetchData} className={`p-2.5 ${darkMode ? 'hover:bg-[#919EAB]/10 text-[#919EAB] hover:text-white' : 'hover:bg-[#919EAB]/10 text-[#637381] hover:text-[#212B36]'} rounded-full transition-colors`} title="Actualizar">
                  <Icon name="refresh" size={20} />
                </button>

                {userProfile?.avatar_url && (
                  <div className={`w-9 h-9 ml-1 rounded-full border overflow-hidden ${darkMode ? 'border-[#919EAB]/20 bg-[#212B36]' : 'border-[#919EAB]/30 bg-[#F4F6F8]'}`}>
                    <img src={userProfile.avatar_url} className="w-full h-full object-cover" alt="Avatar" />
                  </div>
                )}

                <button onClick={() => {
                  setIsLoggingOut(true);
                  setTimeout(() => {
                    supabase.auth.signOut();
                    setView('login');
                    setIsLoggingOut(false);
                  }, 2000);
                }} className={`p-2.5 ml-1 rounded-full transition-colors ${darkMode ? 'text-[#FF5630] hover:bg-[#FF5630]/10' : 'text-[#FF5630] hover:bg-[#FF5630]/10'}`} title="Cerrar Sesión">
                  <Icon name="logout" size={20} />
                </button>
              </div>
            </div>
          </header>

          <main className="max-w-6xl mx-auto p-4 md:p-8">

            {(userProfile.role === 'parent' || userProfile.role === 'teacher') && (
              <div className="grid lg:grid-cols-[360px_1fr] gap-6 lg:gap-8 mb-8 items-start">
                <div className="w-full">
                  {!showParentForm ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-fit">
                      <div className={`p-8 rounded-2xl cursor-pointer transition-colors duration-300 group ${darkMode ? 'bg-[#212B36] hover:bg-[#212B36]/80 shadow-[0_0_2px_0_rgba(0,0,0,0.2),_0_12px_24px_-4px_rgba(0,0,0,0.12)]' : 'bg-white hover:bg-slate-50 shadow-[0_0_2px_0_rgba(145,158,171,0.2),_0_12px_24px_-4px_rgba(145,158,171,0.12)]'}`} onClick={() => setShowParentForm(true)}>
                        <div className="flex flex-col items-center justify-center gap-5">
                          <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${darkMode ? 'bg-[#00A76F]/10 text-[#5BE49B]' : 'bg-[#00A76F]/10 text-[#00A76F]'}`}>
                            <Icon name="plus" size={32} />
                          </div>
                          <div className="text-center">
                            <h3 className={`font-bold text-lg mb-1 ${darkMode ? 'text-white' : 'text-[#212B36]'}`}>Nueva Excusa</h3>
                            <p className={`text-sm ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`}>{userProfile.role === 'teacher' ? 'Reportar Novedad Docente' : 'Reportar Inasistencia'}</p>
                          </div>
                        </div>
                      </div>

                      {userProfile.role === 'teacher' && (
                        <div className={`p-8 rounded-2xl cursor-pointer transition-colors duration-300 group ${darkMode ? 'bg-[#212B36] hover:bg-[#212B36]/80 shadow-[0_0_2px_0_rgba(0,0,0,0.2),_0_12px_24px_-4px_rgba(0,0,0,0.12)]' : 'bg-white hover:bg-slate-50 shadow-[0_0_2px_0_rgba(145,158,171,0.2),_0_12px_24px_-4px_rgba(145,158,171,0.12)]'}`} onClick={() => setShowNursingForm(true)}>
                          <div className="flex flex-col items-center justify-center gap-5">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${darkMode ? 'bg-[#FF5630]/10 text-[#FFAC82]' : 'bg-[#FF5630]/10 text-[#FF5630]'}`}>
                              <Icon name="heart" size={32} />
                            </div>
                            <div className="text-center">
                              <h3 className={`font-bold text-lg mb-1 ${darkMode ? 'text-white' : 'text-[#212B36]'}`}>Permiso Enfermería</h3>
                              <p className={`text-sm ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`}>Solicitar permiso médico</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Botón de Cambio de Periodo (Explícito) */}
                      {periodsConfig.length > 1 && (
                        <div className={`col-span-full p-5 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 mt-2 ${darkMode ? 'bg-[#161C24] border border-[#919EAB]/20' : 'bg-[#F4F6F8] border border-[#919EAB]/20'}`}>
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${darkMode ? 'bg-[#FFAB00]/10 text-[#FFD666]' : 'bg-[#FFAB00]/10 text-[#B76E00]'}`}>
                              <Icon name="clock" size={24} />
                            </div>
                            <div>
                              <h4 className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-[#212B36]'}`}>Periodo Académico</h4>
                              <p className={`text-xs ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`}>Estás viendo datos de: <span className={darkMode ? 'text-[#FFD666] font-semibold' : 'text-[#B76E00] font-semibold'}>Periodo {currentViewPeriod}</span></p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {periodsConfig.map(p => (
                              <button
                                key={p.id}
                                onClick={() => {
                                  setCurrentViewPeriod(p.id);
                                  notify(`Cambiado al Periodo ${p.id}`, "success");
                                }}
                                className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors duration-200 ${currentViewPeriod === p.id
                                  ? (darkMode ? 'bg-[#FFAB00]/20 text-[#FFD666]' : 'bg-[#FFAB00]/20 text-[#B76E00]')
                                  : (darkMode ? 'text-[#919EAB] hover:bg-[#919EAB]/10' : 'text-[#637381] hover:bg-[#919EAB]/10')}`}
                              >
                                Periodo {p.id}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`p-8 md:p-10 rounded-2xl relative group ${darkMode ? 'bg-[#212B36] shadow-[0_0_2px_0_rgba(0,0,0,0.2),_0_12px_24px_-4px_rgba(0,0,0,0.12)]' : 'bg-white shadow-[0_0_2px_0_rgba(145,158,171,0.2),_0_12px_24px_-4px_rgba(145,158,171,0.12)]'}`}>
                      <div className="flex justify-between items-center mb-8">
                        <h3 className={`font-bold text-xl flex items-center gap-3 ${darkMode ? 'text-white' : 'text-[#212B36]'}`}>
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${darkMode ? 'bg-[#FF5630]/10 text-[#FFAC82]' : 'bg-[#FF5630]/10 text-[#FF5630]'}`}>
                            <Icon name="file" size={20} />
                          </div>
                          ENVIAR REPORTE
                        </h3>
                        <button onClick={() => setShowParentForm(false)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${darkMode ? 'text-[#919EAB] hover:bg-[#919EAB]/10' : 'text-[#637381] hover:bg-[#919EAB]/10'}`}>CANCELAR</button>
                      </div>

                      <form onSubmit={handleSubmitExcuse} className="space-y-6">
                        {/* ALERTA: Selección de tipo obligatoria */}
                        {userProfile.role === 'parent' && (
                          <div className={`p-4 rounded-xl flex items-start gap-4 ${excuseType === 'absence' ? (darkMode ? 'bg-[#FFAB00]/10 text-[#FFD666]' : 'bg-[#FFAB00]/20 text-[#B76E00]') : (darkMode ? 'bg-[#22C55E]/10 text-[#77ED8B]' : 'bg-[#22C55E]/20 text-[#118D57]')}`}>
                            <div className="mt-0.5 shrink-0">
                              <Icon name={excuseType === 'absence' ? 'alert' : 'check'} size={20} />
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold">
                                {excuseType === 'absence' ? 'Paso 1: Selecciona el tipo de excusa' : 'Tipo seleccionado correctamente'}
                              </h4>
                              <p className="text-xs mt-1 opacity-80">
                                {excuseType === 'absence' ? 'Elige en el menú de abajo: Inasistencia, Médica, Salida, etc.' : `Tipo actual: ${excuseType === 'medical' ? 'Incapacidad Médica' : excuseType === 'medical_appointment' ? 'Cita Médica' : excuseType === 'early_exit' ? 'Salida Durante Jornada' : excuseType === 'tardiness' ? 'Tardanza' : excuseType === 'uniform' ? 'Uniforme' : 'Inasistencia / Ausencia'}`}
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="relative group/input">
                          <Icon name="filter" size={20} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`} />
                          <select name="type" className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-sm transition-colors outline-none appearance-none cursor-pointer ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'} ${(excuseType === 'absence' && userProfile.role === 'parent') ? 'border-[#FFAB00]' : ''}`} onChange={(e) => setExcuseType(e.target.value)}>
                            <option value="absence">Inasistencia/Ausencia</option>
                            <option value="medical">Incapacidad Médica</option>
                            <option value="medical_appointment">Cita Médica</option>
                            <option value="early_exit">Salida Jornada</option>
                            <option value="tardiness">Tardanza</option>
                            {userProfile.role === 'parent' && <option value="uniform">Uniforme</option>}
                          </select>
                        </div>

                        {userProfile.role === 'parent' && userProfile.student_represented?.includes('|') ? (
                          <div className="relative group/input">
                            <Icon name="user" size={20} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`} />
                            <select
                              name="studentName"
                              className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-sm transition-colors outline-none appearance-none cursor-pointer ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'}`}
                              onChange={(e) => setSelectedStudentIndex(e.target.selectedIndex)}
                              required
                            >
                              {userProfile.student_represented.split(/\s*\|\s*/).map((s, i) => (
                                <option key={i} value={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="relative">
                            <Icon name="user" size={20} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`} />
                            <input name="studentName" defaultValue={userProfile.role === 'teacher' ? userProfile.name : userProfile.student_represented} className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-sm transition-colors outline-none ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-[#919EAB]' : 'bg-[#F4F6F8] border-[#919EAB]/30 text-[#637381]'}`} readOnly={userProfile.role === 'teacher'} required />
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="w-full sm:w-1/2 relative">
                            <Icon name="book" size={20} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`} />
                            <select name="grade" className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-sm transition-colors outline-none appearance-none ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-[#919EAB]' : 'bg-[#F4F6F8] border-[#919EAB]/30 text-[#637381]'}`}>
                              <option>
                                {userProfile.role === 'parent' && userProfile.assigned_grade?.includes('|')
                                  ? userProfile.assigned_grade.split(/\s*\|\s*/)[selectedStudentIndex]
                                  : userProfile.assigned_grade}
                              </option>
                            </select>
                          </div>
                          {excuseType !== 'medical_appointment' && (
                            <div className="w-full sm:w-1/2 relative group/input">
                              <Icon name="clock" size={20} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`} />
                              <input name="days" type="number" min="1" max="30" defaultValue={1} className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-sm transition-colors outline-none ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'}`} required title="Número de días" />
                            </div>
                          )}
                        </div>

                        {excuseType === 'early_exit' && userProfile.role === 'parent' && (
                          <div className="relative group/input">
                            <Icon name="logout" size={20} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`} />
                            <select name="pickup" className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-sm transition-colors outline-none appearance-none ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F]'}`}>
                              <option value="SI">SÍ, yo vengo por él/ella</option>
                              <option value="NO">NO, se va solo/a</option>
                            </select>
                          </div>
                        )}

                        <div className="group/textarea relative">
                          <textarea name="reason" className={`w-full p-4 rounded-xl border text-sm h-32 transition-colors outline-none resize-none ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#00A76F] placeholder:text-[#919EAB]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#00A76F] placeholder:text-[#919EAB]'}`} placeholder="Escribe detalladamente el motivo..." required></textarea>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div className={`p-4 rounded-xl border transition-colors ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 focus-within:border-[#00A76F]' : 'bg-white border-[#919EAB]/30 focus-within:border-[#00A76F]'}`}>
                            <label className={`block text-xs font-semibold mb-2 ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`}>
                              {excuseType === 'medical_appointment' ? 'Fecha de la cita médica' : 'Válida desde'}
                            </label>
                            <input name="valid_from" type="date" className={`w-full bg-transparent border-none text-sm p-0 focus:ring-0 outline-none ${darkMode ? 'text-white' : 'text-[#212B36]'}`} defaultValue={new Date().toISOString().split('T')[0]} required />
                          </div>
                        </div>

                        <div className={`p-5 rounded-xl border ${darkMode ? 'bg-[#FFAB00]/10 border-[#FFAB00]/20' : 'bg-[#FFAB00]/10 border-[#FFAB00]/20'}`}>
                          <p className={`text-xs font-semibold mb-3 text-center ${darkMode ? 'text-[#FFD666]' : 'text-[#B76E00]'}`}>Validación de Seguridad</p>
                          <div className="flex gap-3">
                            <input name="verificationCode" className={`w-1/2 p-3 rounded-xl border text-center text-sm font-bold tracking-widest outline-none transition-colors ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#FFAB00]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#FFAB00]'}`} placeholder="CÓDIGO" required />
                            <button type="button" onClick={handleRequestCode} disabled={requestingCode} className={`w-1/2 text-sm font-bold rounded-xl transition-colors ${darkMode ? 'bg-[#FFAB00] hover:bg-[#B76E00] text-[#212B36]' : 'bg-[#FFAB00] hover:bg-[#B76E00] text-[#212B36]'}`}>
                              {requestingCode ? 'ENVIANDO...' : 'OBTENER CÓDIGO'}
                            </button>
                          </div>
                        </div>

                        {excuseType !== 'early_exit' && (
                          <div className="flex flex-col sm:flex-row gap-4">
                            <label className={`flex-1 flex flex-col items-center justify-center p-6 rounded-xl border border-dashed cursor-pointer transition-colors ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 hover:bg-[#919EAB]/5' : 'bg-[#F4F6F8] border-[#919EAB]/30 hover:bg-[#919EAB]/10'}`}>
                              <Icon name="folder" className={`${darkMode ? 'text-[#5BE49B]' : 'text-[#00A76F]'} mb-2`} size={28} />
                              <span className={`text-xs font-semibold ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`}>{userProfile.role === 'teacher' ? 'Adjuntar archivo' : 'Subir documento'}</span>
                              <input type="file" accept="image/*,application/pdf" onChange={(e) => setSelectedFile(e.target.files[0])} className="hidden" />
                            </label>
                            <label className={`flex-1 flex flex-col items-center justify-center p-6 rounded-xl border border-dashed cursor-pointer transition-colors ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 hover:bg-[#919EAB]/5' : 'bg-[#F4F6F8] border-[#919EAB]/30 hover:bg-[#919EAB]/10'}`}>
                              <Icon name="camera" className={`${darkMode ? 'text-[#FFAC82]' : 'text-[#FF5630]'} mb-2`} size={28} />
                              <span className={`text-xs font-semibold ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`}>{userProfile.role === 'teacher' ? 'Tomar foto' : 'Tomar fotografía'}</span>
                              <input type="file" capture="environment" onChange={(e) => setSelectedFile(e.target.files[0])} className="hidden" />
                            </label>
                          </div>
                        )}
                        {selectedFile && <div className={`text-xs text-center font-semibold p-3 rounded-xl ${darkMode ? 'bg-[#00A76F]/10 text-[#5BE49B]' : 'bg-[#00A76F]/20 text-[#007867]'}`}>📎 {selectedFile.name}</div>}
                        
                        <button disabled={uploading} className="w-full py-4 text-white font-bold rounded-xl text-sm transition-colors duration-200 bg-[#212B36] hover:bg-[#454F5B] hover:shadow-[0_8px_16px_0_rgba(33,43,54,0.24)]">
                          {uploading ? (
                            submissionStep === 'ai' ? 'Analizando...' :
                              submissionStep === 'upload' ? 'Subiendo...' :
                                submissionStep === 'save' ? 'Guardando...' :
                                  submissionStep === 'done' ? 'Enviado' : 'Procesando...'
                          ) : 'Enviar Reporte'}
                        </button>
                      </form>
                    </div>
                  )}

                  {showNursingForm && (
                    <div className={`p-8 md:p-10 rounded-2xl relative group mt-6 ${darkMode ? 'bg-[#212B36] shadow-[0_0_2px_0_rgba(0,0,0,0.2),_0_12px_24px_-4px_rgba(0,0,0,0.12)]' : 'bg-white shadow-[0_0_2px_0_rgba(145,158,171,0.2),_0_12px_24px_-4px_rgba(145,158,171,0.12)]'}`}>
                      <div className="flex justify-between items-center mb-8">
                        <h3 className={`font-bold text-xl flex items-center gap-3 ${darkMode ? 'text-white' : 'text-[#212B36]'}`}>
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${darkMode ? 'bg-[#FF5630]/10 text-[#FFAC82]' : 'bg-[#FF5630]/10 text-[#FF5630]'}`}>
                            <Icon name="heart" size={20} />
                          </div>
                          SOLICITAR ENFERMERÍA
                        </h3>
                        <button onClick={() => setShowNursingForm(false)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${darkMode ? 'text-[#919EAB] hover:bg-[#919EAB]/10' : 'text-[#637381] hover:bg-[#919EAB]/10'}`}>CANCELAR</button>
                      </div>

                      <form onSubmit={handleRequestNursingPermission} className="space-y-6">
                        <div className="relative group/input">
                          <Icon name="user" size={20} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`} />
                          <input name="studentName" list="student-list" className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-sm transition-colors outline-none ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#FF5630]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#FF5630]'}`} placeholder="Nombre del Estudiante" required />
                        </div>

                        <div className="relative group/input">
                          <Icon name="book" size={20} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${darkMode ? 'text-[#919EAB]' : 'text-[#637381]'}`} />
                          <select name="grade" className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-sm transition-colors outline-none appearance-none cursor-pointer ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#FF5630]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#FF5630]'}`} required>
                            <option value="">Curso</option>
                            {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>

                        <div className="group/textarea relative">
                          <textarea name="reason" className={`w-full p-4 rounded-xl border text-sm h-32 transition-colors outline-none resize-none ${darkMode ? 'bg-[#161C24] border-[#919EAB]/20 text-white focus:border-[#FF5630] placeholder:text-[#919EAB]' : 'bg-white border-[#919EAB]/30 text-[#212B36] focus:border-[#FF5630] placeholder:text-[#919EAB]'}`} placeholder="Explica brevemente el síntoma o motivo..." required></textarea>
                        </div>

                        <button disabled={uploading} className="w-full py-4 text-white font-bold rounded-xl text-sm transition-colors duration-200 bg-[#FF5630] hover:bg-[#B71D18] hover:shadow-[0_8px_16px_0_rgba(255,86,48,0.24)]">
                          {uploading ? 'Procesando...' : 'Enviar Solicitud'}
                        </button>
                      </form>
                    </div>
                  )}
                </div>

                {userProfile.role === 'parent' && (
                  <div className="space-y-4">
                      <div className="flex gap-2 mb-6 border-b pb-2 overflow-x-auto custom-scrollbar">
                        <button
                          onClick={() => setParentTab('all')}
                          className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 ${parentTab === 'all' ? (darkMode ? 'border-[#00A76F] text-[#00A76F]' : 'border-[#00A76F] text-[#00A76F]') : 'border-transparent ' + (darkMode ? 'text-[#919EAB] hover:text-white' : 'text-[#637381] hover:text-[#212B36]')}`}
                        >
                          TODO 📑
                        </button>
                        <button
                          onClick={() => setParentTab('excuses')}
                          className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 ${parentTab === 'excuses' ? (darkMode ? 'border-[#00A76F] text-[#00A76F]' : 'border-[#00A76F] text-[#00A76F]') : 'border-transparent ' + (darkMode ? 'text-[#919EAB] hover:text-white' : 'text-[#637381] hover:text-[#212B36]')}`}
                        >
                          EXCUSAS 📋
                        </button>
                        <button
                          onClick={() => setParentTab('tardies')}
                          className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 ${parentTab === 'tardies' ? (darkMode ? 'border-[#FFAB00] text-[#FFAB00]' : 'border-[#FFAB00] text-[#B76E00]') : 'border-transparent ' + (darkMode ? 'text-[#919EAB] hover:text-white' : 'text-[#637381] hover:text-[#212B36]')}`}
                        >
                          TARDANZAS ⏰
                        </button>
                        <button
                          onClick={() => setParentTab('infirmary')}
                          className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 ${parentTab === 'infirmary' ? (darkMode ? 'border-[#FF5630] text-[#FF5630]' : 'border-[#FF5630] text-[#FF5630]') : 'border-transparent ' + (darkMode ? 'text-[#919EAB] hover:text-white' : 'text-[#637381] hover:text-[#212B36]')}`}
                        >
                          ENFERMERÍA 🩺
                        </button>
                      </div>

                    {renderParentFeed()}
                  </div>
                )}

                {userProfile.role === 'teacher' && (
                  <div className="space-y-6">
                    {/* SECCIÓN DE LLAMADOS DE ATENCIÓN */}
                    {(() => {
                      const myCalls = teacherAttentionCalls.filter(c => c.teacher_name === userProfile.name);
                      if (myCalls.length === 0) return null;
                      const latestCall = myCalls[0];
                      const olderCalls = myCalls.slice(1);
                      return (
                        <div className="space-y-4 mb-4">
                          <h3 className={`text-xs font-black ${darkMode ? 'text-indigo-400' : 'text-indigo-500'} uppercase flex items-center gap-2 mb-4`}><Icon name="alert" size={16} /> Llamados y Felicitaciones</h3>
                          {/* Último Llamado o Felicitación */}
                          <div className={`${darkMode ? (latestCall.type === 'attention' ? 'bg-red-900/30 border-red-500/50 text-red-100' : 'bg-emerald-900/30 border-emerald-500/50 text-emerald-100') : (latestCall.type === 'attention' ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200')} p-6 rounded-[2rem] border-2 flex flex-col xl:flex-row items-start xl:items-center justify-between shadow-lg transition-colors gap-4`}>
                            <div className="flex items-center gap-4">
                              <div className={`shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center ${latestCall.type === 'attention' ? 'bg-red-500 text-white shadow-red-500/30' : 'bg-emerald-500 text-white shadow-emerald-500/30'} shadow-lg animate-bounce-short`}>
                                <Icon name={latestCall.type === 'attention' ? 'alert' : 'check'} size={24} />
                              </div>
                              <div>
                                <h3 className={`font-black uppercase tracking-tight text-lg leading-tight ${latestCall.type === 'attention' ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                  {latestCall.type === 'attention' ? 'Llamado de Atención' : '¡Felicitaciones! 🎉'}
                                </h3>
                                <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'} font-bold mt-1 max-w-lg`}>{latestCall.observation}</p>
                              </div>
                            </div>
                            <div className="text-left xl:text-right shrink-0">
                              <span className={`text-[10px] font-black uppercase tracking-widest ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                {new Date(latestCall.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>

                          {/* Historial Antiguo */}
                          {olderCalls.length > 0 && (
                            <div className="space-y-3 mt-4">
                              <h4 className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'} ml-2`}><Icon name="clock" size={12} /> Historial de Evaluaciones</h4>
                              <div className="grid grid-cols-1 gap-2">
                                {olderCalls.map(call => (
                                  <div key={call.id} className={`${darkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-white border-slate-200'} p-3 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center justify-between shadow-sm opacity-80 hover:opacity-100 transition-opacity gap-3`}>
                                    <div className="flex items-center gap-3">
                                      <div className={`shrink-0 w-8 h-8 rounded-xl flex items-center justify-center ${call.type === 'attention' ? 'bg-red-500/10 text-red-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                                        <Icon name={call.type === 'attention' ? 'alert' : 'check'} size={16} />
                                      </div>
                                      <div>
                                        <h4 className={`font-black text-xs uppercase tracking-tight ${call.type === 'attention' ? (darkMode ? 'text-red-400' : 'text-red-700') : (darkMode ? 'text-emerald-400' : 'text-emerald-700')}`}>
                                          {call.type === 'attention' ? 'Llamado Atención' : 'Felicitación'}
                                        </h4>
                                        <p className={`text-[10px] ${darkMode ? 'text-slate-400' : 'text-slate-500'} font-bold line-clamp-1`}>{call.observation}</p>
                                      </div>
                                    </div>
                                    <span className={`text-[9px] font-black uppercase tracking-widest shrink-0 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                      {new Date(call.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* SECCIÓN DE ENFERMERÍA */}
                    <div className="space-y-4 mb-4">
                      <h3 className={`text-xs font-black ${darkMode ? 'text-rose-400' : 'text-rose-500'} uppercase flex items-center gap-2 mb-4`}><Icon name="heart" size={16} /> Mis Solicitudes de Enfermería</h3>
                      {memoizedData.filteredNursingPermissions.filter(p => p.teacher_id === user.id && !p.hidden_by_teacher).length === 0 ? (
                        <div className={`p-10 rounded-[2rem] border-2 border-dashed ${darkMode ? 'bg-slate-900 border-slate-800 text-slate-500' : 'bg-white border-slate-100 text-slate-300'} text-center text-xs font-bold italic`}>No has enviado solicitudes de enfermería recientes.</div>
                      ) : (
                        memoizedData.filteredNursingPermissions.filter(p => p.teacher_id === user.id && !p.hidden_by_teacher).map(p => (
                          <div key={p.id} className={`${darkMode ? 'glass-card border-none shadow-2xl overflow-hidden group' : 'glass-card-light border-slate-100 shadow-sm'} p-6 rounded-[2rem] transition-colors relative mb-4`}>
                            <div className={`absolute top-0 left-0 w-1 h-full ${p.status === 'accepted' ? 'bg-green-500' : p.status === 'denied' ? 'bg-red-500' : 'bg-amber-400'}`} />
                            <div className="flex justify-between items-start relative z-10">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-3">
                                  <h4 className={`font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase text-base tracking-tight`}>
                                    {p.student_name}
                                  </h4>
                                  <span className={`${darkMode ? 'bg-white/5 text-slate-400 border-white/10' : 'bg-slate-100 text-slate-500 border-slate-200'} border text-[9px] font-black px-2 py-0.5 rounded-lg uppercase tracking-widest`}>
                                    {p.grade}
                                  </span>
                                </div>
                                <div className={`p-4 rounded-2xl border ${darkMode ? 'bg-slate-950/40 border-white/5 text-slate-300' : 'bg-slate-50 border-slate-100 text-slate-600 shadow-inner'}`}>
                                  <p className="text-xs font-medium italic leading-relaxed">"{p.reason}"</p>
                                </div>
                                <div className="flex items-center justify-between mt-4">
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-1.5">
                                      <Icon name="clock" size={12} className="text-slate-500" />
                                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-tighter">{new Date(p.created_at).toLocaleString()}</span>
                                    </div>
                                    <span className={`text-[10px] font-black uppercase px-3 py-1 rounded-full ${p.status === 'accepted' ? (darkMode ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-green-50 text-green-700 border border-green-100') : p.status === 'denied' ? (darkMode ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-700 border border-red-100') : (darkMode ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-amber-50 text-amber-700 border border-amber-100')}`}>
                                      {p.status === 'accepted' ? 'ACEPTADO ✅' : p.status === 'denied' ? 'RECHAZADO ❌' : 'PENDIENTE ⏳'}
                                    </span>
                                  </div>
                                  <button onClick={async () => {
                                    if (window.confirm('¿Estás seguro de eliminar este reporte de enfermería de TU vista? (El personal de enfermería aún podrá verlo)')) {
                                      const { error } = await supabase.from('nursing_permissions').update({ hidden_by_teacher: true }).eq('id', p.id);
                                      if (error) {
                                        notify('Error al ocultar: ' + error.message, 'error');
                                        return;
                                      }
                                      fetchData();
                                      notify('Reporte ocultado exitosamente', 'success');
                                    }
                                  }} className={`p-2 rounded-xl transition-colors shadow-sm ${darkMode ? 'bg-red-900/20 text-red-400 hover:bg-red-900/40 border border-red-900/50' : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-100'}`} title="Ocultar Reporte">
                                    <Icon name="trash" size={14} />
                                  </button>
                                </div>
                                {p.nurse_response && (
                                  <div className={`mt-4 p-4 rounded-2xl border-2 animate-in slide-in-from-top-2 duration-500 ${p.status === 'accepted' ? (darkMode ? 'bg-green-500/5 border-green-500/20 text-green-400' : 'bg-green-50 border-green-200 text-green-800') : (darkMode ? 'bg-red-500/5 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-800')}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <Icon name="heart" size={12} />
                                      <p className="text-[9px] font-black uppercase tracking-[0.2em]">Respuesta Médica</p>
                                    </div>
                                    <p className="text-xs font-black">{p.nurse_response}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="space-y-4 mb-8">
                      <h3 className={`text-xs font-black ${darkMode ? 'text-teal-400' : 'text-teal-500'} uppercase flex items-center gap-2 mb-4`}><Icon name="file" size={16} /> Mis Novedades Reportadas</h3>
                      {teacherNovelties.length === 0 ? (
                        <div className={`p-10 rounded-[2rem] border-2 border-dashed ${darkMode ? 'bg-slate-900 border-slate-800 text-slate-500' : 'bg-white border-slate-100 text-slate-300'} text-center text-xs font-bold italic`}>No has reportado novedades.</div>
                      ) : (
                        <div className="grid grid-cols-1 gap-4 opacity-75 hover:opacity-100 transition-opacity">
                          {teacherNovelties.map(nov => (
                            <ExcuseCard
                              key={nov.id}
                              ex={nov}
                              isAdmin={false}
                              darkMode={darkMode}
                              userProfile={userProfile}
                              setSelectedExcuse={() => { }}
                              setRejectId={() => { }}
                              handleUpdateStatus={() => { }}
                              handleDeleteExcuse={async (id) => {
                                if (window.confirm('¿Estás seguro de que deseas eliminar esta novedad?')) {
                                  await supabase.from('excuses').delete().eq('id', id);
                                  fetchData();
                                  notify('Novedad eliminada', 'success');
                                }
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(userProfile.role === 'admin' || userProfile.role === 'teacher') && (
              <div className="space-y-6">

                {userProfile.role === 'teacher' && (
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex gap-4 overflow-x-auto border-b custom-scrollbar">
                      {['excuses', 'tardies', 'infirmary', 'capellania'].map(tab => (
                        <button key={tab} onClick={() => setAdminTab(tab)} className={`px-4 py-3 text-sm font-semibold capitalize whitespace-nowrap transition-colors border-b-2 ${adminTab === tab ? (darkMode ? 'border-[#00A76F] text-[#00A76F]' : 'border-[#00A76F] text-[#00A76F]') : 'border-transparent ' + (darkMode ? 'text-[#919EAB] hover:text-white' : 'text-[#637381] hover:text-[#212B36]')}`}>
                          {tab === 'excuses' ? 'Excusas 📋' : tab === 'tardies' ? 'Tardanzas ⏰' : tab === 'infirmary' ? 'Enfermería 🩺' : 'Capellanía 🕊️'}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-4 items-center mb-4">
                      <div className={`flex gap-2 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'} p-1 rounded-xl shadow-sm border`}>
                        <button
                          onClick={() => { setTeacherFilter('my_grade'); fetchData(); }}
                          className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-colors ${teacherFilter === 'my_grade' ? (darkMode ? 'bg-white text-navy shadow-md' : 'bg-navy text-white shadow-md') : (darkMode ? 'text-slate-500 hover:bg-slate-800' : 'text-slate-400 hover:bg-slate-50')}`}
                        >
                          MI CURSO ({userProfile.assigned_grade})
                        </button>
                        <button
                          onClick={() => { setTeacherFilter('all'); fetchData(); }}
                          className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-colors ${teacherFilter === 'all' ? (darkMode ? 'bg-white text-navy shadow-md' : 'bg-navy text-white shadow-md') : (darkMode ? 'text-slate-500 hover:bg-slate-800' : 'text-slate-400 hover:bg-slate-50')}`}
                        >
                          TODO EL COLEGIO
                        </button>
                      </div>

                      {teacherFilter === 'all' && (
                        <div className={`flex items-center gap-4 ${darkMode ? 'glass-morphism' : 'glass-morphism-light border-slate-200'} p-4 rounded-2xl shadow-lg border transform transition-colors duration-500 hover:scale-105`}>
                          <div className={`w-8 h-8 ${darkMode ? 'bg-teal-500/20 text-teal-400Shadow' : 'bg-teal-500/10 text-teal-600'} rounded-lg flex items-center justify-center border border-white/10`}>
                            <Icon name="filter" size={16} />
                          </div>
                          <div className="relative">
                            <select
                              value={teacherGradeFilter}
                              onChange={(e) => { setTeacherGradeFilter(e.target.value); fetchData(); }}
                              className={`appearance-none text-[10px] font-black pl-4 pr-10 py-2 rounded-xl border-2 transition-colors duration-500 outline-none cursor-pointer ${darkMode ? 'bg-slate-950/40 border-white/10 text-white focus:border-teal-500 shadow-inner' : 'bg-white/60 border-white/80 text-navy focus:border-teal-500'} uppercase tracking-widest hover:bg-white/5`}
                            >
                              <option value="all">TODOS</option>
                              {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-500 pointer-events-none group-hover:translate-x-1 transition-transform"><Icon name="arrowRight" size={12} className="rotate-90" /></span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {userProfile.role === 'admin' && (
                  <AdminAlertsSection
                    memoizedData={memoizedData}
                    localHiddenAlerts={localHiddenAlerts}
                    darkMode={darkMode}
                    parentProfiles={parentProfiles}
                    setAdminTab={setAdminTab}
                    setPendingReview={setPendingReview}
                    notify={notify}
                    setSelectedFaltaLeve={setSelectedFaltaLeve}
                    setInvestigatingName={setInvestigatingName}
                  />
                )}

                {userProfile.role === 'admin' && (
                  <div className="flex gap-4 overflow-x-auto border-b custom-scrollbar mb-6">
                    {[
                      { id: 'excuses', label: 'Excusas 📋' },
                      { id: 'tardies', label: 'Tardanzas ⏰' },
                      { id: 'infirmary', label: 'Enfermería 🩺' },
                      { id: 'capellania', label: 'Capellanía 🕊️' },
                      { id: 'ai_assistant', label: 'IA Coordinación 🤖' },
                      { id: 'students', label: 'Estudiantes 👨‍🎓' },
                      { id: 'register', label: 'Registro 📝' },
                      { id: 'teachers', label: 'Docentes 👨‍🏫' },
                      { id: 'settings', label: 'Ajustes ⚙️' },
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => { setAdminTab(tab.id); if (tab.id === 'students') fetchParentProfiles(); }}
                        className={`px-4 py-3 text-sm font-semibold capitalize whitespace-nowrap transition-colors border-b-2 ${adminTab === tab.id ? (darkMode ? 'border-[#00A76F] text-[#00A76F]' : 'border-[#00A76F] text-[#00A76F]') : 'border-transparent ' + (darkMode ? 'text-[#919EAB] hover:text-white' : 'text-[#637381] hover:text-[#212B36]')}`}
                      >{tab.label}</button>
                    ))}
                  </div>
                )}

                {(userProfile.role === 'teacher' || (userProfile.role === 'admin' && adminTab === 'excuses')) && adminTab === 'excuses' && (
                  <div className="flex flex-col gap-8 w-full min-w-0">
                    {userProfile.role === 'admin' && (
                      <div className="flex gap-4 overflow-x-auto border-b custom-scrollbar mb-4">
                        {[
                          { id: 'all', label: 'Todos' },
                          { id: 'absence', label: 'Ausencia' },
                          { id: 'medical', label: 'Médica' },
                          { id: 'early_exit', label: 'Salida' },
                          { id: 'uniform', label: 'Uniforme' },
                        ].map(f => (
                          <button
                            key={f.id}
                            onClick={() => setAdminExcuseTypeFilter(f.id)}
                            className={`px-4 py-3 text-sm font-semibold capitalize whitespace-nowrap transition-colors border-b-2 ${adminExcuseTypeFilter === f.id ? (darkMode ? 'border-[#00A76F] text-[#00A76F]' : 'border-[#00A76F] text-[#00A76F]') : 'border-transparent ' + (darkMode ? 'text-[#919EAB] hover:text-white' : 'text-[#637381] hover:text-[#212B36]')}`}
                          >
                            {f.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {userProfile.role === 'admin' && (
                      <div className={`${darkMode ? 'glass-morphism' : 'glass-morphism-light border-slate-200'} p-6 rounded-[2.5rem] shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6 mb-8 transform transition-colors duration-500 hover:scale-[1.01]`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 ${darkMode ? 'bg-navy/40 text-gold shadow-[0_0_20px_rgba(212,175,55,0.2)]' : 'bg-navy/10 text-navy'} rounded-2xl flex items-center justify-center border border-white/10`}>
                            <Icon name="users" size={24} />
                          </div>
                          <div>
                            <span className={`text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase tracking-[0.2em]`}>Filtrado por</span>
                            <h3 className={`text-sm font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase tracking-tight leading-none`}>Grado de Estudiante</h3>
                          </div>
                        </div>
                        <div className="relative w-full sm:w-auto">
                          <select
                            value={adminExcuseGradeFilter}
                            onChange={(e) => setAdminExcuseGradeFilter(e.target.value)}
                            className={`w-full sm:w-72 appearance-none text-[12px] font-black px-8 py-4 rounded-[1.5rem] border-2 transition-colors duration-500 outline-none cursor-pointer ${darkMode ? 'bg-slate-950/40 border-white/10 text-white focus:border-gold shadow-[0_10px_30px_rgba(0,0,0,0.3)]' : 'bg-white/60 border-white/80 text-navy focus:border-navy shadow-lg text-shadow-sm'} uppercase tracking-wider hover:bg-white/5`}
                          >
                            <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" value="all">TODOS LOS GRADOS</option>
                            {GRADES.map(g => <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" key={g} value={g}>{g}</option>)}
                          </select>
                          <span className="absolute right-6 top-1/2 -translate-y-1/2 text-gold pointer-events-none group-hover:translate-x-1 transition-transform"><Icon name="arrowRight" size={14} className="rotate-90" /></span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mt-4 sm:mt-0">
                          <button onClick={handleDownloadGeneralReport} className={`px-6 py-4 rounded-[1.5rem] text-[12px] font-black shadow-xl transition-colors flex items-center justify-center gap-2 uppercase tracking-wider ${darkMode ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30' : 'bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200'}`}>
                            <Icon name="file" size={16} /> REPORTE GENERAL 📊
                          </button>
                          <button onClick={handleDownloadMedicalExcusesExcel} className="px-6 py-4 bg-rose-600 text-white rounded-[1.5rem] text-[12px] font-black hover:bg-rose-700 shadow-xl transition-colors flex items-center justify-center gap-2 uppercase tracking-wider">
                            <Icon name="download" size={16} /> REPORTES (XLS) 📈
                          </button>
                        </div>
                      </div>
                    )}

                    {userProfile.role === 'admin' && (
                      <div>
                        <h3 className={`text-xs font-black ${darkMode ? 'text-amber-500' : 'text-amber-600'} uppercase mb-4 flex items-center gap-2`}><Icon name="clock" size={16} /> Solicitudes Pendientes</h3>
                        {memoizedData.filteredExcuses.filter(e => e.status === 'pending_review' && !e.is_teacher_report && !(e.contact_info?.includes(e.student_name)) && (adminExcuseTypeFilter === 'all' || e.type === adminExcuseTypeFilter) && (adminExcuseGradeFilter === 'all' || e.grade === adminExcuseGradeFilter)).length === 0 ? (
                          <div className={`${darkMode ? 'bg-slate-900 border-slate-800 text-slate-600' : 'bg-white border-slate-200 text-slate-400'} p-8 rounded-3xl border-2 border-dashed text-center font-bold`}>✨ Todo al día. No hay solicitudes pendientes.</div>
                        ) : (
                          memoizedData.filteredExcuses.filter(e => e.status === 'pending_review' && !e.is_teacher_report && !(e.contact_info?.includes(e.student_name)) && (adminExcuseTypeFilter === 'all' || e.type === adminExcuseTypeFilter) && (adminExcuseGradeFilter === 'all' || e.grade === adminExcuseGradeFilter)).map(ex => {
                            const norm = normalName(ex.student_name);
                            const alertData = memoizedData.studentAlerts[norm];
                            const isAlert = alertData ? (alertData.counts.absence >= 3 || alertData.counts.uniform >= 3 || alertData.counts.tardy >= 3 || alertData.counts.infirmary >= 3) : false;
                            return (
                              <div key={ex.id}>
                                {/* Admin: Control de tipo de excusa */}
                                <div className={`${darkMode ? 'bg-indigo-900/20 border-indigo-800/50' : 'bg-indigo-50 border-indigo-200'} px-4 py-2 rounded-t-2xl border border-b-0 flex items-center gap-3`}>
                                  <Icon name="edit" size={12} className={darkMode ? 'text-indigo-400' : 'text-indigo-600'} />
                                  <span className={`text-[9px] font-black uppercase tracking-widest ${darkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>Admin: Cambiar tipo de excusa</span>
                                  <select
                                    defaultValue={ex.type}
                                    onClick={e => e.stopPropagation()}
                                    onChange={async (e) => {
                                      e.stopPropagation();
                                      const newType = e.target.value;
                                      await supabase.from('excuses').update({ type: newType }).eq('id', ex.id);
                                      notify(`Tipo cambiado a: ${newType}`, 'success');
                                      fetchData();
                                    }}
                                    className={`ml-auto text-[9px] font-black uppercase px-3 py-1.5 rounded-xl border cursor-pointer outline-none transition-colors ${darkMode ? 'bg-indigo-900/40 border-indigo-700 text-indigo-300 focus:border-indigo-400' : 'bg-white border-indigo-300 text-indigo-700 focus:border-indigo-500'}`}
                                  >
                                    <option value="absence">Inasistencia</option>
                                    <option value="medical">Incapacidad Médica</option>
                                    <option value="medical_appointment">Cita Médica</option>
                                    <option value="early_exit">Salida Jornada</option>
                                    <option value="tardiness">Tardanza</option>
                                    <option value="uniform">Uniforme</option>
                                  </select>
                                </div>
                                <ExcuseCard
                                  ex={ex}
                                  isAdmin={true}
                                  darkMode={darkMode}
                                  userProfile={userProfile}
                                  isAlert={isAlert}
                                  setSelectedExcuse={setSelectedExcuse}
                                  setRejectId={setRejectId}
                                  handleUpdateStatus={handleUpdateStatus}
                                  handleDeleteExcuse={handleDeleteExcuse}
                                />
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}


                    <div>
                      <h3 className="text-xs font-black text-slate-400 uppercase mb-4">{userProfile.role === 'admin' ? 'Historial Procesado' : 'Historial de Excusas'}</h3>
                      {userProfile.role === 'admin' ? (
                        (() => {
                          const historyItems = memoizedData.filteredExcuses.filter(e => e.status !== 'pending_review' && !e.is_teacher_report && !(e.contact_info?.includes(e.student_name)) && (adminExcuseTypeFilter === 'all' || e.type === adminExcuseTypeFilter) && (adminExcuseGradeFilter === 'all' || e.grade === adminExcuseGradeFilter));
                          const visibleHistory = historyItems.slice(0, historyPage);
                          return (<>
                            {visibleHistory.map(ex => (
                              <div key={ex.id} onClick={() => setSelectedExcuse(ex)} className={`${darkMode ? 'glass-card hover:bg-white/5 border-white/5 shadow-2xl' : 'glass-card-light hover:bg-white/80'} p-4 sm:p-6 rounded-[2rem] sm:rounded-[2.5rem] mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 cursor-pointer transform transition-colors duration-150 hover:scale-[1.01] border group`}>
                                <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto min-w-0">
                                  <div className={`shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-[1rem] sm:rounded-2xl flex items-center justify-center border-2 transition-colors ${ex.status === 'approved' ? (darkMode ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-emerald-50 border-emerald-100 text-emerald-600') : (darkMode ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-red-50 border-red-100 text-red-600')}`}>
                                    <Icon name={ex.status === 'approved' ? 'check' : 'x'} size={20} className="sm:hidden" />
                                    <Icon name={ex.status === 'approved' ? 'check' : 'x'} size={24} className="hidden sm:block" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className={`font-black uppercase tracking-tight text-sm sm:text-lg block truncate break-words ${darkMode ? 'text-white' : 'text-navy'}`}>{ex.student_name}</span>
                                    <div className="flex flex-wrap gap-2 sm:gap-3 mt-1 items-center">
                                      <span className={`text-[9px] sm:text-[10px] font-black tracking-widest uppercase ${ex.status === 'approved' ? (darkMode ? 'text-emerald-400' : 'text-emerald-700') : (darkMode ? 'text-red-400' : 'text-red-700')}`}>{ex.status === 'approved' ? 'AUTORIZADO' : 'RECHAZADO'}</span>
                                      <span className="w-1 h-1 rounded-full bg-slate-400 hidden sm:block" />
                                      <span className={`text-[9px] sm:text-[10px] font-black uppercase tracking-widest ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{ex.type}</span>
                                      <span className={`text-[9px] sm:text-[10px] px-2 py-0.5 rounded-lg font-black uppercase tracking-tighter ${darkMode ? 'bg-blue-900/40 text-blue-400 border border-blue-800/40 shadow-[0_0_10px_rgba(59,130,246,0.1)]' : 'bg-blue-50 text-blue-700 border border-blue-100'}`}>{ex.days || 1} DÍA(S)</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex flex-row gap-2 items-center shrink-0 w-full sm:w-auto justify-end mt-2 sm:mt-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setExtendingExcuse(ex); }}
                                    className={`flex-1 sm:flex-none justify-center text-[10px] font-black uppercase tracking-widest ${darkMode ? 'bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-blue-500/20 shadow-lg shadow-blue-500/10' : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'} px-4 py-3 sm:py-3 rounded-xl sm:rounded-2xl border transition-colors active:scale-95 flex items-center gap-1`}
                                  >+ DÍAS</button>

                                  {ex.status === 'approved' && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setRejectId(ex.id) }}
                                      className={`flex-1 sm:flex-none justify-center text-[10px] font-black uppercase tracking-widest ${darkMode ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20 shadow-lg shadow-red-500/10' : 'bg-white text-red-500 border-red-100 hover:bg-red-50'} px-4 py-3 sm:py-3 rounded-xl sm:rounded-2xl border transition-colors active:scale-95 flex items-center gap-2`}
                                    >
                                      REVOCAR <Icon name="logout" size={14} className="rotate-180" />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteExcuse(ex.id) }}
                                    className={`flex-1 sm:flex-none justify-center text-[10px] font-black uppercase tracking-widest ${darkMode ? 'bg-red-900/40 text-red-500 border-red-900 shadow-lg shadow-red-900/10 hover:bg-red-900/60' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'} px-4 py-3 sm:py-3 rounded-xl sm:rounded-2xl border transition-colors active:scale-95 flex items-center gap-2`}
                                    title="Eliminar Permanente"
                                  >
                                    ELIMINAR <Icon name="trash" size={14} />
                                  </button>
                                </div>
                              </div>
                            ))}
                            {historyItems.length > historyPage && (
                              <button onClick={() => setHistoryPage(p => p + 20)} className={`w-full py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-colors mb-4 ${darkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                                CARGAR MÁS ({historyItems.length - historyPage} restantes)
                              </button>
                            )}
                          </>);
                        })()
                      ) : (
                        memoizedData.filteredExcuses.filter(e => adminExcuseTypeFilter === 'all' || e.type === adminExcuseTypeFilter).map(ex => {
                          const norm = normalName(ex.student_name);
                          const alertData = memoizedData.studentAlerts[norm];
                          const isAlert = alertData ? (alertData.counts.absence >= 3 || alertData.counts.uniform >= 3 || alertData.counts.tardy >= 3 || alertData.counts.infirmary >= 3) : false;
                          return (
                            <ExcuseCard
                              key={ex.id}
                              ex={ex}
                              isAdmin={false}
                              darkMode={darkMode}
                              userProfile={userProfile}
                              isAlert={isAlert}
                              setSelectedExcuse={setSelectedExcuse}
                              setRejectId={setRejectId}
                              handleUpdateStatus={handleUpdateStatus}
                              handleDeleteExcuse={handleDeleteExcuse}
                            />
                          );
                        })
                      )}
                      {memoizedData.filteredExcuses.filter(e => (userProfile.role === 'admin' ? (e.status !== 'pending_review' && !(e.contact_info?.includes(e.student_name))) : true) && (adminExcuseTypeFilter === 'all' || e.type === adminExcuseTypeFilter)).length === 0 && <p className="text-center text-slate-400 font-bold py-10">NO HAY REGISTROS</p>}
                    </div>
                  </div>
                )}

                {adminTab === 'tardies' && (userProfile.role === 'admin' || userProfile.role === 'teacher') && (
                  <div className="grid md:grid-cols-3 gap-8">
                    {userProfile.role === 'admin' && (
                      <div className={`${darkMode ? 'bg-slate-900/40  border border-slate-800 shadow-2xl' : 'bg-white border-slate-100 shadow-lg'} p-6 rounded-3xl border-t-8 border-amber-500 h-fit transition-colors`}>
                        <h3 className="font-black mb-4 text-navy dark:text-white uppercase tracking-tighter">REGISTRAR LLEGADA TARDE</h3>
                        <form onSubmit={handleRegisterTardy} className="space-y-4">
                          <input name="studentName" list="student-list" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-teal-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-teal-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors`} placeholder="Nombre Estudiante" required />
                          <select name="grade" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-amber-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-amber-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors`} required><option className="bg-white text-navy dark:bg-slate-900 dark:text-white" value="">Seleccionar Grado</option>{GRADES.map(g => <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" key={g} value={g}>{g}</option>)}</select>
                          <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Fecha y Hora (opcional)</label>
                            <input name="tardyDate" type="datetime-local" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-amber-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-amber-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors`} />
                          </div>
                          <button disabled={uploading} className="w-full py-3 bg-amber-500 text-white font-bold rounded-xl text-xs">{uploading ? 'REGISTRANDO...' : 'REGISTRAR TARDANZA'}</button>
                        </form>
                        <div className="mt-6 pt-6 border-t"><button onClick={handleDownloadTardiesExcel} className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white font-bold rounded-xl text-xs hover:bg-green-700"><Icon name="download" size={16} /> DESCARGAR EXCEL</button></div>
                      </div>
                    )}
                    <div className={userProfile.role === 'admin' ? "md:col-span-2 space-y-4" : "col-span-3 space-y-4"}>
                      {(userProfile.role === 'admin' || userProfile.role === 'teacher') && (
                        <div className={`${darkMode ? 'glass-morphism' : 'glass-morphism-light border-slate-200'} p-6 rounded-[2.5rem] shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6 transform transition-colors duration-500 hover:scale-[1.01]`}>
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 ${darkMode ? 'bg-amber-500/20 text-amber-500 shadow-[0_0_20px_rgba(245,158,11,0.2)]' : 'bg-amber-500/10 text-amber-600'} rounded-2xl flex items-center justify-center border border-white/10`}>
                              <Icon name="filter" size={24} />
                            </div>
                            <div>
                              <span className={`text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase tracking-[0.2em]`}>Filtrado por</span>
                              <h3 className={`text-sm font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase tracking-tight leading-none`}>Grado Escolar</h3>
                            </div>
                          </div>
                          <div className="relative w-full sm:w-auto">
                            <select
                              value={tardyGradeFilter}
                              onChange={(e) => setTardyGradeFilter(e.target.value)}
                              className={`w-full sm:w-72 appearance-none text-[12px] font-black px-8 py-4 rounded-[1.5rem] border-2 transition-colors duration-500 outline-none cursor-pointer ${darkMode ? 'bg-slate-950/40 border-white/10 text-white focus:border-amber-500 shadow-[0_10px_30px_rgba(0,0,0,0.3)]' : 'bg-white/60 border-white/80 text-navy focus:border-amber-500 shadow-lg'} uppercase tracking-wider hover:bg-white/5`}
                            >
                              <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" value="all">TODOS LOS GRADOS</option>
                              {GRADES.map(g => <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" key={g} value={g}>{g}</option>)}
                            </select>
                            <span className="absolute right-6 top-1/2 -translate-y-1/2 text-amber-500 pointer-events-none"><Icon name="arrowRight" size={14} className="rotate-90" /></span>
                          </div>
                        </div>
                      )}
                      {memoizedData.filteredTardies
                        .filter(t => tardyGradeFilter === 'all' || t.grade === tardyGradeFilter)
                        .map(t => {
                          const norm = normalName(t.student_name);
                          const alertData = memoizedData.studentAlerts[norm];
                          const hasTardyAlert = alertData?.counts?.tardy >= 3;

                          return (
                            <div key={t.id} className={`${darkMode ? 'bg-slate-900/80  border-slate-800' : 'bg-white'} p-4 rounded-2xl shadow border-l-4 flex justify-between items-center transition-colors ${hasTardyAlert ? 'border-red-600 bg-red-50/10 dark:bg-red-900/20 ring-1 ring-red-200' : 'border-amber-200 dark:border-amber-500/50'}`}>
                              <div>
                                <h4 className={`font-black uppercase text-sm ${hasTardyAlert ? 'text-red-700 dark:text-red-400' : 'text-navy dark:text-white'}`}>{t.student_name} {hasTardyAlert && '⚠️'}</h4>
                                <div className="flex gap-2 mt-1">
                                  <span className="text-[10px] bg-slate-100 dark:bg-slate-700 px-2 rounded font-bold dark:text-slate-300">{t.grade}</span>
                                  <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">{new Date(t.created_at).toLocaleString()}</span>
                                  {hasTardyAlert && <span className="text-[9px] font-black text-red-600 uppercase">Frecuencia Alta: {alertData.counts.tardy} en el mes</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-black ${hasTardyAlert ? 'text-red-600' : 'text-amber-500'}`}>LLEGADA TARDE</span>
                                {userProfile.role === 'admin' && (
                                  <div className="flex gap-1 ml-2">
                                    <button
                                      onClick={() => setEditingTardy(t)}
                                      className={`px-3 py-1.5 ${darkMode ? 'bg-amber-900/20 text-amber-500 border-amber-900' : 'bg-amber-50 text-amber-700 border-amber-200'} rounded-lg text-[10px] font-black hover:opacity-80 transition-colors flex items-center gap-1`}
                                      title="Editar tardanza"
                                    >✏️ EDITAR</button>
                                    <button
                                      onClick={() => handleDeleteTardy(t.id)}
                                      className={`px-3 py-1.5 ${darkMode ? 'bg-red-900/20 text-red-400 border-red-900' : 'bg-red-50 text-red-600 border-red-200'} rounded-lg text-[10px] font-black hover:opacity-80 transition-colors flex items-center gap-1`}
                                      title="Eliminar tardanza"
                                    >🗑️</button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      {memoizedData.filteredTardies.length === 0 && <p className="text-center text-slate-400 font-bold py-10">NO HAY TARDANZAS</p>}
                    </div>
                  </div>
                )}

                {adminTab === 'capellania' && (userProfile.role === 'admin' || userProfile.role === 'teacher') && (
  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <div className={`p-6 sm:p-8 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'} rounded-[2rem] border-2 shadow-sm relative overflow-hidden`}>
      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-bl-[100px] -z-10" />
      <h3 className={`text-lg font-black uppercase tracking-tight ${darkMode ? 'text-white' : 'text-navy'} mb-6 flex items-center gap-3`}>
        <span className="p-2 bg-blue-100 text-blue-600 rounded-xl"><Icon name="book" size={20} /></span> Registro Capellanía
      </h3>
      <form onSubmit={handleRegisterCapellania} className="space-y-4">
        <input name="studentName" placeholder="Nombre completo del estudiante" list="student-list" required className={`w-full p-4 rounded-xl border-2 text-sm font-bold uppercase ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'} outline-none transition-colors`} />
        <textarea name="diagnosis" placeholder="Motivo o detalle de la visita" required className={`w-full p-4 rounded-xl border-2 text-sm font-medium resize-none h-24 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:border-blue-500' : 'bg-slate-50 border-slate-200 focus:border-blue-500'} outline-none transition-colors`} />
        <button type="submit" className="w-full py-4 bg-blue-600 text-white rounded-xl text-sm font-black uppercase tracking-widest hover:bg-blue-700 transition-colors shadow-md active:scale-[0.98]">Registrar Visita</button>
      </form>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {memoizedData.filteredCapellania.map(rec => (
        <div key={rec.id} className={`p-5 sm:p-6 ${darkMode ? 'bg-slate-900 border-slate-800 hover:border-blue-500/50' : 'bg-white border-slate-100 hover:border-blue-300'} rounded-2xl border-2 shadow-sm transition-colors group relative`}>
          <div className="flex items-start justify-between gap-2 mb-3">
            <h4 className={`font-black uppercase text-sm sm:text-base ${darkMode ? 'text-white' : 'text-navy'}`}>{rec.student_name}</h4>
            <span className={`text-[10px] font-black px-2 py-1 rounded-lg border uppercase tracking-widest ${darkMode ? 'bg-blue-950/50 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>CAP</span>
          </div>
          <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'} mb-4 line-clamp-2`}>{rec.diagnosis}</p>
          <div className="flex items-center justify-between mt-auto">
            <span className={`text-[10px] font-bold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{new Date(rec.created_at).toLocaleDateString()} - {rec.reporter_name}</span>
            {userProfile.role === 'admin' && (
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setEditingCapellania(rec)} className={`p-2 ${darkMode ? 'text-blue-400 hover:bg-blue-950/50' : 'text-blue-600 hover:bg-blue-50'} rounded-lg transition-colors`}><Icon name="edit" size={14} /></button>
                <button onClick={() => handleDeleteCapellaniaRecord(rec.id)} className={`p-2 ${darkMode ? 'text-red-400 hover:bg-red-950/50' : 'text-red-600 hover:bg-red-50'} rounded-lg transition-colors`}><Icon name="trash" size={14} /></button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
)}

{adminTab === 'infirmary' && (userProfile.role === 'admin' || userProfile.role === 'teacher') && (
                  <div className="grid md:grid-cols-3 gap-8">
                    {userProfile.role === 'admin' ? (
                      <div className={`${darkMode ? 'bg-slate-900/40  border border-slate-800 shadow-2xl' : 'bg-white border-slate-100 shadow-lg'} p-6 rounded-3xl border-t-8 border-rose-500 h-fit transition-colors`}>
                        <h3 className="font-black mb-4 flex items-center gap-2 text-navy dark:text-white uppercase tracking-tighter"><Icon name="heart" className="text-rose-500" /> REGISTRO ENFERMERÍA</h3>
                        <form onSubmit={handleRegisterInfirmary} className="space-y-4">
                          <input name="studentName" list="student-list" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-teal-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-teal-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors`} placeholder="Nombre Estudiante" required />
                          <textarea name="diagnosis" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-rose-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-rose-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors h-32`} placeholder="Diagnóstico..." required></textarea>
                          <label className={`block w-full p-4 ${darkMode ? 'bg-slate-800/50 border-slate-700 text-slate-400 border-dashed border-2' : 'bg-slate-50 border-slate-200 text-slate-500 border-dashed border-2'} text-center rounded-2xl text-[10px] font-black cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors uppercase tracking-widest`}>Subir Foto de Evidencia 📸<input name="photo" type="file" accept="image/*" className="hidden" /></label>
                          <button disabled={uploading} className="w-full py-3 bg-rose-500 text-white font-bold rounded-xl text-xs shadow-lg hover:bg-rose-600 transition-colors">{uploading ? 'REGISTRANDO...' : 'REGISTRAR VISITA'}</button>
                        </form>
                        <div className="mt-6 pt-6 border-t">
                          <button onClick={handleDownloadInfirmaryReport} className="w-full flex items-center justify-center gap-2 py-3 bg-rose-600 text-white font-black rounded-xl text-[10px] uppercase tracking-widest hover:bg-rose-700 transition-colors shadow-lg shadow-rose-600/20">
                            <Icon name="fileText" size={16} /> GENERAR REPORTE GRÁFICO
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="md:col-span-1 hidden md:block"></div>
                    )}
                    <div className={userProfile.role === 'admin' ? "md:col-span-2 space-y-4" : "col-span-3 space-y-4"}>

                      {userProfile.role === 'admin' && nursingPermissions.filter(p => p.status === 'pending').map(p => (
                        <div key={p.id} className={`${darkMode ? 'bg-slate-900/80  border-slate-800' : 'bg-white border-amber-400'} p-4 rounded-2xl shadow border-l-4 animate-pulse-subtle`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-[10px] font-black text-rose-500 uppercase tracking-wider">SOLICITUD DE: {p.teacher_name}</span>
                              <h4 className={`font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase text-sm mt-1`}>{p.student_name} <span className={`${darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'} text-[10px] px-1.5 py-0.5 rounded`}>{p.grade}</span></h4>
                              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'} mt-1 italic`}>"{p.reason}"</p>
                            </div>
                            <div className="flex flex-col gap-2">
                              <button onClick={() => setDenialId(p.id)} className={`${darkMode ? 'bg-rose-900/40 text-rose-400 border-rose-900 shadow-sm' : 'bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-100'} px-3 py-1.5 border rounded-lg text-[10px] font-black transition-colors`}>GESTIONAR</button>
                            </div>
                          </div>
                          {denialId === p.id && (
                            <div className={`${darkMode ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-200'} mt-4 p-4 rounded-xl border`}>
                              <textarea
                                value={denialReason}
                                onChange={(e) => setDenialReason(e.target.value)}
                                className={`w-full p-3 text-xs border rounded-lg mb-3 ${darkMode ? 'bg-slate-900 border-slate-700 text-white focus:border-rose-500' : 'bg-white border-slate-200 text-navy focus:border-rose-300'} shadow-inner transition-colors font-bold outline-none`}
                                placeholder="Escribe una respuesta o diagnóstico para el registro..."
                              ></textarea>
                              <div className="flex justify-end gap-3">
                                <button onClick={() => { setDenialId(null); setDenialReason(""); }} className={`text-[10px] font-bold ${darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}>CANCELAR</button>
                                <button onClick={() => handleUpdateNursingStatus(p.id, 'denied', denialReason)} className={`px-4 py-2 ${darkMode ? 'bg-red-900/40 text-red-500 border-red-900 hover:bg-red-900/60' : 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100'} border rounded-lg text-[10px] font-black transition-colors`}>DENEGAR</button>
                                <button onClick={() => handleUpdateNursingStatus(p.id, 'accepted', denialReason)} className="px-4 py-2 bg-green-600 text-white rounded-lg text-[10px] font-black hover:bg-green-700 shadow-sm transition-colors shadow-green-600/20">ACEPTAR Y ENVIAR</button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {memoizedData.filteredInfirmary.map(rec => {
                        const norm = normalName(rec.student_name);
                        const alertData = memoizedData.studentAlerts[norm];
                        const hasInfAlert = alertData?.counts?.infirmary >= 3;

                        return (
                          <div key={rec.id} className={`${darkMode ? 'bg-slate-900/80  border-slate-800' : 'bg-white'} p-4 rounded-2xl shadow border-l-4 flex justify-between items-start transition-colors ${hasInfAlert ? 'border-red-600 bg-red-50/10 dark:bg-red-900/20 ring-1 ring-red-200' : 'border-rose-200 dark:border-rose-500/50'}`}>
                            <div>
                              <h4 className={`font-black uppercase text-sm ${hasInfAlert ? 'text-red-700 dark:text-red-400' : 'text-navy dark:text-white'}`}>{rec.student_name} {hasInfAlert && '🚨'}</h4>
                              <p className="text-xs text-rose-600 dark:text-rose-400 font-bold mt-1">DX: {rec.diagnosis}</p>
                              <div className="flex gap-2 mt-1">
                                <span className="text-[10px] text-slate-400 font-mono block">{new Date(rec.created_at).toLocaleString()}</span>
                                {hasInfAlert && <span className="text-[9px] font-black text-red-600 uppercase">Frecuencia Alta: {alertData.counts.infirmary} en el mes</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {rec.photo_url && (<a href={rec.photo_url} target="_blank" rel="noreferrer" className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-400 hover:text-rose-500 transition-colors"><Icon name="eye" size={16} /></a>)}
                              {userProfile.role === 'admin' && (
                                <>
                                  <button onClick={() => setEditingInfirmary(rec)} className={`p-2 ${darkMode ? 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'} rounded-lg transition-colors`} title="Editar registro"><Icon name="edit" size={16} /></button>
                                  <button onClick={() => handleDeleteInfirmaryRecord(rec.id)} className={`p-2 ${darkMode ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'} rounded-lg transition-colors`} title="Eliminar registro"><Icon name="trash" size={16} /></button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {memoizedData.filteredInfirmary.length === 0 && <p className="text-center text-slate-400 font-bold py-10">NO HAY VISITAS</p>}
                    </div>
                  </div>
                )}

                {adminTab === 'ai_assistant' && userProfile.role === 'admin' && (
                  <AdminAiAssistant
                    darkMode={darkMode}
                    adminChatMessages={adminChatMessages}
                    setAdminChatMessages={setAdminChatMessages}
                    isChatLoading={isChatLoading}
                    setIsChatLoading={setIsChatLoading}
                    apiKey={apiKey}
                    userProfile={userProfile}
                  />
                )}

                {adminTab === 'register' && userProfile.role === 'admin' && (
                  <div className="grid place-items-center">
                    <div className={`${darkMode ? 'bg-slate-900/90  border-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.5)]' : 'bg-white border-teal-600 shadow-2xl'} p-10 rounded-[2.5rem] border-t-[12px] w-full max-w-lg`}>
                      <h3 className="text-2xl font-black uppercase italic tracking-tighter mb-4 text-center text-navy dark:text-white">Registro Administrativo</h3>
                      <form onSubmit={handleAdminRegister} className="space-y-4">
                        <div className="bg-teal-50 dark:bg-teal-900/20 p-3 rounded-xl border border-teal-200 dark:border-teal-800 text-teal-800 dark:text-teal-400 font-bold text-xs text-center uppercase">Registrando Nuevo Docente</div>
                        <input name="name" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-teal-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-teal-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors`} placeholder="Nombre Completo" required />
                        <input name="email" type="email" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-teal-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-teal-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors`} placeholder="Correo" required />
                        <input name="phone" type="tel" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-teal-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-teal-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors`} placeholder="Teléfono" />
                        <input name="password" type="password" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-teal-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-teal-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors`} placeholder="Contraseña Temporal" required />
                        <select name="grade" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:ring-teal-500/50' : 'bg-slate-50 border-slate-200 text-navy focus:ring-teal-500/20'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 transition-colors`} required><option className="bg-white text-navy dark:bg-slate-900 dark:text-white" value="">Curso / Dirección de Grupo</option>{GRADES.map(g => <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" key={g} value={g}>{g}</option>)}</select>
                        <button disabled={uploading} className="w-full py-4 text-white font-bold rounded-xl bg-teal-600 hover:bg-teal-700 transition-colors mt-2 shadow-lg">{uploading ? 'CREANDO...' : 'CREAR DOCENTE'}</button>
                      </form>
                    </div>
                  </div>
                )}

                {adminTab === 'students' && userProfile.role === 'admin' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xs font-black text-slate-400 uppercase flex items-center gap-2">👨‍🎓 Estudiantes Registrados ({parentProfiles.length})</h3>
                      <input
                        value={studentsSearchFilter}
                        onChange={e => setStudentsSearchFilter(e.target.value)}
                        placeholder="Buscar por nombre..."
                        className="p-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold text-navy dark:text-white shadow-sm w-48 outline-none focus:ring-2 focus:ring-teal-500 transition-colors"
                      />
                    </div>
                    {parentProfiles
                      .filter(p => !studentsSearchFilter || (p.student_represented || '').toLowerCase().includes(studentsSearchFilter.toLowerCase()) || (p.name || '').toLowerCase().includes(studentsSearchFilter.toLowerCase()))
                      .map(p => (
                        <div key={p.id} className={`${darkMode ? 'bg-slate-900/80  border-slate-800' : 'bg-white border-slate-100'} p-4 rounded-2xl shadow border flex justify-between items-center transition-colors`}>
                          <div>
                            <p className="font-black text-sm text-navy dark:text-white">{p.student_represented || <span className='italic text-slate-300 dark:text-slate-600'>Sin estudiante</span>}</p>
                            <div className="flex gap-2 mt-1">
                              <span className="text-[10px] bg-slate-100 dark:bg-slate-700 px-2 rounded font-bold text-slate-500 dark:text-slate-400">{p.assigned_grade || '—'}</span>
                              <span className="text-[10px] text-slate-400 dark:text-slate-500">Acudiente: {p.name}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                const names = (p.student_represented || '').split(/\s*\|\s*/).map(n => n.trim());
                                const grades = (p.assigned_grade || '').split(/\s*\|\s*/).map(g => g.trim());
                                const structured = names.map((n, i) => ({ name: n, grade: grades[i] || '' }));
                                setAddingStudents(structured.length > 0 ? structured : [{ name: '', grade: '' }]);
                                setEditingStudent(p);
                              }}
                              className="px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-[10px] font-black hover:bg-teal-100 transition-colors flex items-center gap-1"
                            >✏️ EDITAR</button>
                            <button
                              onClick={() => handleResetPassword(p.id, p.email)}
                              className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-[10px] font-black hover:bg-amber-100 transition-colors flex items-center gap-1"
                            >🔑 RESET</button>
                          </div>
                        </div>
                      ))
                    }
                    {parentProfiles.length === 0 && <p className="text-center text-slate-400 font-bold py-10">NO HAY ACUDIENTES REGISTRADOS</p>}
                  </div>
                )}

                {adminTab === 'teachers' && userProfile.role === 'admin' && (
                  <div className="space-y-8">
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <h3 className={`text-sm font-black uppercase tracking-tight ${darkMode ? 'text-white' : 'text-navy'}`}>Gestión Docente</h3>
                        <button onClick={handleDownloadAttentionReport} className="px-6 py-2 bg-indigo-600 text-white rounded-2xl text-[10px] sm:text-xs font-black shadow-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 uppercase tracking-widest"><Icon name="download" size={14} /> REPORTE LLAMADOS</button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {teachers.map(t => (
                          <div key={t.id} className={`${darkMode ? 'bg-slate-900/80  border-slate-800 hover:border-slate-700 shadow-lg' : 'bg-white border-slate-200 hover:border-slate-300 shadow-md'} p-6 rounded-[2rem] flex flex-col justify-between border transition-colors relative overflow-hidden group`}>
                            <div className={`absolute top-0 left-0 w-full h-1 ${darkMode ? 'bg-gradient-to-r from-teal-500/50 to-indigo-500/50' : 'bg-gradient-to-r from-teal-500 to-indigo-500'}`} />
                            <div className="flex items-center gap-4 mb-6">
                              <div className="w-12 h-12 bg-teal-50 dark:bg-teal-900/20 rounded-2xl flex items-center justify-center text-teal-600 dark:text-teal-400">
                                <Icon name="users" />
                              </div>
                              <div>
                                <p className="font-black text-sm text-navy dark:text-white uppercase truncate">{t.name}</p>
                                <p className="text-xs text-slate-400 dark:text-slate-500 font-bold tracking-widest">{t.assigned_grade || 'Sin curso asignado'}</p>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 mt-auto">
                              <div className="flex gap-2">
                                <button onClick={() => setEditingTeacher(t)} className="flex-1 px-2 py-2.5 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 border border-teal-200 dark:border-teal-800/50 rounded-xl text-[10px] font-black hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors flex justify-center items-center gap-1 uppercase">✏️ Editar</button>
                                <button onClick={() => handleResetPassword(t.id, t.email)} className="flex-1 px-2 py-2.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/50 rounded-xl text-[10px] font-black hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors flex justify-center items-center gap-1 uppercase">🔑 Reset</button>
                              </div>
                              <button onClick={() => setSelectedTeacherForAttention(t)} className="w-full px-2 py-3 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800/50 rounded-xl text-[10px] sm:text-xs font-black hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors flex justify-center items-center gap-2 uppercase tracking-widest"><Icon name="edit" size={14} /> EVALUAR / LLAMADO</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className={`text-sm font-black uppercase tracking-tight ${darkMode ? 'text-teal-400' : 'text-teal-600'} flex items-center gap-2`}><Icon name="clock" size={18} /> Novedades Pendientes</h3>
                        <button onClick={handleDownloadTeacherExcusesExcel} className={`flex items-center gap-2 px-4 py-2 ${darkMode ? 'bg-teal-900/20 text-teal-400 border-teal-800 hover:bg-teal-900/40' : 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100'} rounded-2xl text-[10px] sm:text-xs font-black transition-colors shadow-sm border uppercase`}>
                          <Icon name="download" size={14} /> DESCARGAR REPORTE
                        </button>
                      </div>
                      {memoizedData.filteredExcuses.filter(e => (e.is_teacher_report || e.contact_info?.includes(e.student_name)) && e.status === 'pending_review').length === 0 ? (
                        <div className={`${darkMode ? 'bg-slate-900 border-slate-800 text-slate-600' : 'bg-white border-slate-100 text-slate-400'} p-8 rounded-[2rem] border-2 border-dashed text-center text-sm font-bold uppercase tracking-widest`}>✨ No hay novedades pendientes</div>
                      ) : (
                        <div className="grid grid-cols-1 gap-4">
                          {memoizedData.filteredExcuses.filter(e => (e.is_teacher_report || e.contact_info?.includes(e.student_name)) && e.status === 'pending_review').map(ex => (
                            <ExcuseCard key={ex.id} ex={ex} isAdmin={true} darkMode={darkMode} userProfile={userProfile} setSelectedExcuse={setSelectedExcuse} setRejectId={setRejectId} handleUpdateStatus={handleUpdateStatus} handleDeleteExcuse={handleDeleteExcuse} />
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
                      <h3 className={`text-sm font-black uppercase tracking-tight ${darkMode ? 'text-slate-400' : 'text-slate-500'} flex items-center gap-2 mb-6`}><Icon name="check" size={18} /> Historial Procesado</h3>
                      {memoizedData.filteredExcuses.filter(e => (e.is_teacher_report || e.contact_info?.includes(e.student_name)) && e.status !== 'pending_review').length === 0 ? (
                        <div className={`${darkMode ? 'bg-slate-900 border-slate-800 text-slate-600' : 'bg-white border-slate-100 text-slate-400'} p-8 rounded-[2rem] border-2 border-dashed text-center text-sm font-bold uppercase tracking-widest`}>No hay historial reciente</div>
                      ) : (
                        <div className="grid grid-cols-1 gap-4 opacity-75 hover:opacity-100 transition-opacity">
                          {memoizedData.filteredExcuses.filter(e => (e.is_teacher_report || e.contact_info?.includes(e.student_name)) && e.status !== 'pending_review').map(ex => (
                            <ExcuseCard key={ex.id} ex={ex} isAdmin={true} darkMode={darkMode} userProfile={userProfile} setSelectedExcuse={setSelectedExcuse} setRejectId={setRejectId} handleUpdateStatus={handleUpdateStatus} handleDeleteExcuse={handleDeleteExcuse} />
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
                      <h3 className={`text-sm font-black uppercase tracking-tight ${darkMode ? 'text-indigo-400' : 'text-indigo-600'} flex items-center gap-2 mb-6`}><Icon name="alert" size={18} /> Historial de Evaluaciones y Llamados</h3>
                      {teacherAttentionCalls.length === 0 ? (
                        <div className={`${darkMode ? 'bg-slate-900 border-slate-800 text-slate-600' : 'bg-white border-slate-100 text-slate-400'} p-8 rounded-[2rem] border-2 border-dashed text-center text-sm font-bold uppercase tracking-widest`}>No hay evaluaciones recientes</div>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 opacity-80 hover:opacity-100 transition-opacity">
                          {teacherAttentionCalls.map(call => (
                            <div key={call.id} className={`${darkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'} p-4 sm:p-5 rounded-3xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm hover:shadow-md transition-colors`}>
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${call.type === 'attention' ? 'bg-red-500/10 text-red-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                                  <Icon name={call.type === 'attention' ? 'alert' : 'check'} size={20} />
                                </div>
                                <div className="text-left">
                                  <h4 className={`font-black uppercase tracking-tight text-sm ${call.type === 'attention' ? (darkMode ? 'text-red-400' : 'text-red-700') : (darkMode ? 'text-emerald-400' : 'text-emerald-700')}`}>
                                    {call.teacher_name} <span className="text-[10px] ml-1 opacity-70">({call.type === 'attention' ? 'Llamado Atención' : 'Felicitación'})</span>
                                  </h4>
                                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'} font-bold mt-1 line-clamp-2`}>{call.observation}</p>
                                </div>
                              </div>
                              <div className="text-left sm:text-right shrink-0 flex flex-col items-start sm:items-end mt-2 sm:mt-0 gap-2">
                                <span className={`text-[9px] font-black uppercase tracking-widest ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                  {new Date(call.created_at).toLocaleDateString()}
                                </span>
                                <div className="flex gap-2 w-full justify-end">
                                  <button onClick={() => { setSelectedTeacherForAttention({ name: call.teacher_name }); setAttentionType(call.type); setAttentionObservation(call.observation); setEditingAttentionCallId(call.id); }} className={`p-2 rounded-xl border transition-colors ${darkMode ? 'bg-indigo-900/20 text-indigo-400 border-indigo-900/50 hover:bg-indigo-900/40' : 'bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100'}`} title="Editar"><Icon name="edit" size={12} /></button>
                                  <button onClick={() => handleDeleteAttentionCall(call.id)} className={`p-2 rounded-xl border transition-colors ${darkMode ? 'bg-red-900/20 text-red-500 border-red-900/50 hover:bg-red-900/40' : 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100'}`} title="Eliminar"><Icon name="trash" size={12} /></button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {adminTab === 'settings' && userProfile.role === 'admin' && (
                  <div className="space-y-6">
                    <div className={`${darkMode ? 'bg-slate-900/80  border-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.4)]' : 'bg-white border-gold shadow-2xl'} p-10 rounded-[2.5rem] border-t-[12px] transition-colors`}>
                      <h3 className={`text-2xl font-black uppercase italic tracking-tighter mb-4 ${darkMode ? 'text-white' : 'text-navy'}`}>Logo Institucional</h3>
                      <label className="inline-block bg-gold text-white px-10 py-5 rounded-2xl text-[11px] font-black uppercase cursor-pointer hover:shadow-2xl transition-colors shadow-xl">{uploading ? "CARGANDO..." : "SUBIR NUEVO LOGO"}<input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} /></label>
                    </div>

                    <div className={`${darkMode ? 'bg-slate-900/80  border-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.4)]' : 'bg-white border-rose-500 shadow-2xl'} p-10 rounded-[2.5rem] border-t-[12px] border-rose-500 transition-colors`}>
                      <h3 className={`text-2xl font-black uppercase italic tracking-tighter mb-2 ${darkMode ? 'text-white' : 'text-navy'}`}>Mantenimiento de Datos</h3>
                      <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'} mb-6 font-bold`}>Esta herramienta busca estudiantes con nombres similares (variaciones de acentos o letras) y unifica sus registros en un solo perfil maestro.</p>
                      <button
                        onClick={handleMergeStudents}
                        disabled={uploading}
                        className={`px-8 py-5 rounded-2xl text-[11px] font-black uppercase transition-colors shadow-xl hover:shadow-rose-500/20 active:scale-95 ${darkMode ? 'bg-rose-900/40 text-rose-400 border border-rose-900 hover:bg-rose-900/60' : 'bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100'}`}
                      >
                        {uploading ? "PROCESANDO..." : "🔍 UNIFICAR ESTUDIANTES DUPLICADOS"}
                      </button>
                    </div>
                  </div>
                )}

              </div>
            )}
          </main>
        </div>
      )}

      {selectedExcuse && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 " onClick={() => setSelectedExcuse(null)}>
          <div className={`${darkMode ? 'bg-slate-900/90  border-slate-800' : 'bg-white border-slate-200'} w-full max-w-2xl max-h-[90vh] rounded-3xl shadow-[0_32px_64px_rgba(0,0,0,0.5)] p-8 border-4 flex flex-col`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className={`font-black text-2xl ${darkMode ? 'text-white' : 'text-navy'} uppercase tracking-tight`}>{selectedExcuse.student_name}</h3>
                <div className="flex gap-2 items-center mt-1">
                  <span className={`${darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'} px-2 py-1 rounded text-xs font-bold`}>{selectedExcuse.grade}</span>
                  {(() => {
                    const alerts = getStudentAlerts(excuses, selectedExcuse.student_name, tardies, infirmaryRecords);
                    if (alerts.absenceAlert || alerts.uniformAlert || alerts.tardinessAlert || alerts.infirmaryAlert) {
                      return <span className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded font-black animate-pulse flex items-center gap-1"><Icon name="alert" size={10} /> ALERTA CRÍTICA</span>
                    }
                    return null;
                  })()}
                </div>
              </div>
              <button onClick={() => setSelectedExcuse(null)} className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-400 hover:text-navy'} transition-colors`}><Icon name="x" size={24} /></button>
            </div>
            <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar">
              {/* ADMIN: Cambiar tipo de excusa desde el modal de detalles */}
              {userProfile?.role === 'admin' && (
                <div className={`${darkMode ? 'bg-indigo-900/20 border-indigo-800/40' : 'bg-indigo-50 border-indigo-200'} p-4 rounded-2xl border-2 flex flex-col sm:flex-row items-start sm:items-center gap-3`}>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${darkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                      <Icon name="edit" size={16} />
                    </div>
                    <span className={`text-[10px] font-black uppercase tracking-widest ${darkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>Control Admin — Tipo de Excusa</span>
                  </div>
                  <select
                    value={selectedExcuse.type}
                    onChange={async (e) => {
                      const newType = e.target.value;
                      const updated = { ...selectedExcuse, type: newType };
                      setSelectedExcuse(updated);
                      await supabase.from('excuses').update({ type: newType }).eq('id', selectedExcuse.id);
                      notify(`✅ Tipo cambiado a: ${newType}`, 'success');
                      fetchData();
                    }}
                    className={`w-full sm:w-auto flex-1 text-[11px] font-black uppercase px-4 py-2.5 rounded-xl border-2 cursor-pointer outline-none transition-colors ${darkMode ? 'bg-indigo-950/40 border-indigo-700 text-indigo-200 focus:border-indigo-400' : 'bg-white border-indigo-300 text-indigo-800 focus:border-indigo-500'} shadow-sm`}
                  >
                    <option value="absence">Inasistencia / Ausencia</option>
                    <option value="medical">Incapacidad Médica</option>
                    <option value="medical_appointment">Cita Médica</option>
                    <option value="early_exit">Salida Durante Jornada</option>
                    <option value="tardiness">Tardanza</option>
                    <option value="uniform">Uniforme</option>
                  </select>
                </div>
              )}
              <div className={`${darkMode ? 'bg-slate-950 border-slate-800 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600'} p-4 rounded-xl text-sm italic border-l-4 border-teal-500`}>"{selectedExcuse.reason}"</div>
              <div className="grid grid-cols-2 gap-3 pb-2 pt-2 border-t border-b border-white/5">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${darkMode ? 'bg-teal-500/10 text-teal-400' : 'bg-teal-50 text-teal-600'}`}>
                    <Icon name="calendar" size={16} />
                  </div>
                  <div>
                    <span className="text-[9px] font-black uppercase text-slate-500 block">Inicio</span>
                    <span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-navy'}`}>{new Date(selectedExcuse.valid_from || selectedExcuse.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${darkMode ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>
                    <Icon name="clock" size={16} />
                  </div>
                  <div>
                    <span className="text-[9px] font-black uppercase text-slate-500 block">Fin Previsto</span>
                    <span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-navy'}`}>
                      {(() => {
                        const start = new Date(selectedExcuse.valid_from || selectedExcuse.created_at);
                        const end = new Date(start.getTime() + ((selectedExcuse.days - 1 || 0) * 24 * 60 * 60 * 1000));
                        return `${end.toLocaleDateString()} · ${selectedExcuse.valid_until_time || '23:59'}`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {selectedExcuse.status === 'approved' && (
                <CountdownTimer
                  startDate={selectedExcuse.valid_from || selectedExcuse.created_at}
                  days={selectedExcuse.days}
                  validUntilTime={selectedExcuse.valid_until_time}
                  darkMode={darkMode}
                />
              )}

              {/* MODIFICACIÓN DE SEGURIDAD Y CARGA DE IMAGEN: Solo admin ve evidencia */}
              {(selectedExcuse.file_url && userProfile.role === 'admin') && (
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">Evidencia Adjunta</span>
                  <div className={`flex justify-center items-center ${darkMode ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-200'} rounded-2xl shadow-inner border overflow-hidden w-full min-h-[200px] max-h-[60vh] relative group/img`}>
                    {selectedExcuse.file_url.toLowerCase().endsWith('.pdf') ? (
                      <div className="flex flex-col items-center justify-center p-8 gap-4 w-full">
                        <div className={`w-20 h-20 rounded-3xl flex items-center justify-center ${darkMode ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 'bg-rose-50 text-rose-600 border border-rose-200'} shadow-lg`}>
                          <Icon name="file" size={40} />
                        </div>
                        <div className="text-center">
                          <h4 className={`font-black text-sm uppercase ${darkMode ? 'text-white' : 'text-navy'}`}>Documento PDF</h4>
                          <p className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'} font-bold mt-1 max-w-[200px] truncate`}>
                            {selectedExcuse.file_url.split('/').pop()}
                          </p>
                        </div>
                        <a
                          href={selectedExcuse.file_url.startsWith('http') ? selectedExcuse.file_url : `${SUPABASE_URL}/storage/v1/object/public/evidencias/${selectedExcuse.file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-8 py-3 bg-navy text-white rounded-xl text-[11px] font-black uppercase tracking-[0.2em] shadow-xl hover:bg-black transition-colors active:scale-95 flex items-center gap-2"
                        >
                          <Icon name="eye" size={14} /> VER DOCUMENTO COMPLETO
                        </a>
                      </div>
                    ) : !imageError ? (
                      <img
                        src={selectedExcuse.file_url.startsWith('http') ? selectedExcuse.file_url : `${SUPABASE_URL}/storage/v1/object/public/evidencias/${selectedExcuse.file_url}`}
                        alt="Evidencia"
                        className="w-full h-full object-contain transition-transform duration-500 group-hover/img:scale-110"
                        onError={() => setImageError(true)}
                        onLoad={() => setImageError(false)}
                      />
                    ) : (
                      <div className={`p-10 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'} font-bold text-xs flex flex-col items-center gap-4 w-full`}>
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${darkMode ? 'bg-red-900/20 text-red-500' : 'bg-red-50 text-red-500'}`}>
                          <Icon name="alert" size={32} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-sm">No se pudo cargar la imagen</span>
                          <span className="text-[10px] opacity-50 font-medium lowercase break-all px-4">{selectedExcuse.file_url}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageError(false);
                            const originalUrl = selectedExcuse.file_url;
                            setSelectedExcuse({ ...selectedExcuse, file_url: originalUrl.includes('?') ? originalUrl : `${originalUrl}?t=${Date.now()}` });
                          }}
                          className="px-6 py-2.5 bg-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-colors active:scale-95 shadow-lg"
                        >
                          REINTENTAR CARGA
                        </button>
                      </div>
                    )}
                    {!imageError && !selectedExcuse.file_url.toLowerCase().endsWith('.pdf') && (
                      <a href={selectedExcuse.file_url.startsWith('http') ? selectedExcuse.file_url : `${SUPABASE_URL}/storage/v1/object/public/evidencias/${selectedExcuse.file_url}`} target="_blank" rel="noreferrer" className="absolute bottom-4 right-4 p-3 bg-navy text-white rounded-xl text-xs font-black shadow-2xl opacity-0 group-hover/img:opacity-100 transition-opacity">
                        VER ORIGINAL ↗
                      </a>
                    )}
                  </div>
                </div>
              )}

              <div className={`pt-4 mt-4 border-t ${darkMode ? 'border-slate-800' : 'border-slate-100'} flex gap-3`}>
                {userProfile?.role === 'admin' && selectedExcuse.status === 'pending_review' && (
                  <>
                    <button
                      onClick={() => { setRejectId(selectedExcuse.id); setSelectedExcuse(null); }}
                      className="flex-1 py-4 bg-red-600 text-white font-black rounded-xl text-xs transition-colors tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-red-600/20 hover:bg-red-700 active:scale-95"
                    >
                      RECHAZAR ❌
                    </button>
                    <button
                      onClick={() => { handleUpdateStatus(selectedExcuse.id, 'approved'); setSelectedExcuse(null); }}
                      className="flex-1 py-4 bg-emerald-600 text-white font-black rounded-xl text-xs transition-colors tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 active:scale-95"
                    >
                      APROBAR ✅
                    </button>
                  </>
                )}
                <button
                  onClick={() => setSelectedExcuse(null)}
                  className={`flex-1 py-4 ${darkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'} font-bold rounded-xl text-xs transition-colors tracking-widest flex items-center justify-center gap-2 shadow-sm`}
                >
                  <Icon name="x" size={16} /> CERRAR
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedFaltaLeve && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 " onClick={() => setSelectedFaltaLeve(null)}>
          <div className={`${darkMode ? 'bg-slate-900/90  border-orange-500/30' : 'bg-white border-orange-300'} w-full max-w-lg rounded-3xl shadow-[0_32px_64px_rgba(0,0,0,0.5)] p-8 border flex flex-col`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className={`font-black text-2xl ${darkMode ? 'text-white' : 'text-navy'} uppercase tracking-tight flex items-center gap-2`}><Icon name="alert" className="text-orange-500" /> Falta Leve</h3>
                <p className={`text-sm font-bold ${darkMode ? 'text-slate-400' : 'text-slate-500'} mt-1`}>{selectedFaltaLeve}</p>
              </div>
              <button onClick={() => setSelectedFaltaLeve(null)} className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-400 hover:text-navy'} transition-colors`}><Icon name="x" size={24} /></button>
            </div>
            <div className="space-y-4">
              <div className={`${darkMode ? 'bg-orange-900/20 border-orange-900/50 text-orange-400' : 'bg-orange-50 border-orange-200 text-orange-700'} p-4 rounded-xl border text-sm font-bold`}>
                El estudiante ha registrado nuevas inasistencias, retardos o reportes menores después de la última revisión.
              </div>
              <div className="max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar space-y-3 mt-4">
                {(() => {
                  const norm = normalName(selectedFaltaLeve);
                  const docs = [
                    ...memoizedData.filteredExcuses.filter(e => normalName(e.student_name) === norm).map(e => ({ ...e, isTardy: false })),
                    ...memoizedData.filteredTardies.filter(t => normalName(t.student_name) === norm).map(t => ({ ...t, isTardy: true }))
                  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                  if (docs.length === 0) return <p className="text-center text-xs font-bold text-slate-400">No hay registros recientes.</p>;
                  return docs.map(d => (
                    <div key={d.id} className={`p-3 rounded-lg border flex justify-between items-center ${darkMode ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
                      <div>
                        <span className={`text-[10px] font-black uppercase tracking-widest ${d.isTardy ? 'text-amber-500' : 'text-slate-500'} block`}>{d.isTardy ? 'Tardanza' : d.type}</span>
                        <span className={`text-xs font-bold ${darkMode ? 'text-slate-300' : 'text-navy'} block mt-1`}>{new Date(d.created_at).toLocaleString()}</span>
                      </div>
                      <span className={`text-[10px] font-black px-2 py-1 rounded-md ${darkMode ? 'bg-white/5' : 'bg-black/5'}`}>{d.isTardy ? '—' : d.status}</span>
                    </div>
                  ));
                })()}
              </div>
              <button
                onClick={() => setSelectedFaltaLeve(null)}
                className={`w-full py-4 mt-4 ${darkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'} font-bold rounded-xl text-xs transition-colors tracking-widest flex items-center justify-center gap-2`}
              >
                CERRAR
              </button>
            </div>
          </div>
        </div>
      )}

      {editingTeacher && (
        <div className="fixed inset-0 bg-black/50  z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className={`${darkMode ? 'bg-slate-900/90  border-teal-500/30 shadow-[0_32px_64px_rgba(0,0,0,0.5)]' : 'bg-white border-teal-500 shadow-2xl'} w-full max-w-sm p-6 rounded-3xl animate-in zoom-in-95 duration-200 border-t-8`}>
            <div className="flex justify-between items-center mb-6">
              <h3 className={`font-black text-lg ${darkMode ? 'text-white' : 'text-navy'} flex items-center gap-2`}><Icon name="users" /> Editar Docente</h3>
              <button onClick={() => setEditingTeacher(null)} className={`p-2 ${darkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-400'} rounded-full transition-colors`}><Icon name="x" size={18} /></button>
            </div>
            <p className={`text-sm font-bold ${darkMode ? 'text-slate-300 bg-slate-950 border-slate-800' : 'text-slate-800 bg-slate-50 border-slate-100'} mb-6 p-3 flex justify-center rounded-xl border`}>{editingTeacher.name}</p>
            <div className="space-y-4">
              <div>
                <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase mb-2`}>Cambiar Grado Asignado</label>
                <select
                  value={editingTeacher.assigned_grade || ''}
                  onChange={(e) => setEditingTeacher({ ...editingTeacher, assigned_grade: e.target.value })}
                  className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200 text-navy'} rounded-2xl border text-sm font-bold shadow-inner focus:ring-2 focus:ring-teal-500 outline-none transition-colors`}
                >
                  <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" value="">Seleccionar Grado</option>
                  {GRADES.map(g => <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <button
                onClick={async () => {
                  setUploading(true);
                  const { error } = await supabase.from('profiles').update({ assigned_grade: editingTeacher.assigned_grade }).eq('id', editingTeacher.id);
                  setUploading(false);
                  if (error) return notify('Error al actualizar docente: ' + error.message, 'error');
                  notify('Docente actualizado exitosamente', 'success');
                  setEditingTeacher(null);
                  fetchData();
                }}
                disabled={uploading || !editingTeacher.assigned_grade}
                className="w-full py-4 mt-2 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl disabled:opacity-50 transition-colors shadow-lg shadow-teal-600/20 active:scale-95"
              >
                {uploading ? 'GUARDANDO...' : 'GUARDAR CAMBIOS'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4  animate-in fade-in duration-200">
          <div className={`${darkMode ? 'bg-slate-900 border-red-600/50' : 'bg-white border-red-500'} w-full max-w-sm p-6 rounded-3xl shadow-2xl border-t-8`}>
            <h3 className={`font-black text-lg mb-4 ${darkMode ? 'text-white' : 'text-navy'} flex items-center gap-2`}><Icon name="alert" className="text-red-600" /> Confirmar Rechazo</h3>
            <textarea
              className={`w-full ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:border-red-600' : 'bg-slate-50 border-slate-200 text-navy focus:border-red-500'} p-3 rounded-xl border h-32 text-sm font-bold outline-none transition-colors`}
              placeholder="Escribe el motivo del rechazo para que el usuario sea notificado..."
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
            ></textarea>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setRejectId(null)} className={`flex-1 py-3 ${darkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'} rounded-xl font-bold text-xs transition-colors`}>CANCELAR</button>
              <button onClick={handleRejectSubmit} className="flex-1 py-3 bg-red-600 text-white rounded-xl font-black text-xs shadow-lg shadow-red-600/20 hover:bg-red-700 transition-colors active:scale-95">RECHAZAR</button>
            </div>
          </div>
        </div>
      )}

      {editingTardy && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 " onClick={() => setEditingTardy(null)}>
          <div className={`${darkMode ? 'bg-slate-900 border-amber-600/50' : 'bg-white border-amber-500'} w-full max-w-sm p-6 rounded-3xl shadow-2xl border-t-8 transition-colors`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className={`font-black text-lg ${darkMode ? 'text-white' : 'text-navy'} flex items-center gap-2`}><Icon name="clock" size={20} className="text-amber-500" /> Editar Tardanza</h3>
              <button onClick={() => setEditingTardy(null)} className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-400 hover:text-navy'}`}><Icon name="x" size={20} /></button>
            </div>
            <form onSubmit={handleEditTardy} className="space-y-4">
              <div>
                <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase mb-1`}>Nombre del Estudiante</label>
                <input
                  name="studentName"
                  list="student-list"
                  defaultValue={editingTardy.student_name}
                  className={`w-full p-3 ${darkMode ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200 text-navy'} rounded-xl border text-sm font-bold outline-none focus:ring-2 focus:ring-amber-500`}
                  placeholder="Nombre del estudiante"
                  required
                />
              </div>
              <div>
                <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase mb-1`}>Grado</label>
                <select name="grade" defaultValue={editingTardy.grade} className={`w-full p-3 ${darkMode ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200 text-navy'} rounded-xl border text-sm font-bold outline-none focus:ring-2 focus:ring-amber-500`} required>
                  <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" value="">Seleccionar Grado</option>
                  {GRADES.map(g => <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase mb-1`}>Fecha y Hora</label>
                <input
                  name="tardyDate"
                  type="datetime-local"
                  defaultValue={new Date(editingTardy.created_at).toISOString().slice(0, 16)}
                  className={`w-full p-3 ${darkMode ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200 text-navy'} rounded-xl border text-sm font-bold outline-none focus:ring-2 focus:ring-amber-500`}
                  required
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => handleDeleteTardy(editingTardy.id)}
                  className={`flex-1 py-3 ${darkMode ? 'bg-red-900/20 text-red-500 border-red-900/50 hover:bg-red-900/40' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'} border rounded-xl font-black text-xs transition-colors shadow-sm`}
                >🗑️ ELIMINAR</button>
                <button disabled={uploading} className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-black text-xs hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20">
                  {uploading ? 'GUARDANDO...' : '✅ GUARDAR CAMBIOS'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingCapellania && (
  <div className="fixed inset-0 bg-slate-950/60  z-[100] flex items-center justify-center p-4">
    <div className={`w-full max-w-md p-6 rounded-3xl border-2 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'} shadow-2xl animate-in zoom-in-95 duration-200`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-lg font-black uppercase ${darkMode ? 'text-white' : 'text-navy'}`}>Editar Visita Capellanía</h3>
        <button onClick={() => setEditingCapellania(null)} className="text-slate-400 hover:text-rose-500"><Icon name="x" size={24} /></button>
      </div>
      <form onSubmit={handleEditCapellaniaRecord} className="space-y-6">
        <div>
          <label className={`block text-[10px] font-black uppercase mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Estudiante</label>
          <input name="studentName" defaultValue={editingCapellania.student_name} list="student-list" required className={`w-full p-4 rounded-xl border-2 text-sm font-bold ${darkMode ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200'} outline-none`} />
        </div>
        <div>
          <label className={`block text-[10px] font-black uppercase mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Motivo</label>
          <textarea name="diagnosis" defaultValue={editingCapellania.diagnosis} className={`w-full p-4 rounded-xl border-2 text-sm h-32 resize-none ${darkMode ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200'} outline-none`} />
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={() => handleDeleteCapellaniaRecord(editingCapellania.id)} className={`flex-1 py-4 ${darkMode ? 'bg-red-950/50 text-red-500 hover:bg-red-900/50' : 'bg-red-50 text-red-600 hover:bg-red-100'} rounded-xl text-sm font-black uppercase transition-colors`}>Eliminar</button>
          <button type="submit" className="flex-1 py-4 bg-emerald-600 text-white rounded-xl text-sm font-black uppercase hover:bg-emerald-700 transition-colors shadow-md">Guardar</button>
        </div>
      </form>
    </div>
  </div>
)}

{editingInfirmary && (
        <div className="fixed inset-0 bg-slate-950/60  z-[100] flex items-center justify-center p-4 animate-fade-in" onClick={() => setEditingInfirmary(null)}>
          <div className={`${darkMode ? 'bg-slate-900 border-slate-800 shadow-2xl' : 'bg-white shadow-2xl'} w-full max-w-lg rounded-[2.5rem] border overflow-hidden p-8 animate-scale-in`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className={`text-xl font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase tracking-tighter flex items-center gap-2`}><Icon name="heart" className="text-rose-500" /> EDITAR REGISTRO</h3>
              <button onClick={() => setEditingInfirmary(null)} className="text-slate-400 hover:text-rose-500"><Icon name="x" size={24} /></button>
            </div>
            <form onSubmit={handleEditInfirmaryRecord} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Estudiante</label>
                <input name="studentName" defaultValue={editingInfirmary.student_name} list="student-list" className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200 text-navy'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 focus:ring-rose-500/20 transition-colors`} required />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Diagnóstico / Motivo</label>
                <textarea name="diagnosis" defaultValue={editingInfirmary.diagnosis} className={`w-full p-4 ${darkMode ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200 text-navy'} rounded-2xl border text-sm font-bold shadow-inner outline-none focus:ring-4 focus:ring-rose-500/20 transition-colors h-32`} required />
              </div>
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => handleDeleteInfirmaryRecord(editingInfirmary.id)} className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-red-500 font-black rounded-2xl text-[10px] uppercase tracking-widest hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors border border-transparent hover:border-red-200 shadow-sm">Borrar Registro</button>
                <button type="submit" disabled={uploading} className="flex-[2] py-4 bg-rose-500 text-white font-black rounded-2xl text-[10px] uppercase tracking-widest hover:bg-rose-600 transition-colors shadow-xl shadow-rose-500/20">{uploading ? 'GUARDANDO...' : 'GUARDAR CAMBIOS'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showChangePassword && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy/95  p-4">
          <div className={`${darkMode ? 'bg-slate-900' : 'bg-white'} w-full max-w-md rounded-[2.5rem] shadow-2xl p-10 relative overflow-hidden transition-colors`}>
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-gold via-navy to-gold"></div>
            <div className="text-center mb-8">
              <div className={`w-20 h-20 ${darkMode ? 'bg-amber-900/20 text-amber-500' : 'bg-amber-50 text-gold'} rounded-3xl flex items-center justify-center mx-auto mb-6 rotate-3 shadow-lg`}>
                <Icon name="lock" size={40} />
              </div>
              <h3 className={`text-2xl font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase italic tracking-tighter mb-2`}>Seguridad Requerida</h3>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'} font-bold px-4`}>Por motivos de seguridad, debes actualizar tu contraseña temporal antes de continuar.</p>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-1">
                <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase ml-2`}>Nueva Contraseña</label>
                <input
                  name="newPassword"
                  type="password"
                  className={`w-full p-5 ${darkMode ? 'bg-slate-950 border-slate-800 text-white focus:border-gold' : 'bg-slate-50 border-slate-100 text-navy focus:border-navy'} rounded-2xl border-2 transition-colors font-bold outline-none`}
                  placeholder="Mínimo 6 caracteres"
                  required
                />
              </div>
              <button
                disabled={uploading}
                className="w-full py-5 bg-navy border border-white/10 text-white font-black rounded-2xl text-xs tracking-[0.2em] shadow-xl hover:bg-black transition-colors transform hover:-translate-y-1 active:scale-95"
              >
                {uploading ? 'ACTUALIZANDO...' : 'ACTUALIZAR Y ENTRAR'}
              </button>
            </form>
            <p className={`text-[10px] text-center ${darkMode ? 'text-slate-700' : 'text-slate-300'} font-black uppercase mt-8 tracking-widest`}>© PORTAL DE EXCUSAS CAN SECURITY SYSTEM</p>
          </div>
        </div>
      )}

      {editingStudent && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 " onClick={() => setEditingStudent(null)}>
          <div className={`${darkMode ? 'bg-slate-900 border-teal-600/30' : 'bg-white border-teal-500'} w-full max-w-sm p-6 rounded-3xl shadow-xl border-t-8 transition-colors`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className={`font-black text-lg ${darkMode ? 'text-white' : 'text-navy'} flex items-center gap-2`}><Icon name="user" className="text-teal-500" /> Editar Registro</h3>
              <button onClick={() => setEditingStudent(null)} className={`${darkMode ? 'text-slate-500 hover:text-white' : 'text-slate-400 hover:text-navy'} transition-colors`}><Icon name="x" size={20} /></button>
            </div>
            <form onSubmit={handleEditStudent} className="space-y-4">
              <div>
                <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase mb-1`}>Acudiente Portavoz</label>
                <input
                  value={editingStudent.name}
                  readOnly
                  className={`w-full p-3 ${darkMode ? 'bg-slate-950 border-slate-800 text-slate-500' : 'bg-slate-100 border-slate-200 text-slate-500'} rounded-xl border text-sm font-bold cursor-not-allowed`}
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase`}>Estudiantes Representados</label>
                  <button type="button" onClick={() => setAddingStudents([...addingStudents, { name: '', grade: '' }])} className="text-[10px] bg-teal-500 text-white px-2 py-1 rounded-lg font-bold flex items-center gap-1 hover:bg-teal-600 transition-colors">
                    <Icon name="plus" size={12} /> AGREGAR
                  </button>
                </div>
                <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                  {addingStudents.map((s, idx) => (
                    <div key={idx} className={`p-3 ${darkMode ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-200'} rounded-xl border relative`}>
                      {addingStudents.length > 1 && (
                        <button type="button" onClick={() => setAddingStudents(addingStudents.filter((_, i) => i !== idx))} className={`absolute -top-2 -right-2 w-6 h-6 ${darkMode ? 'bg-red-900/60 text-red-400 hover:bg-red-800' : 'bg-red-100 text-red-600 hover:bg-red-200'} rounded-full flex items-center justify-center transition-colors shadow-sm`}>
                          <Icon name="x" size={14} />
                        </button>
                      )}
                      <div className="space-y-2">
                        <input
                          value={s.name}
                          onChange={(e) => {
                            const newAdd = [...addingStudents];
                            newAdd[idx].name = e.target.value;
                            setAddingStudents(newAdd);
                          }}
                          className={`w-full p-2 ${darkMode ? 'bg-slate-900 border-slate-700 text-white focus:border-teal-500' : 'bg-white border-slate-300 text-navy focus:border-teal-500'} rounded-lg border text-xs font-bold outline-none transition-colors`}
                          placeholder="Nombre Estudiante"
                          required
                        />
                        <select
                          value={s.grade}
                          onChange={(e) => {
                            const newAdd = [...addingStudents];
                            newAdd[idx].grade = e.target.value;
                            setAddingStudents(newAdd);
                          }}
                          className={`w-full p-2 ${darkMode ? 'bg-slate-900 border-slate-700 text-white focus:border-teal-500' : 'bg-white border-slate-300 text-navy focus:border-teal-500'} rounded-lg border text-xs font-bold outline-none transition-colors`}
                          required
                        >
                          <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" value="">Seleccionar Curso</option>
                          {GRADES.map(g => <option className="bg-white text-navy dark:bg-slate-900 dark:text-white" key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setEditingStudent(null)} className={`flex-1 py-3 ${darkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'} rounded-xl font-bold text-xs transition-colors`}>CANCELAR</button>
                <button disabled={uploading} className="flex-1 py-3 bg-teal-600 text-white rounded-xl font-black text-xs hover:bg-teal-700 transition-colors shadow-lg shadow-teal-600/20 active:scale-95">
                  {uploading ? 'GUARDANDO...' : '✅ GUARDAR CAMBIOS'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {extendingExcuse && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 " onClick={() => setExtendingExcuse(null)}>
          <div className={`${darkMode ? 'bg-slate-900 border-blue-600/30' : 'bg-white border-blue-500'} w-full max-w-sm p-6 rounded-3xl shadow-2xl border-t-8 transition-colors`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`font-black text-lg ${darkMode ? 'text-white' : 'text-navy'} flex items-center gap-2`}><Icon name="plus" className="text-blue-500" /> Ampliar Excusa</h3>
              <button onClick={() => setExtendingExcuse(null)} className={`${darkMode ? 'text-slate-500 hover:text-white' : 'text-slate-400 hover:text-navy'} transition-colors`}><Icon name="x" size={20} /></button>
            </div>
            <div className={`${darkMode ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-100'} p-3 rounded-xl mb-4 text-sm border`}>
              <span className={`font-black ${darkMode ? 'text-white' : 'text-navy'} block`}>{extendingExcuse.student_name}</span>
              <span className={`${darkMode ? 'text-slate-500' : 'text-slate-500'} text-xs uppercase font-bold tracking-widest`}>{extendingExcuse.type} · {extendingExcuse.days || 1} día(s) actuales</span>
            </div>
            <form onSubmit={handleExtendExcuse} className="space-y-4">
              <div>
                <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase mb-1`}>Días adicionales a agregar</label>
                <input
                  name="extraDays"
                  type="number"
                  min="1"
                  max="30"
                  defaultValue={1}
                  className={`w-full p-3 ${darkMode ? 'bg-slate-900 border-slate-700 text-white focus:border-blue-500' : 'bg-slate-50 border-slate-200 text-navy focus:border-blue-500'} rounded-xl border text-sm font-black text-center text-lg transition-colors outline-none`}
                  required
                />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setExtendingExcuse(null)} className={`flex-1 py-3 ${darkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'} rounded-xl font-bold text-xs transition-colors`}>CANCELAR</button>
                <button disabled={uploading} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-black text-xs hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20 active:scale-95">
                  {uploading ? 'GUARDANDO...' : '✅ GUARDAR'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedTeacherForAttention && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 " onClick={handleCloseAttentionModal}>
          <div className={`${darkMode ? 'bg-slate-900 border-indigo-600/30' : 'bg-white border-indigo-500'} w-full max-w-sm p-6 sm:p-8 rounded-[2.5rem] shadow-2xl border-t-8 transition-colors`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className={`font-black text-xl ${darkMode ? 'text-white' : 'text-navy'} flex items-center gap-3 uppercase italic tracking-tighter`}><Icon name="alert" className="text-indigo-500" size={24} /> {editingAttentionCallId ? 'Editar Evaluación' : 'Evaluación Docente'}</h3>
              <button onClick={handleCloseAttentionModal} className={`${darkMode ? 'text-slate-500 hover:text-white bg-slate-800 hover:bg-slate-700' : 'text-slate-400 hover:text-navy bg-slate-100 hover:bg-slate-200'} p-2 rounded-full transition-colors`}><Icon name="x" size={16} /></button>
            </div>

            <div className={`${darkMode ? 'bg-slate-950/50 border-slate-800' : 'bg-slate-50 border-slate-100'} p-4 rounded-2xl mb-6 text-center border`}>
              <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-500 mx-auto rounded-2xl flex items-center justify-center mb-2 shadow-inner"><Icon name="users" size={24} /></div>
              <span className={`font-black text-lg uppercase tracking-tight ${darkMode ? 'text-white' : 'text-navy'} block`}>{selectedTeacherForAttention.name}</span>
            </div>

            <form onSubmit={handleCreateAttentionCall} className="space-y-5">
              <div>
                <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase mb-2 tracking-widest`}>Tipo de Registro</label>
                <div className="relative">
                  <select value={attentionType} onChange={e => setAttentionType(e.target.value)} className={`w-full p-4 ${darkMode ? 'bg-slate-900 border-slate-700 text-white focus:border-indigo-500 shadow-[0_4px_20px_rgba(0,0,0,0.3)]' : 'bg-white border-slate-200 text-navy focus:border-indigo-500 shadow-sm'} rounded-2xl border text-xs font-black transition-colors outline-none appearance-none cursor-pointer uppercase tracking-wider`}>
                    <option value="attention">Llamado de Atención 🚨</option>
                    <option value="congratulation">Felicitación 🎉</option>
                  </select>
                  <Icon name="arrowRight" size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-indigo-500 rotate-90 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className={`block text-[10px] font-black ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase mb-2 tracking-widest`}>Observación Detallada</label>
                <textarea required value={attentionObservation} onChange={e => setAttentionObservation(e.target.value)} rows="4" className={`w-full p-4 ${darkMode ? 'bg-slate-900 border-slate-700 text-white focus:border-indigo-500 shadow-inner' : 'bg-slate-50 border-slate-200 text-navy focus:border-indigo-500 shadow-inner'} rounded-2xl border text-sm font-bold transition-colors outline-none resize-none placeholder-slate-400/50 block`} placeholder="Escribe el motivo de la evaluación..." />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={handleCloseAttentionModal} className={`flex-1 py-4 ${darkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-navy'} rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-colors`}>CANCELAR</button>
                <button disabled={uploading} className={`flex-1 py-4 ${attentionType === 'attention' ? 'bg-red-600 hover:bg-red-700 shadow-red-600/20' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20'} text-white rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-colors shadow-xl active:scale-95 flex items-center justify-center gap-2`}>
                  {uploading ? 'PROCESANDO...' : (editingAttentionCallId ? 'ACTUALIZAR' : 'REGISTRAR')} <Icon name="check" size={16} className={uploading ? "hidden" : "block"} />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingReview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4  animate-in fade-in duration-200" onClick={() => setPendingReview(null)}>
          <div className={`${darkMode ? 'bg-slate-900 border-emerald-600/50' : 'bg-white border-emerald-500'} w-full max-w-sm p-8 rounded-[2.5rem] shadow-2xl border-t-8 animate-in zoom-in-95 duration-200`} onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className={`w-20 h-20 ${darkMode ? 'bg-emerald-900/20 text-emerald-500' : 'bg-emerald-50 text-emerald-600'} rounded-3xl flex items-center justify-center mx-auto mb-6 rotate-3 shadow-lg`}>
                <Icon name="check" size={40} />
              </div>
              <h3 className={`text-2xl font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase italic tracking-tighter mb-2`}>Confirmar Revisión</h3>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'} font-bold px-4`}>
                ¿Confirmas que has revisado la situación de <span className="text-emerald-500">{pendingReview.name}</span>?
                <br /><br />
                <span className="text-[10px] uppercase tracking-widest opacity-70">La alerta se eliminará instantáneamente de la lista.</span>
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setPendingReview(null)}
                className={`flex-1 py-4 ${darkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'} rounded-2xl font-black text-xs tracking-widest transition-colors active:scale-95`}
              >
                CANCELAR
              </button>
              <button
                onClick={handleConfirmReview}
                className="flex-1 py-4 bg-emerald-600 text-white rounded-2xl font-black text-xs tracking-widest shadow-xl shadow-emerald-600/20 hover:bg-emerald-700 transition-colors transform active:scale-95 border border-white/10"
              >
                CONFIRMAR
              </button>
            </div>
          </div>
        </div>
      )}


      <InvestigationModal
        name={investigatingName}
        memoizedData={memoizedData}
        darkMode={darkMode}
        schoolLogo={schoolLogo}
        onClose={() => setInvestigatingName(null)}
      />

      {aiAnalyzing && <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center text-white font-bold animate-pulse">{aiStatus}</div>}
    </div>
  );
}

// --- HELPER COMPONENTS PARA OPTIMIZACIÓN ---

const AdminAlertsSection = React.memo(({
  memoizedData,
  localHiddenAlerts,
  darkMode,
  parentProfiles,
  setAdminTab,
  setPendingReview,
  notify,
  setSelectedFaltaLeve,
  setInvestigatingName
}) => {
  const {
    uniqueStudentNames,
    reviewsByStudent,
    studentAlerts,
    currentMonth,
    currentYear
  } = memoizedData;

  const handlePhoneAction = (name) => {
    const parent = parentProfiles.find(p => p.student_represented && p.student_represented.includes(name));
    if (!parent || !parent.phone) return notify(`No hay teléfono registrado para el familiar de ${name}`, 'warning');
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      window.location.href = `tel:${parent.phone}`;
    } else {
      navigator.clipboard.writeText(parent.phone);
      notify(`Teléfono copiado: ${parent.phone}`, 'success');
    }
  };

  const alertCards = [];
  const leveLCards = [];

  uniqueStudentNames.forEach(name => {
    if (localHiddenAlerts.has(name)) return;

    const norm = normalName(name);
    const alertData = studentAlerts[norm];
    if (!alertData) return;

    const { counts, hasCritical, hasNewActivity } = alertData;
    const reviewThisMonth = reviewsByStudent[norm];

    if (hasCritical && !reviewThisMonth) {
      const isCritical = counts.absence > 3 || counts.tardy > 3 || counts.infirmary > 3 || counts.uniform > 3;
      alertCards.push(
        <div key={name} className={`group relative p-5 rounded-[2rem] border transition-colors duration-300 ${darkMode ? 'bg-slate-900/60 border-red-500/20 hover:border-red-500/50 shadow-2xl' : (isCritical ? 'bg-white border-red-400 shadow-xl ring-4 ring-red-50' : 'bg-white border-red-100 shadow-lg')} flex flex-col justify-between overflow-hidden`}>
          <div className={`absolute top-0 right-0 w-24 h-24 blur-3xl opacity-20 bg-red-500 -mr-12 -mt-12 transition-colors group-hover:opacity-40`} />
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-4">
              <div className="min-w-0 flex-1">
                <span className={`text-[10px] font-black tracking-widest uppercase mb-1 block ${darkMode ? 'text-red-400' : 'text-red-600'}`}>ALERTA CRÍTICA</span>
                <h4 className={`text-base font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase leading-none truncate`}>{name}</h4>
              </div>
              <button
                onClick={() => handlePhoneAction(name)}
                className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-colors ${darkMode ? 'bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white' : 'bg-red-50 text-red-600 hover:bg-red-600 hover:text-white'} shadow-sm`}
                title="Llamar acudiente"
              >
                <Icon name="phoneCall" size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {counts.absence >= 3 && (
                <div className={`${darkMode ? 'bg-red-950/40 border-red-900/50' : 'bg-red-50 border-red-100'} p-2 rounded-xl border flex flex-col`}>
                  <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">Ausencia</span>
                  <span className={`text-sm font-black ${darkMode ? 'text-red-400' : 'text-red-600'}`}>+{counts.absence} 🚨</span>
                </div>
              )}
              {counts.tardy >= 3 && (
                <div className={`${darkMode ? 'bg-amber-950/40 border-amber-900/50' : 'bg-amber-50 border-amber-100'} p-2 rounded-xl border flex flex-col`}>
                  <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">Tardanza</span>
                  <span className={`text-sm font-black ${darkMode ? 'text-amber-500' : 'text-amber-600'}`}>+{counts.tardy} ⏳</span>
                </div>
              )}
              {counts.infirmary >= 3 && (
                <div className={`${darkMode ? 'bg-rose-950/40 border-rose-900/50' : 'bg-rose-50 border-rose-100'} p-2 rounded-xl border flex flex-col`}>
                  <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">Salud</span>
                  <span className={`text-sm font-black ${darkMode ? 'text-rose-500' : 'text-rose-600'}`}>+{counts.infirmary} 🩺</span>
                </div>
              )}
              {counts.uniform >= 3 && (
                <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'} p-2 rounded-xl border flex flex-col`}>
                  <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">Uniforme</span>
                  <span className={`text-sm font-black ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>+{counts.uniform} 👔</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 relative z-10 pt-2 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={() => setInvestigatingName(name)}
              className={`flex-1 py-2.5 text-[10px] font-black uppercase rounded-xl transition-colors ${darkMode ? 'bg-slate-800 text-slate-300 hover:bg-white/10' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              INVESTIGAR
            </button>
            <button
              onClick={() => setPendingReview({ name, type: 'general', month: currentMonth, year: currentYear })}
              className={`flex-1 py-2.5 text-[10px] font-black uppercase rounded-xl bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-600/20 active:scale-95 transition-colors`}
            >
              DETERMINAR
            </button>
          </div>
        </div>
      );
    } else if (reviewThisMonth && hasNewActivity) {
      leveLCards.push(
        <div key={`leve-${name}`} className={`group relative p-5 rounded-[2rem] border transition-colors duration-300 ${darkMode ? 'bg-slate-900 border-orange-500/20 hover:border-orange-500/50' : 'bg-white border-orange-200 shadow-lg ring-2 ring-orange-50'} flex flex-col justify-between overflow-hidden`}>
          <div className={`absolute top-0 right-0 w-20 h-20 blur-3xl opacity-10 bg-orange-500 -mr-10 -mt-10 transition-colors group-hover:opacity-30`} />
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-3">
              <div className="min-w-0 flex-1">
                <span className={`text-[10px] font-black tracking-widest uppercase mb-1 block ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>NOVEDAD RECIENTE</span>
                <h4 className={`text-base font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase leading-none truncate`}>{name}</h4>
              </div>
              <button
                onClick={() => handlePhoneAction(name)}
                className={`w-9 h-9 rounded-2xl flex items-center justify-center transition-colors ${darkMode ? 'bg-orange-500/10 text-orange-400' : 'bg-orange-50 text-orange-600'} hover:scale-110`}
              >
                <Icon name="phoneCall" size={14} />
              </button>
            </div>

            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase ${darkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700'}`}>
              <span className="animate-pulse">🔔</span> FALTA LEVE
            </div>
            <p className={`text-[10px] font-medium leading-tight mt-3 ${darkMode ? 'text-slate-500 italic' : 'text-slate-400 italic'}`}>Registros posteriores a la última revisión mensual.</p>
          </div>

          <div className="flex gap-2 mt-5 relative z-10">
            <button onClick={() => setSelectedFaltaLeve(name)} className={`flex-1 py-2 text-[10px] font-black uppercase rounded-xl transition-colors ${darkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>REVISAR</button>
            <button onClick={() => setPendingReview({ name, type: 'general', month: currentMonth, year: currentYear })} className={`flex-1 py-2 text-[10px] font-black uppercase rounded-xl border-2 ${darkMode ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500 hover:text-white' : 'border-orange-600/50 text-orange-600 hover:bg-orange-600 hover:text-white'} transition-colors`}>SALDAR ✓</button>
          </div>
        </div>
      );
    }
  });

  const totalCards = alertCards.length + leveLCards.length;

  return (
    <div className={`relative overflow-hidden ${darkMode ? 'bg-slate-950/50 border-slate-800 shadow-[0_32px_128px_rgba(0,0,0,0.8)]' : 'bg-slate-50 border-slate-200 shadow-2xl shadow-navy/5'} border-2 p-8 rounded-[3rem] mb-12`}>
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 via-orange-500 to-red-600 opacity-50" />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
        <div>
          <h3 className={`${darkMode ? 'text-white' : 'text-navy'} text-2xl font-black uppercase tracking-tighter flex items-center gap-3`}>
            <div className={`p-2 rounded-2xl ${darkMode ? 'bg-red-500/20 text-red-400 shadow-[0_0_20px_rgba(239,68,68,0.2)]' : 'bg-red-100 text-red-600'} flex items-center justify-center`}>
              <Icon name="alert" size={24} />
            </div>
            Monitor de Reincidencias
          </h3>
          <p className={`text-[11px] font-bold ${darkMode ? 'text-slate-500' : 'text-slate-400'} uppercase tracking-[0.2em] mt-1 ml-11`}>
            Seguimiento crítico de patrones mensuales de inasistencia y conducta
          </p>
        </div>

        {totalCards > 0 && (
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end">
              <span className={`text-[9px] font-black ${darkMode ? 'text-red-400' : 'text-red-600'} uppercase tracking-widest`}>Casos Activos</span>
              <span className={`text-3xl font-black ${darkMode ? 'text-white' : 'text-navy'} leading-none`}>{totalCards}</span>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-10">
        {alertCards.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-red-600/30 to-transparent" />
              <p className="text-[10px] font-black text-red-600 dark:text-red-500 uppercase tracking-[0.4em] px-4 py-1.5 bg-red-50 dark:bg-red-950/20 rounded-full border border-red-200 dark:border-red-900/50">Prioridad Crítica 🔥</p>
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-red-600/30 to-transparent" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {alertCards}
            </div>
          </div>
        )}

        {leveLCards.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-orange-500/30 to-transparent" />
              <p className="text-[10px] font-black text-orange-600 dark:text-orange-500 uppercase tracking-[0.4em] px-4 py-1.5 bg-orange-50 dark:bg-orange-950/20 rounded-full border border-orange-200 dark:border-orange-900/50">Revisiones Recientes 🔔</p>
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-orange-500/30 to-transparent" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {leveLCards}
            </div>
          </div>
        )}

        {totalCards === 0 && (
          <div className={`py-20 flex flex-col items-center justify-center text-center ${darkMode ? 'bg-slate-900/20' : 'bg-white'} rounded-[2.5rem] border-2 border-dashed ${darkMode ? 'border-slate-800' : 'border-slate-100'}`}>
            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 ${darkMode ? 'bg-emerald-500/5 text-emerald-500/30' : 'bg-emerald-50 text-emerald-300'}`}>
              <Icon name="check" size={48} />
            </div>
            <h4 className={`text-lg font-black uppercase tracking-tight ${darkMode ? 'text-slate-600' : 'text-slate-300'}`}>Zona de Calma</h4>
            <p className={`text-xs font-bold ${darkMode ? 'text-slate-500' : 'text-slate-400'} mt-1`}>No se han detectado reincidencias críticas este mes. ✨</p>
          </div>
        )}
      </div>

      <div className={`mt-10 pt-6 border-t ${darkMode ? 'border-slate-800' : 'border-slate-100'} flex justify-center`}>
        <p className={`text-[9px] font-black italic uppercase tracking-widest ${darkMode ? 'text-slate-700' : 'text-slate-300'}`}>
          Sincronización en tiempo real · Período Académico {currentMonth} / {currentYear}
        </p>
      </div>
    </div>
  );
});

const InvestigationModal = React.memo(({ name, memoizedData, darkMode, schoolLogo, onClose }) => {
  if (!name) return null;
  const norm = normalName(name);
  const excuses = memoizedData.excusesByStudent[norm] || [];
  const tardies = memoizedData.tardiesByStudent[norm] || [];
  const infirmary = memoizedData.infirmaryByStudent[norm] || [];
  const capellania = memoizedData.capellaniaByStudent ? (memoizedData.capellaniaByStudent[norm] || []) : [];

  const handleDownloadReport = () => {
    const css = [
      'body{font-family:system-ui,-apple-system,sans-serif;padding:40px;color:#1e293b;max-width:800px;margin:0 auto}',
      '.header{text-align:center;border-bottom:2px solid #e2e8f0;padding-bottom:20px;margin-bottom:30px}',
      '.logo{max-height:100px;margin-bottom:10px}',
      'h1{margin:0;color:#0f172a;text-transform:uppercase;font-size:24px}',
      'h2{font-size:16px;color:#64748b;font-weight:normal;margin-top:5px}',
      '.section{margin-bottom:30px}',
      '.section-title{font-size:14px;font-weight:bold;color:#334155;text-transform:uppercase;border-bottom:1px solid #cbd5e1;padding-bottom:5px;margin-bottom:15px}',
      '.item{padding:10px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;margin-bottom:10px;font-size:13px}',
      '.item-header{display:flex;justify-content:space-between;font-weight:bold;margin-bottom:5px}',
      '.date{color:#64748b}',
      '.summary{display:flex;gap:20px;margin-bottom:30px}',
      '.stat{background:#f1f5f9;padding:15px;border-radius:10px;flex:1;text-align:center}',
      '.stat-num{font-size:24px;font-weight:900;color:#0f172a}',
      '.stat-label{font-size:11px;text-transform:uppercase;font-weight:bold;color:#64748b}',
      '@media print{body{padding:0}}'
    ].join('');

    const logoHTML = schoolLogo ? '<img src="' + schoolLogo + '" class="logo" />' : '';

    const buildItems = (items, renderFn) => items.map(renderFn).join('');

    const excusasHTML = excuses.length > 0
      ? '<div class="section"><div class="section-title">Historial de Excusas</div>' +
        buildItems(excuses, ex =>
          '<div class="item"><div class="item-header"><span>' + ex.type.toUpperCase() + '</span>' +
          '<span class="date">' + new Date(ex.created_at).toLocaleDateString() + '</span></div>' +
          '<div>' + (ex.reason || '') + '</div></div>'
        ) + '</div>'
      : '';

    const tardiesHTML = tardies.length > 0
      ? '<div class="section"><div class="section-title">Historial de Tardanzas</div>' +
        buildItems(tardies, t =>
          '<div class="item"><div class="item-header"><span>LLEGADA TARDE</span>' +
          '<span class="date">' + new Date(t.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) + '</span></div></div>'
        ) + '</div>'
      : '';

    const infirmaryHTML = infirmary.length > 0
      ? '<div class="section"><div class="section-title">Historial de Enfermería</div>' +
        buildItems(infirmary, inf =>
          '<div class="item"><div class="item-header"><span>DIAGNÓSTICO</span>' +
          '<span class="date">' + new Date(inf.created_at).toLocaleDateString() + '</span></div>' +
          '<div>' + (inf.diagnosis || '') + '</div></div>'
        ) + '</div>'
      : '';

    const capellaniaHTML = capellania.length > 0
      ? '<div class="section"><div class="section-title">Historial de Capellanía</div>' +
        buildItems(capellania, cap =>
          '<div class="item"><div class="item-header"><span>REGISTRO</span>' +
          '<span class="date">' + new Date(cap.created_at).toLocaleDateString() + '</span></div>' +
          '<div>' + (cap.diagnosis || '') + '</div></div>'
        ) + '</div>'
      : '';

    const html = '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Boletín - ' + name + '</title>' +
      '<style>' + css + '</style></head><body>' +
      '<div class="header">' + logoHTML +
      '<h1>Reporte General del Estudiante</h1>' +
      '<h2>' + name + '</h2></div>' +
      '<div class="summary">' +
        '<div class="stat"><div class="stat-label">Excusas</div><div class="stat-num">' + excuses.length + '</div></div>' +
        '<div class="stat"><div class="stat-label">Tardanzas</div><div class="stat-num">' + tardies.length + '</div></div>' +
        '<div class="stat"><div class="stat-label">Enfermería</div><div class="stat-num">' + infirmary.length + '</div></div>' +
        '<div class="stat"><div class="stat-label">Capellanía</div><div class="stat-num">' + capellania.length + '</div></div>' +
      '</div>' +
      excusasHTML + tardiesHTML + infirmaryHTML + capellaniaHTML +
      '<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}}<\\/script>' +
      '</body></html>';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black/60  z-[110] flex items-center justify-center p-4 animate-in fade-in transition-colors" onClick={onClose}>
      <div className={`w-full max-w-2xl rounded-[3rem] border-2 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'}`} onClick={e => e.stopPropagation()}>
        <div className={`p-8 border-b ${darkMode ? 'bg-slate-950/50 border-slate-800' : 'bg-slate-50 border-slate-100'} flex justify-between items-center`}>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${darkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-50 text-indigo-600 shadow-inner'}`}>
              <Icon name="users" size={32} />
            </div>
            <div>
              <h3 className={`text-2xl font-black ${darkMode ? 'text-white' : 'text-navy'} uppercase tracking-tighter leading-none`}>{name}</h3>
              <p className={`text-[10px] font-black uppercase tracking-[0.3em] ${darkMode ? 'text-slate-500' : 'text-slate-400'} mt-2`}>Expediente de Reincidencias</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-full transition-colors ${darkMode ? 'bg-slate-800 text-slate-400 hover:text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
            <Icon name="x" size={24} />
          </button>
        </div>

        <div className="p-8 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-8">
          {/* Dashboard de Totales */}
          <div className="grid grid-cols-4 gap-4">
            <div className={`p-4 rounded-3xl border ${darkMode ? 'bg-teal-500/5 border-teal-500/20 text-teal-400' : 'bg-teal-50 border-teal-100 text-teal-700'} text-center`}>
              <span className="text-[10px] font-black uppercase block mb-1">Excusas</span>
              <span className="text-3xl font-black">{excuses.length}</span>
            </div>
            <div className={`p-4 rounded-3xl border ${darkMode ? 'bg-amber-500/5 border-amber-500/20 text-amber-400' : 'bg-amber-50 border-amber-100 text-amber-700'} text-center`}>
              <span className="text-[10px] font-black uppercase block mb-1">Tardanzas</span>
              <span className="text-3xl font-black">{tardies.length}</span>
            </div>
            <div className={`p-4 rounded-3xl border ${darkMode ? 'bg-rose-500/5 border-rose-500/20 text-rose-400' : 'bg-rose-50 border-rose-100 text-rose-700'} text-center`}>
              <span className="text-[10px] font-black uppercase block mb-1">Salud</span>
              <span className="text-3xl font-black">{infirmary.length}</span>
            </div>
            <div className={`p-4 rounded-3xl border ${darkMode ? 'bg-blue-500/5 border-blue-500/20 text-blue-400' : 'bg-blue-50 border-blue-100 text-blue-700'} text-center`}>
              <span className="text-[10px] font-black uppercase block mb-1">Capellán</span>
              <span className="text-3xl font-black">{capellania.length}</span>
            </div>
          </div>

          {/* Historial Detallado */}
          <div className="space-y-6">
            {excuses.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <h4 className={`text-xs font-black uppercase tracking-widest ${darkMode ? 'text-slate-500' : 'text-slate-400'} flex items-center gap-2 whitespace-nowrap`}>
                    <Icon name="file" size={14} /> Historial de Excusas
                  </h4>
                  <div className={`h-px flex-1 ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`} />
                </div>
                <div className="space-y-2">
                  {excuses.map(ex => (
                    <div key={ex.id} className={`p-4 rounded-2xl border transition-colors hover:translate-x-1 ${darkMode ? 'bg-slate-800/40 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600 shadow-sm'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-lg ${darkMode ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-teal-50 text-teal-700 border border-teal-200'}`}>{ex.type.toUpperCase()}</span>
                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{new Date(ex.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm font-bold opacity-90 leading-relaxed italic">"{ex.reason}"</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {tardies.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <h4 className={`text-xs font-black uppercase tracking-widest ${darkMode ? 'text-slate-500' : 'text-slate-400'} flex items-center gap-2 whitespace-nowrap`}>
                    <Icon name="clock" size={14} /> Historial de Tardanzas
                  </h4>
                  <div className={`h-px flex-1 ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`} />
                </div>
                <div className="space-y-2">
                  {tardies.map(t => (
                    <div key={t.id} className={`p-4 rounded-2xl border transition-colors hover:translate-x-1 ${darkMode ? 'bg-slate-800/40 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600 shadow-sm'} flex justify-between items-center`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${darkMode ? 'bg-amber-500/10 text-amber-500' : 'bg-amber-50 text-amber-600'}`}>
                          <Icon name="clock" size={18} />
                        </div>
                        <span className="text-sm font-bold">Llegada Tarde</span>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] font-black text-slate-500 block uppercase tracking-tighter">{new Date(t.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <span className="text-[9px] font-black text-slate-400 block uppercase tracking-widest">{new Date(t.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {capellania.length > 0 && (
  <div className={`p-4 rounded-2xl ${darkMode ? 'bg-blue-950/30 border border-blue-900/50' : 'bg-blue-50 border border-blue-100'}`}>
    <h4 className={`text-xs font-black uppercase tracking-widest mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Capellanía ({capellania.length})</h4>
    <div className="space-y-2">
      {capellania.map(cap => (
        <div key={cap.id} className={`p-3 rounded-xl text-xs ${darkMode ? 'bg-slate-900/50 text-slate-300' : 'bg-white text-slate-600'} shadow-sm`}>
          <span className="font-bold block mb-1">{new Date(cap.created_at).toLocaleDateString()}</span>
          {cap.diagnosis}
        </div>
      ))}
    </div>
  </div>
)}

{infirmary.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <h4 className={`text-xs font-black uppercase tracking-widest ${darkMode ? 'text-slate-500' : 'text-slate-400'} flex items-center gap-2 whitespace-nowrap`}>
                    <Icon name="heart" size={14} /> Historial de Enfermería
                  </h4>
                  <div className={`h-px flex-1 ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`} />
                </div>
                <div className="space-y-2">
                  {infirmary.map(inf => (
                    <div key={inf.id} className={`p-4 rounded-2xl border transition-colors hover:translate-x-1 ${darkMode ? 'bg-slate-800/40 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600 shadow-sm'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-lg ${darkMode ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>DIAGNÓSTICO</span>
                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{new Date(inf.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm font-bold opacity-90 leading-relaxed italic">"{inf.diagnosis}"</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        <div className={`p-6 border-t ${darkMode ? 'bg-slate-950/50 border-slate-800' : 'bg-slate-50 border-slate-100'} flex justify-center gap-4`}>
          <button onClick={handleDownloadReport} className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-black text-xs tracking-widest hover:bg-blue-700 transition-colors transform active:scale-95 shadow-xl">DESCARGAR BOLETÍN</button>
          <button onClick={onClose} className="px-8 py-3 bg-navy text-white rounded-2xl font-black text-xs tracking-widest hover:bg-black transition-colors transform active:scale-95 shadow-xl">CERRAR EXPEDIENTE</button>
        </div>
      </div>
    </div>
  );
});
